// ImportPage.tsx — /import
// CSV / SQL import with field mapping to PE API operations.
// Supports unit-test mode: preview a single record before executing the batch.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Upload, Play, FlaskConical, CheckCircle2, XCircle, ChevronDown, ChevronUp, Loader2, Database, FileSpreadsheet, X, AlertTriangle, AlertCircle, Search } from 'lucide-react';
import { toast } from 'react-toastify';
import {
  testImportRecord, executeImport, runImportQuery,
  searchAttributes, getAttributeDetail, getJobMeta, getRoleMeta,
  type TestResult, type ExecuteRowResult,
  type AttributeListItem, type AttributeDetail, type AttributeFieldOption,
  type ImportParams, type JobMeta, type JobTemplate, type JobRoleType,
} from '../../services/import';
import { parseTabularFile as parseCsv } from '../../utils/parseTabular';

type DataSource   = 'csv' | 'sql';
type ValueMode    = 'constant' | 'per-row';
type JobMgmtMode  = 'single' | 'both';
type JobMgmtRole  = 'JP' | 'JM';
type JobRolesMode = 'replace' | 'add';

// ── Execute confirmation modal ──────────────────────────────────────────────

interface ExecConfirmProps {
  totalRows: number;
  testedRowNum: number | null;   // null = no test was run
  onConfirm: (excludeTestRow: boolean) => void;
  onCancel: () => void;
}

function ExecConfirmModal({ totalRows, testedRowNum, onConfirm, onCancel }: ExecConfirmProps) {
  const [exclude, setExclude] = useState(true);
  const processCount = testedRowNum !== null && exclude ? totalRows - 1 : totalRows;

  const overlayStyle: React.CSSProperties = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,.65)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
  };
  const modalStyle: React.CSSProperties = {
    background: 'var(--surface, #1e2030)',
    border: '1px solid var(--border)', borderRadius: 12, padding: 24,
    width: 460, maxWidth: '95vw',
    boxShadow: '0 8px 40px rgba(0,0,0,.6)',
  };

  return (
    <div style={overlayStyle} onClick={(e) => e.target === e.currentTarget && onCancel()}>
      <div style={modalStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <AlertTriangle size={18} style={{ color: 'var(--warn)' }} />
            <span style={{ fontWeight: 700, fontSize: 16 }}>Execute Import</span>
          </div>
          <button className="btn" style={{ padding: '4px 8px' }} onClick={onCancel}>
            <X size={14} />
          </button>
        </div>

        {testedRowNum !== null && (
          <div style={{
            background: 'rgba(122,162,255,.06)', border: '1px solid rgba(122,162,255,.15)',
            borderRadius: 8, padding: 12, marginBottom: 16,
          }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
              Row {testedRowNum} was already processed in the unit test
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {([
                { val: true, label: `Exclude row ${testedRowNum}`, desc: 'Skip it — already saved to PE' },
                { val: false, label: `Include row ${testedRowNum}`, desc: 'Process it again with the batch' },
              ] as const).map((opt) => (
                <label
                  key={String(opt.val)}
                  style={{
                    display: 'flex', alignItems: 'flex-start', gap: 10,
                    padding: '8px 10px', borderRadius: 6, cursor: 'pointer',
                    background: exclude === opt.val ? 'rgba(122,162,255,.1)' : 'transparent',
                    border: `1px solid ${exclude === opt.val ? 'rgba(122,162,255,.3)' : 'transparent'}`,
                    transition: 'all .15s',
                  }}
                >
                  <input
                    type="radio"
                    name="excludeTestRow"
                    checked={exclude === opt.val}
                    onChange={() => setExclude(opt.val)}
                    style={{ marginTop: 2, accentColor: 'var(--accent)' }}
                  />
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{opt.label}</div>
                    <div style={{ fontSize: 11, color: 'var(--muted)' }}>{opt.desc}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>
        )}

        <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 20 }}>
          {processCount === 0
            ? 'No records to process — the tested row was the only record.'
            : <>This will process <strong style={{ color: 'var(--text)' }}>{processCount} record{processCount !== 1 ? 's' : ''}</strong> against PE.</>}
        </div>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button className="btn" onClick={onCancel} style={{ fontSize: 13 }}>Cancel</button>
          <button
            className="btn"
            onClick={() => onConfirm(exclude)}
            disabled={processCount === 0}
            style={{
              fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6,
              background: 'rgba(122,162,255,.15)', borderColor: 'rgba(122,162,255,.5)',
              color: 'var(--accent)', opacity: processCount === 0 ? .45 : 1,
            }}
          >
            <Play size={13} /> Execute {processCount} Record{processCount !== 1 ? 's' : ''}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Use-case definitions ────────────────────────────────────────────────────

interface FieldDef {
  key: string;
  label: string;
  description: string;
}

// Dropdown sections, in display order. Items are sorted alphabetically inside each.
const USE_CASE_GROUPS = ['Clients', 'Jobs', 'Staff', 'Billing'] as const;
type UseCaseGroup = typeof USE_CASE_GROUPS[number];

interface UseCase {
  id: string;
  label: string;
  group: UseCaseGroup;
  description: string;
  requiredFields: FieldDef[];
  // Fields the user MAY map but aren't required (e.g. per-month hours columns).
  // Mapped optional fields are included in each record sent to the backend.
  optionalFields?: FieldDef[];
  // Optional flag — marks a use case that uses the attribute picker form block
  // and supports a per-row value mode (which adds a "Value" column to requiredFields).
  attributeImport?: boolean;
  valueColumnField?: FieldDef;
  // Optional flag — marks the disbursement-writeoff form (date + narrative)
  writeoffImport?: boolean;
  // Optional flag — marks the staff-budget form (BudgetYear, applied to all rows)
  staffBudgetImport?: boolean;
  // Optional flag — marks the create-jobs form (template picker, applied to all rows)
  jobImport?: boolean;
  // Optional flag — marks the update-client-record use case (no extra form block needed)
  clientRecordImport?: boolean;
  // Optional flag — marks the modify-jobs use case (form-level job field updates)
  jobModifyImport?: boolean;
  // Optional flag — marks the update-job-budget use case (rows are per task, so
  // several rows can share one Job_Idx; the backend saves once per job)
  jobBudgetImport?: boolean;
  // Optional flag — marks the update-job-complexity use case (form-level level picker)
  jobComplexityImport?: boolean;
  // Optional flag — marks the update-client-status use case (form-level status picker)
  clientStatusImport?: boolean;
  // Optional flag — marks the update-contact-status use case (active/inactive picker)
  contactStatusImport?: boolean;
  // Optional flag — marks the update-contact-record use case (contact address/email/
  // phone fields, plus a per-row JSON column that overrides them)
  contactRecordImport?: boolean;
  // Optional flag — marks the update-job-management use case (JP/JM assignment;
  // its mappable columns depend on the mode picked in the form)
  jobManagementImport?: boolean;
  // Optional flag — marks the update-job-roles use case (form picks one PE job role
  // type and a Replace/Add mode; rows supply Job_Idx + a staff reference)
  jobRolesImport?: boolean;
  // Optional flag — marks the update-service-roles use case (service manager /
  // partner / staff on tblClientServices; keyed on ContIndex + ServIndex, columns only)
  serviceRolesImport?: boolean;
  templateCsv?: string;
}

/** Substitute PE year placeholders in a template name/code pattern. */
function applyYearPattern(pattern: string, fiscalYear: number | string): string {
  if (!pattern) return '';
  const y4 = String(fiscalYear);
  const y2 = y4.slice(-2);
  return pattern
    .replace(/\[TTTT\]/gi, y4).replace(/\[YYYY\]/gi, y4)
    .replace(/\[TT\]/gi, y2).replace(/\[YY\]/gi, y2);
}

// Month hours columns for the staff-budget use case. Productive = billable hours
// (→ ProdHrs); Standard = total available hours (→ StdHrs). The backend resolves
// each supplied month against PE's existing control periods.
const STAFF_BUDGET_MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
] as const;

const STAFF_BUDGET_MONTH_FIELDS: FieldDef[] = STAFF_BUDGET_MONTHS.flatMap((m) => [
  { key: `${m}_Prod`, label: `${m} — Productive`, description: `${m} billable/productive budget hours (PE ProdHrs)` },
  { key: `${m}_Std`,  label: `${m} — Standard`,   description: `${m} total/standard hours (PE StdHrs)` },
]);

const CLIENT_RECORD_OPTIONAL_FIELDS: FieldDef[] = [
  { key: 'ClientStatus',        label: 'Client Status',          description: 'PE code: ACTIVE, FINANCIAL, LOST, NEW, PROSPECT, SUSPENDED' },
  { key: 'ClientDepartment',    label: 'Department',             description: 'PE department code' },
  { key: 'ClientSubDepartment', label: 'Sub-Department',         description: 'PE sub-department code' },
  { key: 'ClientOffice',        label: 'Office',                 description: 'PE office code (e.g. BHM)' },
  { key: 'ClientOwnership',     label: 'Ownership',              description: 'PE ownership code' },
  { key: 'ClientIndustry',      label: 'Industry',               description: 'PE industry code' },
  { key: 'ClientCreditStatus',  label: 'Credit Status',          description: 'PE credit status code' },
  { key: 'ClientPartnerIndex',  label: 'Partner (Staff Index)',   description: 'Numeric PE staff index' },
  { key: 'ClientManagerIndex',  label: 'Manager (Staff Index)',  description: 'Numeric PE staff index' },
  { key: 'ClientInChargeIndex', label: 'In-Charge (Staff Index)', description: 'Numeric PE staff index' },
  { key: 'ContAddress',         label: 'Street Address',         description: 'Primary contact street address' },
  { key: 'ContTownCity',        label: 'City',                   description: 'Primary contact city' },
  { key: 'ContCounty',          label: 'State',                  description: 'Primary contact state (ContCounty in PE)' },
  { key: 'ContPostCode',        label: 'Zip',                    description: 'Primary contact zip / post code' },
  { key: 'ContEmail',           label: 'Email',                  description: 'Primary contact email' },
  { key: 'ContPhone',           label: 'Phone',                  description: 'Primary contact phone' },
  { key: 'ContMobile',          label: 'Mobile',                 description: 'Primary contact mobile' },
];

// Contact-record fields. These are contactDetails fields, so the same six work
// for a standalone contact and for a contact that is also a client.
const CONTACT_RECORD_OPTIONAL_FIELDS: FieldDef[] = [
  { key: 'ContAddress',  label: 'Street Address', description: 'Contact street address' },
  { key: 'ContTownCity', label: 'City',           description: 'Contact city' },
  { key: 'ContCounty',   label: 'State',          description: 'Contact state (ContCounty in PE)' },
  { key: 'ContPostCode', label: 'Zip',            description: 'Contact zip / post code' },
  { key: 'ContEmail',    label: 'Email',          description: 'Contact email' },
  { key: 'ContPhone',    label: 'Phone',          description: 'Contact phone' },
  {
    key: 'ContactJson',
    label: 'JSON Object',
    description: 'Optional — a JSON object of field values, e.g. {"city":"MOBILE","state":"AL"}. When a row has one it replaces the columns and form values for that row.',
  },
];

// Job-management mappable columns, per mode. "Update JP or JM" takes one staff
// column and the role comes from the form; "Update both" takes a column per role.
// Every one of these accepts a StaffIndex, a StaffName, or a StaffEMail.
const STAFF_REF_HINT = 'StaffIndex, staff name, or staff email — e.g. 38933, "Doe, Jane", or jdoe@bmss.com';

const JOB_MANAGEMENT_SINGLE_FIELDS: FieldDef[] = [
  { key: 'StaffIndex', label: 'Staff', description: `Who to set as the selected role on this job. ${STAFF_REF_HINT}` },
];

const JOB_MANAGEMENT_BOTH_FIELDS: FieldDef[] = [
  { key: 'JPIndex', label: 'JP (Partner)', description: `Job partner (Job_Partner). ${STAFF_REF_HINT}` },
  { key: 'JMIndex', label: 'JM (Manager)', description: `Job manager (Job_Manager). ${STAFF_REF_HINT}` },
];

// Update-service-roles columns. Named after the ClientServicesDto fields so the
// output of a `select ContIndex, ServIndex, ServPartner, ServManager from
// tblClientServices` query auto-maps.
const SERVICE_ROLES_FIELDS: FieldDef[] = [
  { key: 'ServManager', label: 'Service Manager', description: `Service manager (tblClientServices.ServManager). ${STAFF_REF_HINT}` },
  { key: 'ServPartner', label: 'Service Partner', description: `Service partner (tblClientServices.ServPartner). ${STAFF_REF_HINT}` },
  { key: 'ServStaff1',  label: 'Service Staff',   description: `Service in-charge staff (tblClientServices.ServStaff1). ${STAFF_REF_HINT}; 0 clears it` },
];

const USE_CASES: UseCase[] = [
  {
    id: 'roll-jurisdictions',
    label: 'Roll Tax Jurisdictions',
    group: 'Jobs',
    description: 'Roll forward jurisdiction due dates and statuses — sets CurrentDueDate to ExtendedDate, StatusIndex to 5, ExtendedCount to 1',
    requiredFields: [
      { key: 'Job_Idx', label: 'Job Index', description: 'PE Job Index (numeric)' },
    ],
  },
  {
    id: 'set-client-attributes',
    label: 'Set Client Attributes',
    group: 'Clients',
    description: 'Apply a PE client attribute value to a list of clients. Pick the attribute, choose a constant value or supply per-row values via a CSV column.',
    requiredFields: [
      { key: 'ClientCode', label: 'Client Code', description: 'PE Client Code (e.g. ABC123)' },
    ],
    attributeImport: true,
    valueColumnField: { key: 'Value', label: 'Value', description: 'Per-row attribute value (only used in per-row mode)' },
  },
  {
    id: 'writeoff-disbursements',
    label: 'Write Off Disbursements',
    group: 'Billing',
    description: 'Write off specific WIP disbursement transactions. One draft is created per client, containing only the WIPs you supply; non-target WIPs auto-pulled by PE are removed. Test mode stops at SUBMITTED so you can verify in PE before the batch confirms.',
    requiredFields: [
      { key: 'WIPIndex',       label: 'WIP Index',        description: 'tblWip primary key (the specific WIP transaction)' },
      { key: 'ContIndex',      label: 'ContIndex',        description: 'PE client ID (numeric)' },
      { key: 'ServPeriod',     label: 'Job Index',        description: 'PE Job_Idx (note: in your WIP table this is named ServPeriod)' },
      { key: 'WIPOutstanding', label: 'WIP Outstanding',  description: 'Dollar amount to write off' },
      { key: 'ClientCode',     label: 'Client Code',      description: 'For the results table & Excel export' },
      { key: 'ClientName',     label: 'Client Name',      description: 'For the results table & Excel export (JOIN tblEngagement)' },
    ],
    writeoffImport: true,
  },
  {
    id: 'update-staff-budget',
    label: 'Update Staff Budget',
    group: 'Staff',
    description: 'Bulk-set staff monthly budget hours in PE. Per employee: look up StaffIndex by name, call GetStaffBudgets (which creates the zeroed period rows for the year if they don’t exist yet), apply your hours (billable → Productive, total → Standard) to the months you map, then save via UpdateStaffBudgets. Map the employee name and set the budget year.',
    requiredFields: [
      { key: 'EmployeeName', label: 'Employee Name', description: 'Must match PE tblStaff.StaffName exactly' },
    ],
    optionalFields: STAFF_BUDGET_MONTH_FIELDS,
    staffBudgetImport: true,
  },
  {
    id: 'create-jobs',
    label: 'Create Jobs',
    group: 'Jobs',
    description: 'Bulk-create the same template job for a list of clients (PE Jobs/CreateTemplateJob). Upload a sheet of client codes, pick the service + template, set period and billing value — name/code/partner/manager auto-fill from the template and can be edited. Clients that already have the job are skipped.',
    requiredFields: [
      { key: 'ClientCode', label: 'Client Code', description: 'PE Client Code (e.g. RLBTest)' },
    ],
    jobImport: true,
  },
  {
    id: 'update-client-record',
    label: 'Update Client Record',
    group: 'Clients',
    description: 'Update PE client record fields in bulk (Clients/Save). Set values in the form to apply the same value to every row, or map CSV columns to PE fields for per-row values. Both work together — CSV column wins per row.',
    requiredFields: [
      { key: 'ClientCode', label: 'Client Code', description: 'PE Client Code (e.g. RLBTest)' },
    ],
    optionalFields: CLIENT_RECORD_OPTIONAL_FIELDS,
    clientRecordImport: true,
  },
  {
    id: 'modify-jobs',
    label: 'Modify Jobs',
    group: 'Jobs',
    description: 'Update PE job fields in bulk (Jobs/SaveDetails). Upload a CSV with Job_Idx, then set the fields you want to change in the form. CSV column values override form values per row.',
    requiredFields: [
      { key: 'Job_Idx', label: 'Job Index', description: 'PE Job_Idx (numeric)' },
    ],
    optionalFields: [
      { key: 'ClientCode',      label: 'Client Code',        description: 'For display in results (recommended)' },
      { key: 'Job_Status',       label: 'Job Status',         description: 'Numeric status index (e.g. 1=open, 3=in progress, 28=completed)' },
      { key: 'Job_WorkStatus',   label: 'Work Status',        description: 'Numeric work status index from PE WorkStatuses' },
      { key: 'Job_Office',       label: 'Office',             description: 'Office code (e.g. BHM)' },
      { key: 'Job_Dept',         label: 'Department',         description: 'Department code (e.g. TAX)' },
      { key: 'Job_Class',        label: 'Class',              description: 'Job class code (e.g. COMP, ADV)' },
      { key: 'Job_CurrentStaff', label: 'Current Staff Index', description: 'Numeric staff index' },
      { key: 'Job_Name',         label: 'Job Name',           description: 'Job name string' },
      { key: 'Job_Code',         label: 'Job Code',           description: 'Job code string' },
    ],
    jobModifyImport: true,
  },
  {
    id: 'update-client-status',
    label: 'Update Client Status',
    group: 'Clients',
    description: 'Set the same client status on a list of clients (Clients/LoadClient → Clients/Save). Upload ContIndex values and pick one status in the form — it applies to every row. Clients already on that status are skipped.',
    requiredFields: [
      { key: 'ContIndex', label: 'ContIndex', description: 'PE client ID (numeric) — the client’s ContIndex, not ClientCode' },
    ],
    clientStatusImport: true,
  },
  {
    id: 'update-contact-status',
    label: 'Update Contact Status',
    group: 'Clients',
    description: 'Mark contacts active or inactive (Contacts/LoadContact → Clients/Save, setting contactDetails.ContStatus). Upload ContIndex values and pick one status in the form. Contacts already on that status are skipped.',
    requiredFields: [
      { key: 'ContIndex', label: 'ContIndex', description: 'PE contact ID (numeric) — works for a standalone contact or one that is also a client' },
    ],
    contactStatusImport: true,
  },
  {
    id: 'update-contact-record',
    label: 'Update Contact Record',
    group: 'Clients',
    description: 'Update contact address, email, and phone in bulk (LoadContact/LoadClient → Clients/Save). Set values in the form to apply them to every row, or map columns for per-row values. You can also map a JSON column — when a row has one, that object is the whole update for the row. Blanks never overwrite an existing value.',
    requiredFields: [
      { key: 'ContIndex', label: 'ContIndex', description: 'PE contact ID (numeric) — works for a standalone contact or one that is also a client' },
    ],
    optionalFields: CONTACT_RECORD_OPTIONAL_FIELDS,
    contactRecordImport: true,
  },
  {
    id: 'update-job-management',
    label: 'Update Job Management',
    group: 'Jobs',
    description: 'Set the partner and/or manager on jobs (Jobs/GetManagement → SaveManagement). Pick a mode: update one role (JP or JM) from a single staff column, or update both from JPIndex and JMIndex columns. Each staff value can be a StaffIndex, a staff name, or a staff email. Form values apply to every row; a mapped column wins per row. Jobs already assigned to that staff member are skipped.',
    requiredFields: [
      { key: 'Job_Idx', label: 'Job Index', description: 'PE Job_Idx (numeric)' },
    ],
    jobManagementImport: true,
  },
  {
    id: 'update-job-roles',
    label: 'Update Job Roles',
    group: 'Jobs',
    description: 'Assign staff to a job role such as Preparer, Reviewer, or Team Member (Jobs/GetRoles → SaveRoles). Pick the role and a mode in the form: Replace makes the role hold exactly the staff member in each row, Add puts them in alongside whoever is already there. Each staff value can be a StaffIndex, a staff name, or a staff email. Use 0 in Replace mode to unassign the role; a blank cell leaves it alone. Jobs already set that way are skipped.',
    requiredFields: [
      { key: 'Job_Idx', label: 'Job Index', description: 'PE Job_Idx (numeric)' },
    ],
    optionalFields: [
      { key: 'StaffIndex', label: 'Staff', description: `Who to put in the selected role on this job. ${STAFF_REF_HINT}` },
    ],
    jobRolesImport: true,
  },
  {
    id: 'update-service-roles',
    label: 'Update Service Roles',
    group: 'Clients',
    description: 'Set the service manager, partner, and/or in-charge staff on a client’s service (Clients/GetClientServices → UpdateClientService). These are the roles on tblClientServices — not the client-level partner/manager (Update Client Record) or job-level ones (Update Job Management). One row per client service: ContIndex + ServIndex, plus a column for each role to change. Each staff value can be a StaffIndex, a staff name, or a staff email; a blank cell leaves that role alone. Services already assigned that way are skipped, and every save is read back from PE to confirm it stuck.',
    requiredFields: [
      { key: 'ContIndex', label: 'ContIndex', description: 'PE client ID (numeric) — the client’s ContIndex, not ClientCode' },
      { key: 'ServIndex', label: 'ServIndex', description: 'Service code on that client — e.g. AUDIT, BUSTAX, INDTAX' },
    ],
    optionalFields: SERVICE_ROLES_FIELDS,
    serviceRolesImport: true,
  },
  {
    id: 'update-job-budget',
    label: 'Update Job Budget',
    group: 'Jobs',
    description: 'Set task-level budget hours on jobs (Jobs/GetBudgets → SaveBudgets). One row per task — repeat the same Job_Idx for each task you want to budget. Hours are absolute (they replace whatever is there), and tasks you don’t list keep their existing budget. ETC is set to match the hours on the tasks you do list.',
    requiredFields: [
      { key: 'Job_Idx',      label: 'Job Index',    description: 'PE Job_Idx (numeric) — repeat across rows for multiple tasks' },
      { key: 'Task_Subject', label: 'Task Name',    description: 'Task name as it appears on the job (e.g. Preparation, Review)' },
      { key: 'BudgetHours',  label: 'Budget Hours', description: 'Hours to set on that task (e.g. 1.75). Blank rows are skipped.' },
    ],
    jobBudgetImport: true,
  },
  {
    id: 'update-job-complexity',
    label: 'Update Job Complexity',
    group: 'Jobs',
    description: 'Set the complexity level on jobs (Jobs/GetManagement → SaveManagement). Map a Job_Complexity column for per-row levels, or pick one level in the form to apply to every job. CSV column wins per row.',
    requiredFields: [
      { key: 'Job_Idx', label: 'Job Index', description: 'PE Job_Idx (numeric)' },
    ],
    optionalFields: [
      { key: 'Job_Complexity', label: 'Complexity', description: 'Per-row level: 0 = Not Selected, 1 = Difficult, 2 = Moderate, 3 = Simple' },
    ],
    jobComplexityImport: true,
  },
  {
    id: 'create-client',
    label: 'Create Client',
    group: 'Clients',
    description: 'Create new PE clients via the 8-step assurance workflow (SavePerson/SaveOrg → SaveClient → SaveServices → SaveJobs → Submit/Approve → SaveOriginator → patch LoadClient). Map all 39 columns from the source CSV. Primary rows are processed before Sub rows.',
    templateCsv: `Old_Key,Old_ID,Old_Description,Old_Name,CLIENT_NAME,ContShort,individual_first_name,individual_last_name,spouse_first_name,spouse_last_name,Office,Entity,Partner,Manager,Industry,industry_code,fiscal_year_end_month,date_opened,principal__client_KEY,Type,MasterCode,FullCode,Primary-Street,Primary-City,Primary-State,Primary-Zip,Country,AddBilling,Billing-Street,Billing-City,Billing-State,Billing-Zip,Billing-Country,billing__contact_KEY,billing_email_recipients,client_email,Invoice Method,preferred__contact_KEY,Phone
17,82940000,"STRACHAN, SALLY W.","SALLY W. STRACHAN","Sally W. Strachan","Strachan, Sally",Sally,Strachan,,,FHP,INDIVIDUAL,Rachel Young,Rachel Young,Individual,,12,41590,17,Primary,06300-000,06300-000,2200 EAST BAY DRIVE,DAPHNE,AL,36526,USA,X,200 EAST BAY DRIVE #33,DAPHNE,AL,36526,USA,4056,sstrachan1948@hotmail.com,sstrachan1948@hotmail.com,Email,4056,(251) 234-1814`,
    requiredFields: [
      { key: 'Old_Key',                label: 'Old Key',              description: 'Source system key (reference)' },
      { key: 'Old_ID',                 label: 'Old ID',               description: 'Source system ID — used as ContCode in PE' },
      { key: 'Old_Description',        label: 'Old Description',      description: 'Source system description (reference)' },
      { key: 'Old_Name',               label: 'Old Name',             description: 'Source system name (reference)' },
      { key: 'CLIENT_NAME',            label: 'Client Name',          description: 'Full client name in PE (ContName / ClientName)' },
      { key: 'ContShort',              label: 'Short Code',           description: 'Client short name (ClientShortCode; org ContShort)' },
      { key: 'individual_first_name',  label: 'First Name',           description: 'Individual forenames (PersonForenames — individuals only)' },
      { key: 'individual_last_name',   label: 'Last Name',            description: 'Individual surname (ContShort for persons)' },
      { key: 'spouse_first_name',      label: 'Spouse First Name',    description: 'Spouse forenames (reference)' },
      { key: 'spouse_last_name',       label: 'Spouse Last Name',     description: 'Spouse surname (reference)' },
      { key: 'Office',                 label: 'Office',               description: 'Office code (e.g. BHM, FHP) — set on contact, client, and jobs' },
      { key: 'Entity',                 label: 'Entity Type',          description: 'INDIVIDUAL, PARTNERSHIP, CORPORATION, etc. — matched to PE EntityTypes' },
      { key: 'Partner',                label: 'Partner',              description: 'Partner staff name — matched to PE StaffIndex' },
      { key: 'Manager',                label: 'Manager',              description: 'Manager staff name — matched to PE StaffIndex' },
      { key: 'Industry',               label: 'Industry',             description: 'Industry name — matched to PE Industries (CatName)' },
      { key: 'industry_code',          label: 'Industry Code',        description: 'Source system industry code (reference)' },
      { key: 'fiscal_year_end_month',  label: 'Fiscal Year End Month', description: 'Month number 1–12 — sets ClientYearEnd to last day of that month' },
      { key: 'date_opened',            label: 'Date Opened',          description: 'Source system date opened (reference)' },
      { key: 'principal__client_KEY',  label: 'Principal Client Key', description: 'Source system principal client key (reference)' },
      { key: 'Type',                   label: 'Type',                 description: 'Primary or Sub — Sub clients require master to already exist' },
      { key: 'MasterCode',             label: 'Master Code',          description: 'PE ClientCode of master record (required for Sub clients)' },
      { key: 'FullCode',               label: 'Full Code',            description: 'PE ClientCode to assign (e.g. 06300-000)' },
      { key: 'Primary-Street',         label: 'Primary Street',       description: 'Primary address street (ContAddress)' },
      { key: 'Primary-City',           label: 'Primary City',         description: 'Primary address city (ContTownCity)' },
      { key: 'Primary-State',          label: 'Primary State',        description: 'Primary address state (ContCounty)' },
      { key: 'Primary-Zip',            label: 'Primary Zip',          description: 'Primary address zip (ContPostCode — stored as integer)' },
      { key: 'Country',                label: 'Country',              description: 'Primary address country (e.g. USA)' },
      { key: 'AddBilling',             label: 'Add Billing',          description: 'X if a separate billing address exists (reference)' },
      { key: 'Billing-Street',         label: 'Billing Street',       description: 'Billing address street (reference)' },
      { key: 'Billing-City',           label: 'Billing City',         description: 'Billing address city (reference)' },
      { key: 'Billing-State',          label: 'Billing State',        description: 'Billing address state (reference)' },
      { key: 'Billing-Zip',            label: 'Billing Zip',          description: 'Billing address zip (reference)' },
      { key: 'Billing-Country',        label: 'Billing Country',      description: 'Billing address country (reference)' },
      { key: 'billing__contact_KEY',   label: 'Billing Contact Key',  description: 'Source system billing contact key (reference)' },
      { key: 'billing_email_recipients', label: 'Billing Email',      description: 'Billing/statement email (ClientBillingEmail + ClientStatementEmail)' },
      { key: 'client_email',           label: 'Client Email',         description: 'Client primary email (ContEmail)' },
      { key: 'Invoice Method',         label: 'Invoice Method',       description: 'Email or other — sets ClientDeliveryFormat (1=Email, 0=other)' },
      { key: 'preferred__contact_KEY', label: 'Preferred Contact Key', description: 'Source system preferred contact key (reference)' },
      { key: 'Phone',                  label: 'Phone',                description: 'Client phone number (ContPhone)' },
    ],
  },
];

// Dropdown sections — groups in USE_CASE_GROUPS order, labels alphabetical inside
// each. The definitions above stay in the order they were built so related use
// cases keep sitting next to each other. Empty groups are dropped.
const GROUPED_USE_CASES: { group: UseCaseGroup; cases: UseCase[] }[] = USE_CASE_GROUPS
  .map((group) => ({
    group,
    cases: USE_CASES.filter((uc) => uc.group === group).sort((a, b) => a.label.localeCompare(b.label)),
  }))
  .filter(({ cases }) => cases.length > 0);

// Client statuses for the form-level picker. PE exposes no status control table
// (no CLSTATUS CatType, no tblClientStatus), so this list is the contract — the
// backend rejects anything not in it rather than writing an unknown code.
const CLIENT_STATUSES = ['ACTIVE', 'FINANCIAL', 'LOST', 'NEW', 'PROSPECT', 'SUSPENDED'] as const;

// Complexity levels for the form-level picker. The backend validates against the
// live Complexities list PE returns per job, so a level added in PE still works
// via a mapped CSV column even if it isn't listed here.
const JOB_COMPLEXITY_LEVELS = [
  { value: '0', label: '0 — Not Selected' },
  { value: '1', label: '1 — Level 1 · Difficult' },
  { value: '2', label: '2 — Level 2 · Moderate' },
  { value: '3', label: '3 — Level 3 · Simple' },
] as const;

// ── Component ───────────────────────────────────────────────────────────────

export default function ImportPage() {
  // Use case — default stays Roll Tax Jurisdictions regardless of sort order
  const [useCaseId, setUseCaseId] = useState('roll-jurisdictions');
  const useCase = USE_CASES.find((uc) => uc.id === useCaseId)!;

  // Data source
  const [dataSource, setDataSource] = useState<DataSource>('csv');

  // CSV state
  const [fileName, setFileName] = useState<string | null>(null);
  const [headers, setHeaders] = useState<string[]>([]);
  const [rows, setRows] = useState<Record<string, string>[]>([]);
  const fileRef = useRef<HTMLInputElement>(null);

  // SQL state
  const [sql, setSql] = useState('');
  const [sqlLoading, setSqlLoading] = useState(false);
  const [sqlTotalCount, setSqlTotalCount] = useState<number | null>(null);

  // Field mapping: requiredFieldKey → csvColumn
  const [mapping, setMapping] = useState<Record<string, string>>({});

  // Writeoff-import-specific state
  const [writeoffDate, setWriteoffDate] = useState<string>('');
  const [writeoffNarrative, setWriteoffNarrative] = useState<string>('Disbursement write-off');

  // Staff-budget-specific state (BudgetYear applied to all rows)
  const [budgetYear, setBudgetYear] = useState<string>('');

  // Create-jobs-specific state (template form applied to all rows)
  const [jobMetaClientCode, setJobMetaClientCode] = useState<string>('');
  const [jobMeta, setJobMeta] = useState<JobMeta | null>(null);
  const [jobMetaLoading, setJobMetaLoading] = useState(false);
  const [jobService, setJobService] = useState<string>('');
  const [jobTmpIdx, setJobTmpIdx] = useState<string>('');     // selected JobTmp_Idx
  const [jobPeriodStart, setJobPeriodStart] = useState<string>('');
  const [jobPeriodEnd, setJobPeriodEnd] = useState<string>('');
  const [jobFiscalYear, setJobFiscalYear] = useState<string>('');
  const [jobValue, setJobValue] = useState<string>('');
  const [jobInCharge, setJobInCharge] = useState<string>('0');
  const [jobName, setJobName] = useState<string>('');
  const [jobCode, setJobCode] = useState<string>('');
  const [jobNameTouched, setJobNameTouched] = useState(false);
  const [jobCodeTouched, setJobCodeTouched] = useState(false);

  // Update-client-record form state (form-level field values applied to all rows)
  const [clientRecordParams, setClientRecordParams] = useState<Record<string, string>>({});
  const setClientRecordField = (field: string, value: string) =>
    setClientRecordParams((prev) => ({ ...prev, [field]: value }));

  // Modify-jobs-specific state
  const [jobModifyParams, setJobModifyParams] = useState<Record<string, string>>({});
  const setJobModifyField = (field: string, value: string) =>
    setJobModifyParams((prev) => ({ ...prev, [field]: value }));

  // Update-job-complexity state (form-level level applied to rows with no column value)
  const [jobComplexity, setJobComplexity] = useState<string>('');

  // Update-client-status state (one status applied to every row)
  const [clientStatus, setClientStatus] = useState<string>('');

  // Update-contact-status state (ACTIVE | INACTIVE applied to every row)
  const [contactStatus, setContactStatus] = useState<string>('');

  // Update-contact-record form state (form-level field values applied to all rows)
  const [contactRecordParams, setContactRecordParams] = useState<Record<string, string>>({});
  const setContactRecordField = (field: string, value: string) =>
    setContactRecordParams((prev) => ({ ...prev, [field]: value }));

  // Update-job-management state. Mode drives which columns are mappable; the
  // index fields are form-level fallbacks a mapped column overrides per row.
  const [jobMgmtMode, setJobMgmtMode] = useState<JobMgmtMode>('single');
  const [jobMgmtRole, setJobMgmtRole] = useState<JobMgmtRole>('JP');
  const [jobMgmtStaffIndex, setJobMgmtStaffIndex] = useState<string>('');
  const [jobMgmtJPIndex, setJobMgmtJPIndex] = useState<string>('');
  const [jobMgmtJMIndex, setJobMgmtJMIndex] = useState<string>('');

  // Update-job-roles state. Role + mode have no default on purpose: Replace can
  // remove people, so the user has to pick it. Role types load from PE using the
  // first row's Job_Idx.
  const [jobRolesRoleIndex, setJobRolesRoleIndex] = useState<string>('');
  const [jobRolesMode, setJobRolesMode] = useState<JobRolesMode | ''>('');
  const [jobRolesStaff, setJobRolesStaff] = useState<string>('');
  const [roleTypes, setRoleTypes] = useState<JobRoleType[]>([]);
  const [roleTypesLoading, setRoleTypesLoading] = useState(false);
  const [roleTypesError, setRoleTypesError] = useState<string | null>(null);

  // Attribute-import-specific state
  const [valueMode, setValueMode] = useState<ValueMode>('constant');
  const [attributeQuery, setAttributeQuery] = useState('');
  const [attributeOptions, setAttributeOptions] = useState<AttributeListItem[]>([]);
  const [attributeOptionsLoading, setAttributeOptionsLoading] = useState(false);
  const [selectedAttribute, setSelectedAttribute] = useState<AttributeListItem | null>(null);
  const [attributeDetail, setAttributeDetail] = useState<AttributeDetail | null>(null);
  const [attributeDetailLoading, setAttributeDetailLoading] = useState(false);
  const [selectedFieldId, setSelectedFieldId] = useState<AttributeFieldOption['fieldId'] | null>(null);
  const [constantValue, setConstantValue] = useState<string>('');

  // Test mode
  const [testMode, setTestMode] = useState(true);
  const [testRowIdx, setTestRowIdx] = useState(0);
  const [testLoading, setTestLoading] = useState(false);
  const [testResult, setTestResult] = useState<TestResult | null>(null);
  const [testError, setTestError] = useState<string | null>(null);
  const [testExpanded, setTestExpanded] = useState(true);

  // Execution
  const [executing, setExecuting] = useState(false);
  const [execResults, setExecResults] = useState<ExecuteRowResult[] | null>(null);
  const [execSummary, setExecSummary] = useState<{ total: number; succeeded: number; failed: number; skipped?: number } | null>(null);
  const [showExecModal, setShowExecModal] = useState(false);

  // ── Effective required fields (per-row mode adds the Value column) ─────

  const effectiveRequiredFields = useMemo<FieldDef[]>(() => {
    const base = useCase.requiredFields;
    if (useCase.attributeImport && valueMode === 'per-row' && useCase.valueColumnField) {
      return [...base, useCase.valueColumnField];
    }
    return base;
  }, [useCase, valueMode]);

  // Optional fields (may be mapped but don't gate test/execute) + the full set we
  // build each record from.
  // Job-management's mappable columns depend on the mode picked in the form, so
  // it resolves here rather than sitting statically on the use case.
  const optionalFields = useMemo<FieldDef[]>(() => {
    if (useCase.jobManagementImport) {
      return jobMgmtMode === 'both' ? JOB_MANAGEMENT_BOTH_FIELDS : JOB_MANAGEMENT_SINGLE_FIELDS;
    }
    return useCase.optionalFields ?? [];
  }, [useCase, jobMgmtMode]);
  const mappableFields = useMemo<FieldDef[]>(
    () => [...effectiveRequiredFields, ...optionalFields],
    [effectiveRequiredFields, optionalFields],
  );

  // ── Reset data (shared by source switch, file load, query run) ───────

  const resetData = useCallback(() => {
    setHeaders([]);
    setRows([]);
    setFileName(null);
    setSqlTotalCount(null);
    setMapping({});
    setTestResult(null);
    setTestError(null);
    setExecResults(null);
    setExecSummary(null);
    setTestRowIdx(0);
  }, []);

  const switchDataSource = useCallback((ds: DataSource) => {
    setDataSource(ds);
    resetData();
  }, [resetData]);

  // ── Use case switch ─────────────────────────────────────────────────────

  const handleUseCaseChange = useCallback((id: string) => {
    setUseCaseId(id);
    setMapping({});
    setTestResult(null);
    setTestError(null);
    setExecResults(null);
    setSelectedAttribute(null);
    setAttributeDetail(null);
    setSelectedFieldId(null);
    setConstantValue('');
    setAttributeQuery('');
    setAttributeOptions([]);
    setValueMode('constant');
    setBudgetYear('');
    setJobMeta(null);
    setJobService('');
    setJobTmpIdx('');
    setJobPeriodStart('');
    setJobPeriodEnd('');
    setJobFiscalYear('');
    setJobValue('');
    setJobInCharge('0');
    setJobName('');
    setJobCode('');
    setJobNameTouched(false);
    setJobCodeTouched(false);
    setClientRecordParams({});
    setJobModifyParams({});
    setJobComplexity('');
    setClientStatus('');
    setContactStatus('');
    setContactRecordParams({});
    setJobMgmtMode('single');
    setJobMgmtRole('JP');
    setJobMgmtStaffIndex('');
    setJobMgmtJPIndex('');
    setJobMgmtJMIndex('');
    setJobRolesRoleIndex('');
    setJobRolesMode('');
    setJobRolesStaff('');
    setRoleTypes([]);
    setRoleTypesError(null);
  }, []);

  // ── Create-jobs: load templates for a client code ──────────────────────────

  const selectedTemplate = useMemo<JobTemplate | null>(
    () => jobMeta?.templates.find((t) => String(t.JobTmp_Idx) === jobTmpIdx) ?? null,
    [jobMeta, jobTmpIdx],
  );

  const loadJobMeta = useCallback(async (code: string) => {
    const cc = code.trim();
    if (!cc) return;
    setJobMetaLoading(true);
    try {
      const meta = await getJobMeta(cc);
      setJobMeta(meta);
      toast.info(`Loaded ${meta.templates.length} templates from ${cc}`);
    } catch (err: any) {
      toast.error(`Couldn't load templates: ${err.message}`);
    } finally {
      setJobMetaLoading(false);
    }
  }, []);

  // Prefill the template-source client code from the first uploaded row.
  useEffect(() => {
    if (!useCase.jobImport) return;
    const col = mapping['ClientCode'];
    if (col && rows.length > 0 && !jobMetaClientCode) {
      const first = rows[0]?.[col];
      if (first) setJobMetaClientCode(first.trim());
    }
  }, [useCase.jobImport, mapping, rows, jobMetaClientCode]);

  // Derive FiscalYear from the period end (year), unless the user overrides it.
  useEffect(() => {
    if (!useCase.jobImport || !jobPeriodEnd) return;
    const y = jobPeriodEnd.slice(0, 4);
    if (/^\d{4}$/.test(y)) setJobFiscalYear(y);
  }, [useCase.jobImport, jobPeriodEnd]);

  // When a template is picked, default the in-charge/value from the template.
  // Partner/Manager are NOT set here — they resolve per client at run time.
  useEffect(() => {
    if (!selectedTemplate) return;
    setJobInCharge(String(selectedTemplate.InCharge ?? 0));
    if (selectedTemplate.Value != null && jobValue === '') setJobValue(String(selectedTemplate.Value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobTmpIdx]);

  useEffect(() => {
    if (!selectedTemplate || !jobFiscalYear) return;
    if (!jobNameTouched) setJobName(applyYearPattern(selectedTemplate.JobName, jobFiscalYear));
    if (!jobCodeTouched) setJobCode(applyYearPattern(selectedTemplate.JobCode, jobFiscalYear));
  }, [selectedTemplate, jobFiscalYear, jobNameTouched, jobCodeTouched]);

  // ── Update-job-roles: load the role types ──────────────────────────────────
  // PE serves role types per job, so the first row's Job_Idx is the lookup key.

  const firstJobIdx = useMemo(() => {
    const col = mapping['Job_Idx'];
    return col && rows.length > 0 ? String(rows[0]?.[col] ?? '').trim() : '';
  }, [mapping, rows]);

  useEffect(() => {
    if (!useCase.jobRolesImport || !/^\d+$/.test(firstJobIdx)) return;
    let cancelled = false;
    setRoleTypesLoading(true);
    setRoleTypesError(null);
    getRoleMeta(firstJobIdx)
      .then((types) => { if (!cancelled) setRoleTypes(types); })
      .catch((err) => { if (!cancelled) setRoleTypesError(err.message); })
      .finally(() => { if (!cancelled) setRoleTypesLoading(false); });
    return () => { cancelled = true; };
  }, [useCase.jobRolesImport, firstJobIdx]);

  // ── Attribute picker — search ──────────────────────────────────────────

  useEffect(() => {
    if (!useCase.attributeImport) return;
    let cancelled = false;
    const t = setTimeout(async () => {
      setAttributeOptionsLoading(true);
      try {
        const items = await searchAttributes(attributeQuery, 50);
        if (!cancelled) setAttributeOptions(items);
      } catch (err: any) {
        if (!cancelled) toast.error(`Attribute search failed: ${err.message}`);
      } finally {
        if (!cancelled) setAttributeOptionsLoading(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [useCase.attributeImport, attributeQuery]);

  // ── Attribute picker — fetch detail when selected ──────────────────────

  useEffect(() => {
    if (!selectedAttribute) {
      setAttributeDetail(null);
      setSelectedFieldId(null);
      return;
    }
    let cancelled = false;
    setAttributeDetailLoading(true);
    getAttributeDetail(selectedAttribute.AttrIndex)
      .then((detail) => {
        if (cancelled) return;
        setAttributeDetail(detail);
        // Auto-pick the first supported FieldId
        if (detail.supported.length > 0) {
          setSelectedFieldId(detail.supported[0].fieldId);
        } else {
          setSelectedFieldId(null);
        }
      })
      .catch((err) => {
        if (!cancelled) toast.error(`Attribute detail failed: ${err.message}`);
      })
      .finally(() => {
        if (!cancelled) setAttributeDetailLoading(false);
      });
    return () => { cancelled = true; };
  }, [selectedAttribute]);

  // ── Auto-map columns whose header matches a field key ──────────────────────
  // Fills only unmapped fields; the user can still override any pick. Match is
  // case-insensitive and ignores spaces/underscores (so "Jul Prod" → "Jul_Prod").

  useEffect(() => {
    if (headers.length === 0) return;
    const norm = (s: string) => s.toLowerCase().replace(/[\s_]+/g, '');
    const byNorm = new Map<string, string>();
    for (const h of headers) {
      const k = norm(h);
      if (!byNorm.has(k)) byNorm.set(k, h);
    }
    setMapping((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const f of mappableFields) {
        if (next[f.key]) continue;
        const hit = byNorm.get(norm(f.key));
        if (hit) { next[f.key] = hit; changed = true; }
      }
      return changed ? next : prev;
    });
  }, [headers, mappableFields]);

  // ── File handling ───────────────────────────────────────────────────────

  const handleFile = useCallback(async (file: File) => {
    try {
      const { headers: h, rows: r } = await parseCsv(file);
      resetData();
      setFileName(file.name);
      setHeaders(h);
      setRows(r);
      toast.info(`Loaded ${r.length} rows from ${file.name}`);
    } catch (err: any) {
      toast.error(err.message);
    }
  }, [resetData]);

  const onFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const f = e.target.files?.[0];
      if (f) handleFile(f);
    },
    [handleFile],
  );

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const f = e.dataTransfer.files?.[0];
      if (f) handleFile(f);
    },
    [handleFile],
  );

  // ── SQL query ───────────────────────────────────────────────────────────

  const handleRunQuery = useCallback(async () => {
    if (!sql.trim()) return;
    setSqlLoading(true);
    resetData();
    try {
      const result = await runImportQuery(sql);
      setHeaders(result.headers);
      setRows(result.rows);
      setSqlTotalCount(result.totalCount);
      setTestRowIdx(0);
      toast.info(`Query returned ${result.totalCount} row${result.totalCount !== 1 ? 's' : ''}`);
    } catch (err: any) {
      toast.error(`Query failed: ${err.message}`);
    } finally {
      setSqlLoading(false);
    }
  }, [sql, resetData]);

  // ── Mapping helpers ─────────────────────────────────────────────────────

  const allMapped = useMemo(
    () => effectiveRequiredFields.every((f) => mapping[f.key] && mapping[f.key] !== ''),
    [effectiveRequiredFields, mapping],
  );

  /** Build a mapped record from a raw CSV row (required + any mapped optional fields) */
  const buildMappedRecord = useCallback(
    (row: Record<string, string>) => {
      const out: Record<string, string> = {};
      for (const f of mappableFields) {
        const col = mapping[f.key];
        if (!col) continue;            // unmapped optional fields are omitted
        out[f.key] = row[col] ?? '';
      }
      return out;
    },
    [mappableFields, mapping],
  );

  // ── Form-level params (for attribute import) ───────────────────────────

  const importParams: ImportParams = useMemo(() => {
    if (useCase.attributeImport) {
      if (!selectedAttribute || !selectedFieldId) return undefined;
      return {
        AttrIndex: selectedAttribute.AttrIndex,
        FieldId: selectedFieldId,
        value: valueMode === 'constant' ? constantValue : null,
        valueColumn: valueMode === 'per-row' ? 'Value' : null,
      };
    }
    if (useCase.writeoffImport) {
      if (!writeoffDate) return undefined;
      return {
        debtTranDate: writeoffDate,
        narrative: writeoffNarrative,
      };
    }
    if (useCase.staffBudgetImport) {
      if (!budgetYear) return undefined;
      return { budgetYear };
    }
    if (useCase.jobImport) {
      if (!jobTmpIdx || !jobService || !jobPeriodStart || !jobPeriodEnd || !jobFiscalYear || !jobName || !jobCode) return undefined;
      return {
        JobTmp_Idx: Number(jobTmpIdx),
        ServIndex: jobService,
        InCharge: Number(jobInCharge) || 0,
        Value: Number(jobValue) || 0,
        PeriodStart: jobPeriodStart,
        PeriodEnd: jobPeriodEnd,
        FiscalYear: Number(jobFiscalYear),
        JobName: jobName,
        JobCode: jobCode,
      };
    }
    if (useCase.clientRecordImport) {
      return Object.fromEntries(
        Object.entries(clientRecordParams).filter(([, v]) => v.trim() !== '')
      );
    }
    if (useCase.jobModifyImport) {
      return Object.fromEntries(
        Object.entries(jobModifyParams).filter(([, v]) => v.trim() !== '')
      );
    }
    if (useCase.jobComplexityImport) {
      // Fallback level for rows with no Job_Complexity column value
      return jobComplexity ? { Job_Complexity: jobComplexity } : undefined;
    }
    if (useCase.clientStatusImport) {
      return clientStatus ? { ClientStatus: clientStatus } : undefined;
    }
    if (useCase.contactStatusImport) {
      return contactStatus ? { ContactStatus: contactStatus } : undefined;
    }
    if (useCase.contactRecordImport) {
      return Object.fromEntries(
        Object.entries(contactRecordParams).filter(([, v]) => v.trim() !== '')
      );
    }
    if (useCase.jobManagementImport) {
      // Mode + role are always sent; the index fields are the per-row fallbacks.
      return jobMgmtMode === 'both'
        ? {
            mode: 'both',
            ...(jobMgmtJPIndex.trim() !== '' ? { JPIndex: jobMgmtJPIndex.trim() } : {}),
            ...(jobMgmtJMIndex.trim() !== '' ? { JMIndex: jobMgmtJMIndex.trim() } : {}),
          }
        : {
            mode: 'single',
            Role: jobMgmtRole,
            ...(jobMgmtStaffIndex.trim() !== '' ? { StaffIndex: jobMgmtStaffIndex.trim() } : {}),
          };
    }
    if (useCase.jobRolesImport) {
      // Role + mode are always sent; StaffIndex is the per-row fallback.
      return {
        RoleIndex: jobRolesRoleIndex,
        mode: jobRolesMode,
        ...(jobRolesStaff.trim() !== '' ? { StaffIndex: jobRolesStaff.trim() } : {}),
      };
    }
    return undefined;
  }, [useCase.attributeImport, useCase.writeoffImport, useCase.staffBudgetImport, useCase.jobImport, useCase.clientRecordImport, useCase.jobModifyImport, useCase.jobComplexityImport, useCase.clientStatusImport, useCase.contactStatusImport, useCase.contactRecordImport, useCase.jobManagementImport, useCase.jobRolesImport, selectedAttribute, selectedFieldId, valueMode, constantValue, writeoffDate, writeoffNarrative, budgetYear, jobTmpIdx, jobService, jobInCharge, jobValue, jobPeriodStart, jobPeriodEnd, jobFiscalYear, jobName, jobCode, clientRecordParams, jobModifyParams, jobComplexity, clientStatus, contactStatus, contactRecordParams, jobMgmtMode, jobMgmtRole, jobMgmtStaffIndex, jobMgmtJPIndex, jobMgmtJMIndex, jobRolesRoleIndex, jobRolesMode, jobRolesStaff]);

  // ── Form-level validity (gates test/execute for attribute import) ──────

  const formValid = useMemo(() => {
    if (useCase.attributeImport) {
      if (!selectedAttribute || !selectedFieldId) return false;
      if (valueMode === 'constant') {
        // Bool constant defaults to false — that's a valid value, so allow empty here
        if (selectedFieldId === 'AttrBool') return true;
        return constantValue.trim() !== '';
      }
      return true;
    }
    if (useCase.writeoffImport) {
      return writeoffDate.trim() !== '';
    }
    if (useCase.staffBudgetImport) {
      return /^\d{4}$/.test(budgetYear.trim());
    }
    if (useCase.jobImport) {
      return !!(jobTmpIdx && jobService && jobPeriodStart && jobPeriodEnd
        && /^\d{4}$/.test(jobFiscalYear.trim()) && jobName.trim() && jobCode.trim());
    }
    if (useCase.clientRecordImport) {
      const hasFormValue   = Object.values(clientRecordParams).some((v) => v.trim() !== '');
      const hasMappedCol   = (useCase.optionalFields ?? []).some((f) => !!mapping[f.key]);
      return hasFormValue || hasMappedCol;
    }
    if (useCase.jobModifyImport) {
      const hasFormValue   = Object.values(jobModifyParams).some((v) => v.trim() !== '');
      const hasMappedCol   = (useCase.optionalFields ?? []).some((f) => !!mapping[f.key]);
      return hasFormValue || hasMappedCol;
    }
    if (useCase.jobComplexityImport) {
      // Needs a level from somewhere — the mapped column or the form
      return jobComplexity.trim() !== '' || !!mapping['Job_Complexity'];
    }
    if (useCase.clientStatusImport) {
      return clientStatus.trim() !== '';
    }
    if (useCase.contactStatusImport) {
      return contactStatus.trim() !== '';
    }
    if (useCase.contactRecordImport) {
      // A value has to come from somewhere: the form, a mapped field column, or
      // a mapped JSON column.
      const hasFormValue = Object.values(contactRecordParams).some((v) => v.trim() !== '');
      const hasMappedCol = optionalFields.some((f) => !!mapping[f.key]);
      return hasFormValue || hasMappedCol;
    }
    if (useCase.jobManagementImport) {
      if (jobMgmtMode === 'both') {
        const hasJP = jobMgmtJPIndex.trim() !== '' || !!mapping['JPIndex'];
        const hasJM = jobMgmtJMIndex.trim() !== '' || !!mapping['JMIndex'];
        // "Update both" means both — a run that could only ever set one role is
        // the single-role mode, so require each side to have a source.
        return hasJP && hasJM;
      }
      return jobMgmtStaffIndex.trim() !== '' || !!mapping['StaffIndex'];
    }
    if (useCase.jobRolesImport) {
      // Role and mode must be chosen, and a staff value has to come from somewhere.
      return jobRolesRoleIndex !== '' && jobRolesMode !== ''
        && (jobRolesStaff.trim() !== '' || !!mapping['StaffIndex']);
    }
    if (useCase.serviceRolesImport) {
      // Columns only — at least one role column has to be mapped to do anything.
      return SERVICE_ROLES_FIELDS.some((f) => !!mapping[f.key]);
    }
    return true;
  }, [useCase.serviceRolesImport, useCase.attributeImport, useCase.writeoffImport, useCase.staffBudgetImport, useCase.jobImport, useCase.clientRecordImport, useCase.jobModifyImport, useCase.jobComplexityImport, useCase.clientStatusImport, useCase.contactStatusImport, useCase.contactRecordImport, useCase.jobManagementImport, useCase.jobRolesImport, useCase.optionalFields, optionalFields, selectedAttribute, selectedFieldId, valueMode, constantValue, writeoffDate, budgetYear, jobTmpIdx, jobService, jobPeriodStart, jobPeriodEnd, jobFiscalYear, jobName, jobCode, clientRecordParams, jobModifyParams, jobComplexity, clientStatus, contactStatus, contactRecordParams, jobMgmtMode, jobMgmtStaffIndex, jobMgmtJPIndex, jobMgmtJMIndex, jobRolesRoleIndex, jobRolesMode, jobRolesStaff, mapping]);

  // Staff-budget needs at least one month pair (Prod + Std) mapped to do anything.
  const hasMappedMonthPair = useMemo(() => {
    if (!useCase.staffBudgetImport) return true;
    return STAFF_BUDGET_MONTHS.some((m) => mapping[`${m}_Prod`] && mapping[`${m}_Std`]);
  }, [useCase.staffBudgetImport, mapping]);

  const canRun = allMapped && rows.length > 0 && formValid && hasMappedMonthPair;

  // ── Test ────────────────────────────────────────────────────────────────

  const handleTest = useCallback(async () => {
    if (!canRun) return;
    setTestLoading(true);
    setTestResult(null);
    setTestError(null);
    try {
      const mapped = buildMappedRecord(rows[testRowIdx]);
      const result = await testImportRecord(useCaseId, mapped, importParams);
      setTestResult(result);
      toast.info('Test completed — review the result below');
    } catch (err: any) {
      setTestError(err.message);
      toast.error(`Test failed: ${err.message}`);
    } finally {
      setTestLoading(false);
    }
  }, [canRun, rows, testRowIdx, useCaseId, importParams, buildMappedRecord]);

  // ── Execute ─────────────────────────────────────────────────────────────

  const handleExecuteClick = useCallback(() => {
    if (!canRun) return;
    setShowExecModal(true);
  }, [canRun]);

  const handleExecuteConfirm = useCallback(async (excludeTestRow: boolean) => {
    setShowExecModal(false);
    const rowsToProcess = (testMode && testResult && excludeTestRow)
      ? rows.filter((_, i) => i !== testRowIdx)
      : rows;

    if (rowsToProcess.length === 0) {
      toast.info('No records to process');
      return;
    }

    setExecuting(true);
    setExecResults(null);
    setExecSummary(null);
    try {
      const mapped = rowsToProcess.map(buildMappedRecord);
      const result = await executeImport(useCaseId, mapped, importParams);
      setExecResults(result.results);
      setExecSummary({ total: result.total, succeeded: result.succeeded, failed: result.failed, skipped: result.skipped });
      const skipped = result.skipped ?? 0;
      if (result.failed === 0) {
        toast.info(`${result.succeeded} processed${skipped ? `, ${skipped} skipped` : ''} of ${result.total}`);
      } else {
        toast.error(`${result.failed} of ${result.total} failed${skipped ? ` (${skipped} skipped)` : ''}`);
      }
    } catch (err: any) {
      toast.error(`Execute failed: ${err.message}`);
    } finally {
      setExecuting(false);
    }
  }, [rows, useCaseId, importParams, buildMappedRecord, testMode, testResult, testRowIdx]);

  // ── Render ──────────────────────────────────────────────────────────────

  const testRecord = rows.length > 0 && allMapped ? buildMappedRecord(rows[testRowIdx]) : null;

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Header */}
      <div>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>Data Import</h1>
        <p className="muted" style={{ margin: '4px 0 0' }}>
          Load data from a CSV or SQL query, map fields to PE API operations, and execute — or test a single record first.
        </p>
      </div>

      {/* Use Case selector */}
      <div className="card">
        <div className="hd" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontWeight: 600 }}>Use Case</span>
        </div>
        <div className="bd">
          <select
            value={useCaseId}
            onChange={(e) => handleUseCaseChange(e.target.value)}
            style={{
              background: 'var(--bg)', color: 'var(--text)', border: '1px solid var(--border)',
              borderRadius: 6, padding: '8px 12px', fontSize: 14, width: '100%', maxWidth: 400,
            }}
          >
            {GROUPED_USE_CASES.map(({ group, cases }) => (
              <optgroup key={group} label={group}>
                {cases.map((uc) => (
                  <option key={uc.id} value={uc.id}>{uc.label}</option>
                ))}
              </optgroup>
            ))}
          </select>
          <p className="muted" style={{ margin: '8px 0 0', fontSize: 13 }}>{useCase.description}</p>
          <div style={{
            marginTop: 12, background: 'rgba(122,162,255,.06)', border: '1px solid rgba(122,162,255,.15)',
            borderRadius: 6, padding: 12, position: 'relative',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--accent)' }}>
                Required {dataSource === 'sql' ? 'Columns in Query' : 'CSV Fields'}
              </div>
              {useCase.templateCsv && (
                <button
                  onClick={() => {
                    const blob = new Blob([useCase.templateCsv!], { type: 'text/csv' });
                    const url  = URL.createObjectURL(blob);
                    const a    = document.createElement('a');
                    a.href     = url;
                    a.download = `${useCase.id}-template.csv`;
                    a.click();
                    URL.revokeObjectURL(url);
                  }}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    padding: '6px 14px', fontSize: 12, fontWeight: 700,
                    background: 'rgba(87,211,140,.15)', color: '#57d38c',
                    border: '1px solid rgba(87,211,140,.5)', borderRadius: 6,
                    cursor: 'pointer', letterSpacing: '0.01em',
                    animation: 'templatePulse 2.4s ease-in-out infinite',
                  }}
                >
                  <FileSpreadsheet size={13} /> Download import template
                </button>
              )}
            </div>
            <style>{`
              @keyframes templatePulse {
                0%, 100% { box-shadow: 0 0 0 0 rgba(87,211,140,.4); }
                50%       { box-shadow: 0 0 0 6px rgba(87,211,140,0); }
              }
            `}</style>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {effectiveRequiredFields.map((f) => (
                <div key={f.key} style={{ fontSize: 12, display: 'flex', gap: 8 }}>
                  <code style={{
                    background: 'rgba(122,162,255,.12)', padding: '1px 6px', borderRadius: 4,
                    fontSize: 11, fontFamily: 'monospace',
                  }}>{f.key}</code>
                  <span className="muted">{f.description}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Attribute Picker (only for attribute-import use cases) */}
      {useCase.attributeImport && (
        <div className="card">
          <div className="hd" style={{ fontWeight: 600 }}>Attribute &amp; Value</div>
          <div className="bd" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {/* Value mode toggle */}
            <div>
              <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 6 }}>Value source</div>
              <div style={{
                display: 'inline-flex', borderRadius: 6, overflow: 'hidden',
                border: '1px solid var(--border)',
              }}>
                {([
                  { v: 'constant' as const, label: 'Constant value', desc: 'Same value applied to all rows' },
                  { v: 'per-row'  as const, label: 'Per-row value',  desc: 'Value column from CSV/query' },
                ]).map((opt) => (
                  <button
                    key={opt.v}
                    title={opt.desc}
                    onClick={() => setValueMode(opt.v)}
                    style={{
                      padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                      border: 'none',
                      background: valueMode === opt.v ? 'var(--accent)' : 'transparent',
                      color: valueMode === opt.v ? '#0b0d10' : 'var(--muted)',
                    }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Attribute search */}
            <div>
              <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 6 }}>Attribute</div>
              <div style={{ position: 'relative', maxWidth: 500 }}>
                <Search size={14} style={{ position: 'absolute', left: 10, top: 9, color: 'var(--muted)' }} />
                <input
                  type="text"
                  value={attributeQuery}
                  onChange={(e) => setAttributeQuery(e.target.value)}
                  placeholder="Search attributes by name..."
                  style={{
                    background: 'var(--bg)', color: 'var(--text)', border: '1px solid var(--border)',
                    borderRadius: 6, padding: '7px 10px 7px 32px', fontSize: 13, width: '100%',
                  }}
                />
              </div>
              <div style={{
                marginTop: 6, maxHeight: 220, overflowY: 'auto',
                border: '1px solid var(--border)', borderRadius: 6, maxWidth: 500,
              }}>
                {attributeOptionsLoading ? (
                  <div className="muted" style={{ padding: 10, fontSize: 12 }}>
                    <Loader2 size={12} style={{ verticalAlign: -2, animation: 'spin 1s linear infinite' }} /> Loading…
                  </div>
                ) : attributeOptions.length === 0 ? (
                  <div className="muted" style={{ padding: 10, fontSize: 12 }}>No attributes match.</div>
                ) : (
                  attributeOptions.map((a) => {
                    const sel = selectedAttribute?.AttrIndex === a.AttrIndex;
                    return (
                      <div
                        key={a.AttrIndex}
                        onClick={() => setSelectedAttribute(a)}
                        style={{
                          padding: '6px 10px', cursor: 'pointer', fontSize: 12,
                          background: sel ? 'rgba(122,162,255,.12)' : 'transparent',
                          borderLeft: sel ? '3px solid var(--accent)' : '3px solid transparent',
                          display: 'flex', justifyContent: 'space-between', gap: 8,
                        }}
                      >
                        <span>{a.AttrName || <em className="muted">(unnamed)</em>}</span>
                        <span className="muted" style={{ fontFamily: 'monospace', fontSize: 11 }}>
                          #{a.AttrIndex} {a.AttrType ? `· ${a.AttrType}` : ''}
                        </span>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* Field type + value input */}
            {selectedAttribute && (
              <div style={{
                background: 'rgba(122,162,255,.06)', border: '1px solid rgba(122,162,255,.15)',
                borderRadius: 6, padding: 12, display: 'flex', flexDirection: 'column', gap: 10,
              }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--accent)' }}>
                  Selected: {selectedAttribute.AttrName} <span className="muted" style={{ fontWeight: 400 }}>(#{selectedAttribute.AttrIndex})</span>
                </div>
                {attributeDetailLoading && (
                  <div className="muted" style={{ fontSize: 12 }}>
                    <Loader2 size={12} style={{ verticalAlign: -2, animation: 'spin 1s linear infinite' }} /> Fetching attribute type…
                  </div>
                )}
                {attributeDetail && (
                  <>
                    {attributeDetail.supported.length > 1 && (
                      <div>
                        <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 4 }}>This attribute supports multiple value types — pick one:</div>
                        <select
                          value={selectedFieldId || ''}
                          onChange={(e) => setSelectedFieldId(e.target.value as AttributeFieldOption['fieldId'])}
                          style={{
                            background: 'var(--bg)', color: 'var(--text)', border: '1px solid var(--border)',
                            borderRadius: 6, padding: '6px 10px', fontSize: 13,
                          }}
                        >
                          {attributeDetail.supported.map((s) => (
                            <option key={s.fieldId} value={s.fieldId}>
                              {s.fieldId} — {s.prompt}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                    {attributeDetail.supported.length === 1 && selectedFieldId && (
                      <div className="muted" style={{ fontSize: 12 }}>
                        Field: <code style={{ fontFamily: 'monospace' }}>{selectedFieldId}</code>
                      </div>
                    )}
                    {attributeDetail.supported.length === 0 && (
                      <div style={{ fontSize: 12, color: 'var(--bad)' }}>
                        This attribute has no supported value types.
                      </div>
                    )}

                    {valueMode === 'constant' && selectedFieldId && (
                      <div>
                        <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 4 }}>
                          Value to apply
                        </div>
                        <ValueInput
                          fieldId={selectedFieldId}
                          value={constantValue}
                          onChange={setConstantValue}
                        />
                      </div>
                    )}
                    {valueMode === 'per-row' && (
                      <div className="muted" style={{ fontSize: 12 }}>
                        Per-row mode — make sure your CSV/query includes a <code style={{ fontFamily: 'monospace' }}>Value</code> column
                        and map it below. Values are coerced to <code style={{ fontFamily: 'monospace' }}>{selectedFieldId}</code> at execute time.
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Writeoff form block (only for writeoff-disbursements use case) */}
      {useCase.writeoffImport && (
        <div className="card">
          <div className="hd" style={{ fontWeight: 600 }}>Write-off Settings</div>
          <div className="bd" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr', alignItems: 'center', gap: 12 }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 13 }}>Debt Tran Date <span style={{ color: 'var(--bad)' }}>*</span></div>
                <div className="muted" style={{ fontSize: 11 }}>Cut-off date applied to every draft in the batch</div>
              </div>
              <input
                type="date"
                value={writeoffDate}
                onChange={(e) => setWriteoffDate(e.target.value)}
                style={{
                  background: 'var(--bg)', color: 'var(--text)', border: '1px solid var(--border)',
                  borderRadius: 6, padding: '7px 10px', fontSize: 13, maxWidth: 220,
                }}
              />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr', alignItems: 'center', gap: 12 }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 13 }}>Narrative</div>
                <div className="muted" style={{ fontSize: 11 }}>Memo line added to every draft (defaults shown)</div>
              </div>
              <input
                type="text"
                value={writeoffNarrative}
                onChange={(e) => setWriteoffNarrative(e.target.value)}
                placeholder="Disbursement write-off"
                style={{
                  background: 'var(--bg)', color: 'var(--text)', border: '1px solid var(--border)',
                  borderRadius: 6, padding: '7px 10px', fontSize: 13, maxWidth: 500,
                }}
              />
            </div>
            <div style={{
              fontSize: 11, color: 'var(--muted)',
              background: 'rgba(122,162,255,.06)', border: '1px solid rgba(122,162,255,.15)',
              borderRadius: 6, padding: 10,
            }}>
              <strong>Hardcoded constants:</strong> TranType=3 (Invoice), IntFinal=PROG, WIPType=DISB, printNow=true.
              Drafts are grouped <strong>one per ContIndex</strong>; concurrency is 3 drafts at a time.
              Test mode runs the full sequence for a single row but stops at SUBMITTED so you can verify in PE.
            </div>
          </div>
        </div>
      )}

      {/* Staff-budget form block (BudgetYear applied to all rows) */}
      {useCase.staffBudgetImport && (
        <div className="card">
          <div className="hd" style={{ fontWeight: 600 }}>Budget Year</div>
          <div className="bd" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr', alignItems: 'center', gap: 12 }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 13 }}>Budget Year <span style={{ color: 'var(--bad)' }}>*</span></div>
                <div className="muted" style={{ fontSize: 11 }}>Applied to every row (joins PE control periods with YearEndDate = {budgetYear || 'YYYY'}-12-31)</div>
              </div>
              <input
                type="number"
                value={budgetYear}
                onChange={(e) => setBudgetYear(e.target.value)}
                placeholder="2026"
                min={2000}
                max={2100}
                style={{
                  background: 'var(--bg)', color: 'var(--text)', border: '1px solid var(--border)',
                  borderRadius: 6, padding: '7px 10px', fontSize: 13, maxWidth: 140,
                }}
              />
            </div>
            <div style={{
              fontSize: 11, color: 'var(--muted)',
              background: 'rgba(122,162,255,.06)', border: '1px solid rgba(122,162,255,.15)',
              borderRadius: 6, padding: 10,
            }}>
              The import <strong>creates the period rows if they don’t exist</strong> (via GetStaffBudgets), then
              updates them — so the year doesn’t have to be pre-rolled. Each employee name must match
              <code style={{ fontFamily: 'monospace' }}> tblStaff.StaffName</code> exactly — a mismatch is reported
              as an error (not silently skipped). Map <strong>Productive</strong> from billable hours and
              <strong> Standard</strong> from total hours; only the months you map are touched.
            </div>
          </div>
        </div>
      )}

      {/* Create-jobs form block (template + settings applied to all clients) */}
      {useCase.jobImport && (
        <div className="card">
          <div className="hd" style={{ fontWeight: 600 }}>Job Template &amp; Settings</div>
          <div className="bd" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {/* Template source client */}
            <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr', alignItems: 'center', gap: 12 }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 13 }}>Load templates from</div>
                <div className="muted" style={{ fontSize: 11 }}>Any client code (templates are firm-wide). Auto-filled from your first row.</div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  type="text"
                  value={jobMetaClientCode}
                  onChange={(e) => setJobMetaClientCode(e.target.value)}
                  placeholder="e.g. RLBTest"
                  style={{
                    background: 'var(--bg)', color: 'var(--text)', border: '1px solid var(--border)',
                    borderRadius: 6, padding: '7px 10px', fontSize: 13, maxWidth: 200,
                  }}
                />
                <button
                  className="btn"
                  disabled={jobMetaLoading || !jobMetaClientCode.trim()}
                  onClick={() => loadJobMeta(jobMetaClientCode)}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}
                >
                  {jobMetaLoading
                    ? <><Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> Loading…</>
                    : <><Search size={14} /> Load templates</>}
                </button>
              </div>
            </div>

            {jobMeta && (
              <>
                {/* Service + template */}
                <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr', alignItems: 'center', gap: 12 }}>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>Service</div>
                  <select
                    value={jobService}
                    onChange={(e) => { setJobService(e.target.value); setJobTmpIdx(''); setJobNameTouched(false); setJobCodeTouched(false); }}
                    style={selStyle}
                  >
                    <option value="">— select service —</option>
                    {jobMeta.services.map((s) => (
                      <option key={s.ServIndex} value={s.ServIndex}>{s.ServTitle} ({s.ServIndex})</option>
                    ))}
                  </select>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr', alignItems: 'center', gap: 12 }}>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>Template</div>
                  <select
                    value={jobTmpIdx}
                    disabled={!jobService}
                    onChange={(e) => { setJobTmpIdx(e.target.value); setJobNameTouched(false); setJobCodeTouched(false); }}
                    style={selStyle}
                  >
                    <option value="">— select template —</option>
                    {jobMeta.templates.filter((t) => t.ServIndex === jobService).map((t) => (
                      <option key={t.JobTmp_Idx} value={t.JobTmp_Idx}>{t.JobName} — {t.JobCode}</option>
                    ))}
                  </select>
                </div>

                {/* Period + fiscal year + value */}
                <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr 1fr', alignItems: 'center', gap: 12 }}>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>Period Start / End <span style={{ color: 'var(--bad)' }}>*</span></div>
                  <input type="date" value={jobPeriodStart} onChange={(e) => setJobPeriodStart(e.target.value)} style={inStyle} />
                  <input type="date" value={jobPeriodEnd} onChange={(e) => setJobPeriodEnd(e.target.value)} style={inStyle} />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr 1fr', alignItems: 'center', gap: 12 }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>Fiscal Year / Billing Value</div>
                    <div className="muted" style={{ fontSize: 11 }}>Year fills the [YYYY] in name/code</div>
                  </div>
                  <input type="number" value={jobFiscalYear} onChange={(e) => setJobFiscalYear(e.target.value)} placeholder="2026" style={inStyle} />
                  <input type="number" step="0.01" value={jobValue} onChange={(e) => setJobValue(e.target.value)} placeholder="0.00" style={inStyle} />
                </div>

                {/* Partner / Manager are resolved per-client; QC is an optional batch constant */}
                <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr', alignItems: 'center', gap: 12 }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>SALT / TAS QC <span className="muted" style={{ fontWeight: 400, fontSize: 11 }}>(optional)</span></div>
                    <div className="muted" style={{ fontSize: 11 }}>Partner &amp; Manager are set per client (see below)</div>
                  </div>
                  <select value={jobInCharge} onChange={(e) => setJobInCharge(e.target.value)} style={{ ...selStyle, maxWidth: 320 }}>
                    {jobMeta.staff.map((s) => <option key={s.StaffIndex} value={s.StaffIndex}>{s.StaffName}</option>)}
                  </select>
                </div>

                {/* Job name / code */}
                <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr', alignItems: 'center', gap: 12 }}>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>Job Name</div>
                  <input type="text" value={jobName} onChange={(e) => { setJobName(e.target.value); setJobNameTouched(true); }} style={inStyle} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr', alignItems: 'center', gap: 12 }}>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>Job Code</div>
                  <input type="text" value={jobCode} onChange={(e) => { setJobCode(e.target.value); setJobCodeTouched(true); }} style={inStyle} />
                </div>

                <div style={{
                  fontSize: 11, color: 'var(--muted)',
                  background: 'rgba(122,162,255,.06)', border: '1px solid rgba(122,162,255,.15)',
                  borderRadius: 6, padding: 10,
                }}>
                  <strong>Partner &amp; Manager are resolved per client:</strong> the service partner/manager from
                  <code style={{ fontFamily: 'monospace' }}> tblClientServices</code> for this service. If the client
                  doesn’t have the service active, the job is created with <code style={{ fontFamily: 'monospace' }}>AddService</code>
                  and the <strong>client</strong> partner/manager (<code style={{ fontFamily: 'monospace' }}>tblEngagement</code>) are used.
                  Job name/code/period are the same for every client (uniqueness is per-client); a client that already
                  has the job is <strong>skipped</strong>, not duplicated.
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Update Client Record form block */}
      {useCase.clientRecordImport && (
        <div className="card">
          <div className="hd" style={{ fontWeight: 600 }}>Field Updates</div>
          <div className="bd" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div className="muted" style={{ fontSize: 12 }}>
              Set the values to apply to every client in your list. Leave a field blank to keep its existing value.
            </div>

            {/* Client Status + Office */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>
                  Client Status
                </div>
                <select
                  value={clientRecordParams.ClientStatus || ''}
                  onChange={(e) => setClientRecordField('ClientStatus', e.target.value)}
                  style={selStyle}
                >
                  <option value="">— no change —</option>
                  <option value="ACTIVE">Active Client</option>
                  <option value="FINANCIAL">Financial Client</option>
                  <option value="LOST">Lost Client</option>
                  <option value="NEW">New Client</option>
                  <option value="PROSPECT">Prospective Client</option>
                  <option value="SUSPENDED">Suspended Pending Loss</option>
                </select>
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Office</div>
                <input
                  type="text"
                  value={clientRecordParams.ClientOffice || ''}
                  onChange={(e) => setClientRecordField('ClientOffice', e.target.value)}
                  placeholder="e.g. BHM"
                  style={inStyle}
                />
              </div>
            </div>

            {/* Department + Sub-Department */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Department</div>
                <input
                  type="text"
                  value={clientRecordParams.ClientDepartment || ''}
                  onChange={(e) => setClientRecordField('ClientDepartment', e.target.value)}
                  placeholder="e.g. TAX"
                  style={inStyle}
                />
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Sub-Department</div>
                <input
                  type="text"
                  value={clientRecordParams.ClientSubDepartment || ''}
                  onChange={(e) => setClientRecordField('ClientSubDepartment', e.target.value)}
                  placeholder="e.g. INDIV"
                  style={inStyle}
                />
              </div>
            </div>

            {/* Ownership + Industry */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Ownership</div>
                <input
                  type="text"
                  value={clientRecordParams.ClientOwnership || ''}
                  onChange={(e) => setClientRecordField('ClientOwnership', e.target.value)}
                  placeholder="PE ownership code"
                  style={inStyle}
                />
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Industry</div>
                <input
                  type="text"
                  value={clientRecordParams.ClientIndustry || ''}
                  onChange={(e) => setClientRecordField('ClientIndustry', e.target.value)}
                  placeholder="PE industry code"
                  style={inStyle}
                />
              </div>
            </div>

            {/* Credit Status */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Credit Status</div>
                <input
                  type="text"
                  value={clientRecordParams.ClientCreditStatus || ''}
                  onChange={(e) => setClientRecordField('ClientCreditStatus', e.target.value)}
                  placeholder="e.g. CHASE"
                  style={inStyle}
                />
              </div>
            </div>

            {/* Staff assignments */}
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', marginBottom: -4 }}>
              Staff Assignments (leave blank to keep existing)
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Partner (Staff Index)</div>
                <input
                  type="number"
                  value={clientRecordParams.ClientPartnerIndex || ''}
                  onChange={(e) => setClientRecordField('ClientPartnerIndex', e.target.value)}
                  placeholder="e.g. 38933"
                  style={inStyle}
                />
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Manager (Staff Index)</div>
                <input
                  type="number"
                  value={clientRecordParams.ClientManagerIndex || ''}
                  onChange={(e) => setClientRecordField('ClientManagerIndex', e.target.value)}
                  placeholder="e.g. 49820"
                  style={inStyle}
                />
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>In-Charge (Staff Index)</div>
                <input
                  type="number"
                  value={clientRecordParams.ClientInChargeIndex || ''}
                  onChange={(e) => setClientRecordField('ClientInChargeIndex', e.target.value)}
                  placeholder="e.g. 12345"
                  style={inStyle}
                />
              </div>
            </div>

            {/* Contact address */}
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', marginBottom: -4 }}>
              Primary Contact (leave blank to keep existing)
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Street Address</div>
              <input
                type="text"
                value={clientRecordParams.ContAddress || ''}
                onChange={(e) => setClientRecordField('ContAddress', e.target.value)}
                placeholder="123 Main St"
                style={inStyle}
              />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>City</div>
                <input
                  type="text"
                  value={clientRecordParams.ContTownCity || ''}
                  onChange={(e) => setClientRecordField('ContTownCity', e.target.value)}
                  placeholder="Birmingham"
                  style={inStyle}
                />
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>State</div>
                <input
                  type="text"
                  value={clientRecordParams.ContCounty || ''}
                  onChange={(e) => setClientRecordField('ContCounty', e.target.value)}
                  placeholder="AL"
                  style={inStyle}
                />
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Zip</div>
                <input
                  type="text"
                  value={clientRecordParams.ContPostCode || ''}
                  onChange={(e) => setClientRecordField('ContPostCode', e.target.value)}
                  placeholder="35201"
                  style={inStyle}
                />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Email</div>
                <input
                  type="text"
                  value={clientRecordParams.ContEmail || ''}
                  onChange={(e) => setClientRecordField('ContEmail', e.target.value)}
                  placeholder="contact@example.com"
                  style={inStyle}
                />
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Phone</div>
                <input
                  type="text"
                  value={clientRecordParams.ContPhone || ''}
                  onChange={(e) => setClientRecordField('ContPhone', e.target.value)}
                  placeholder="205-000-0000"
                  style={inStyle}
                />
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Mobile</div>
                <input
                  type="text"
                  value={clientRecordParams.ContMobile || ''}
                  onChange={(e) => setClientRecordField('ContMobile', e.target.value)}
                  placeholder="205-000-0000"
                  style={inStyle}
                />
              </div>
            </div>

            {/* Active fields summary */}
            {Object.values(clientRecordParams).some((v) => v.trim() !== '') && (
              <div style={{
                background: 'rgba(122,162,255,.06)', border: '1px solid rgba(122,162,255,.15)',
                borderRadius: 6, padding: '8px 12px', fontSize: 12,
              }}>
                <span style={{ fontWeight: 600 }}>Will update: </span>
                <span className="muted">
                  {Object.entries(clientRecordParams)
                    .filter(([, v]) => v.trim() !== '')
                    .map(([k, v]) => `${k} → ${v}`)
                    .join(' · ')}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modify Jobs form block */}
      {useCase.jobModifyImport && (
        <div className="card">
          <div className="hd" style={{ fontWeight: 600 }}>Field Updates</div>
          <div className="bd" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div className="muted" style={{ fontSize: 12 }}>
              Set values to apply to every job in your list. Leave a field blank to keep its existing value.
            </div>

            {/* Status fields */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Job Status</div>
                <div className="muted" style={{ fontSize: 11, marginBottom: 4 }}>Numeric index — 1=open, 3=in progress, 28=completed</div>
                <input
                  type="number"
                  value={jobModifyParams.Job_Status || ''}
                  onChange={(e) => setJobModifyField('Job_Status', e.target.value)}
                  placeholder="e.g. 3"
                  style={inStyle}
                />
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Work Status</div>
                <div className="muted" style={{ fontSize: 11, marginBottom: 4 }}>Numeric StatusIndex from PE WorkStatuses</div>
                <input
                  type="number"
                  value={jobModifyParams.Job_WorkStatus || ''}
                  onChange={(e) => setJobModifyField('Job_WorkStatus', e.target.value)}
                  placeholder="e.g. 8"
                  style={inStyle}
                />
              </div>
            </div>

            {/* Org fields */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Office</div>
                <input
                  type="text"
                  value={jobModifyParams.Job_Office || ''}
                  onChange={(e) => setJobModifyField('Job_Office', e.target.value)}
                  placeholder="e.g. BHM"
                  style={inStyle}
                />
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Department</div>
                <input
                  type="text"
                  value={jobModifyParams.Job_Dept || ''}
                  onChange={(e) => setJobModifyField('Job_Dept', e.target.value)}
                  placeholder="e.g. TAX"
                  style={inStyle}
                />
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Class</div>
                <input
                  type="text"
                  value={jobModifyParams.Job_Class || ''}
                  onChange={(e) => setJobModifyField('Job_Class', e.target.value)}
                  placeholder="e.g. COMP"
                  style={inStyle}
                />
              </div>
            </div>

            {/* Staff + Name/Code */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Current Staff Index</div>
                <input
                  type="number"
                  value={jobModifyParams.Job_CurrentStaff || ''}
                  onChange={(e) => setJobModifyField('Job_CurrentStaff', e.target.value)}
                  placeholder="e.g. 38933"
                  style={inStyle}
                />
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Job Name</div>
                <input
                  type="text"
                  value={jobModifyParams.Job_Name || ''}
                  onChange={(e) => setJobModifyField('Job_Name', e.target.value)}
                  placeholder="Leave blank to keep"
                  style={inStyle}
                />
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Job Code</div>
                <input
                  type="text"
                  value={jobModifyParams.Job_Code || ''}
                  onChange={(e) => setJobModifyField('Job_Code', e.target.value)}
                  placeholder="Leave blank to keep"
                  style={inStyle}
                />
              </div>
            </div>

            {/* Active fields summary */}
            {Object.values(jobModifyParams).some((v) => v.trim() !== '') && (
              <div style={{
                background: 'rgba(122,162,255,.06)', border: '1px solid rgba(122,162,255,.15)',
                borderRadius: 6, padding: '8px 12px', fontSize: 12,
              }}>
                <span style={{ fontWeight: 600 }}>Will update: </span>
                <span className="muted">
                  {Object.entries(jobModifyParams)
                    .filter(([, v]) => v.trim() !== '')
                    .map(([k, v]) => `${k} → ${v}`)
                    .join(' · ')}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Contact status form block (only for update-contact-status use case) */}
      {useCase.contactStatusImport && (
        <div className="card">
          <div className="hd" style={{ fontWeight: 600 }}>Contact Status</div>
          <div className="bd" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div className="muted" style={{ fontSize: 12 }}>
              Applied to every ContIndex in your list. Contacts already on this status are skipped.
            </div>
            <div style={{
              display: 'inline-flex', borderRadius: 6, overflow: 'hidden',
              border: '1px solid var(--border)', alignSelf: 'flex-start',
            }}>
              {([
                { v: 'ACTIVE',   label: 'Active',   desc: 'ContStatus 0' },
                { v: 'INACTIVE', label: 'Inactive', desc: 'ContStatus 1' },
              ]).map((opt) => (
                <button
                  key={opt.v}
                  title={opt.desc}
                  onClick={() => setContactStatus(opt.v)}
                  style={{
                    padding: '6px 18px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                    border: 'none',
                    background: contactStatus === opt.v ? 'var(--accent)' : 'transparent',
                    color: contactStatus === opt.v ? '#0b0d10' : 'var(--muted)',
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            {!contactStatus && (
              <div style={{ fontSize: 12, color: 'var(--warn)', display: 'flex', alignItems: 'center', gap: 6 }}>
                <AlertCircle size={13} /> Pick a status before testing or executing.
              </div>
            )}
            {contactStatus === 'INACTIVE' && (
              <div style={{
                background: 'rgba(255,193,7,.06)', border: '1px solid rgba(255,193,7,.2)',
                borderRadius: 6, padding: 10, fontSize: 12,
              }}>
                <strong style={{ color: 'var(--warn)' }}>
                  <AlertTriangle size={13} style={{ verticalAlign: -2 }} /> Sets ContStatus to 1 only.
                </strong>{' '}
                <span className="muted">
                  It does not stamp ContDateInactive or archive the contact. Run the single-record test first
                  and confirm the contact reads as Inactive in PE before executing the batch.
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Update Contact Record form block */}
      {useCase.contactRecordImport && (
        <div className="card">
          <div className="hd" style={{ fontWeight: 600 }}>Field Updates</div>
          <div className="bd" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div className="muted" style={{ fontSize: 12 }}>
              Set the values to apply to every contact in your list. Leave a field blank to keep its existing
              value — blanks never overwrite what's already in PE.
            </div>

            <div>
              <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Street Address</div>
              <input
                type="text"
                value={contactRecordParams.ContAddress || ''}
                onChange={(e) => setContactRecordField('ContAddress', e.target.value)}
                placeholder="123 Main St"
                style={inStyle}
              />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>City</div>
                <input
                  type="text"
                  value={contactRecordParams.ContTownCity || ''}
                  onChange={(e) => setContactRecordField('ContTownCity', e.target.value)}
                  placeholder="Birmingham"
                  style={inStyle}
                />
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>State</div>
                <input
                  type="text"
                  value={contactRecordParams.ContCounty || ''}
                  onChange={(e) => setContactRecordField('ContCounty', e.target.value)}
                  placeholder="AL"
                  style={inStyle}
                />
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Zip</div>
                <input
                  type="text"
                  value={contactRecordParams.ContPostCode || ''}
                  onChange={(e) => setContactRecordField('ContPostCode', e.target.value)}
                  placeholder="35201"
                  style={inStyle}
                />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Email</div>
                <input
                  type="text"
                  value={contactRecordParams.ContEmail || ''}
                  onChange={(e) => setContactRecordField('ContEmail', e.target.value)}
                  placeholder="contact@example.com"
                  style={inStyle}
                />
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Phone</div>
                <input
                  type="text"
                  value={contactRecordParams.ContPhone || ''}
                  onChange={(e) => setContactRecordField('ContPhone', e.target.value)}
                  placeholder="(205) 000-0000"
                  style={inStyle}
                />
              </div>
            </div>

            {/* Active fields summary */}
            {Object.values(contactRecordParams).some((v) => v.trim() !== '') && (
              <div style={{
                background: 'rgba(122,162,255,.06)', border: '1px solid rgba(122,162,255,.15)',
                borderRadius: 6, padding: '8px 12px', fontSize: 12,
              }}>
                <span style={{ fontWeight: 600 }}>Will update: </span>
                <span className="muted">
                  {Object.entries(contactRecordParams)
                    .filter(([, v]) => v.trim() !== '')
                    .map(([k, v]) => `${k} → ${v}`)
                    .join(' · ')}
                </span>
              </div>
            )}

            {/* JSON column explainer */}
            <div style={{
              background: mapping['ContactJson'] ? 'rgba(122,162,255,.06)' : 'rgba(255,255,255,.02)',
              border: `1px solid ${mapping['ContactJson'] ? 'rgba(122,162,255,.15)' : 'var(--border)'}`,
              borderRadius: 6, padding: 10, fontSize: 11,
            }}>
              <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 4 }}>
                JSON column{mapping['ContactJson'] ? <span style={{ color: 'var(--accent)' }}> — mapped to “{mapping['ContactJson']}”</span> : <span className="muted" style={{ fontWeight: 400 }}> (optional)</span>}
              </div>
              <div className="muted" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div>
                  Map a <code style={{ fontFamily: 'monospace' }}>ContactJson</code> column below to supply a whole
                  object per row:
                </div>
                <code style={{
                  fontFamily: 'monospace', fontSize: 10, display: 'block',
                  background: 'rgba(0,0,0,.25)', borderRadius: 4, padding: '6px 8px',
                  whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                }}>
                  {'{"address":"605 BEL AIR BOULEVARD SUITE 21","city":"MOBILE","state":"AL","postCode":"36606","email":"mfirle@example.com","phone":"(251) 301-0050"}'}
                </code>
                <div>
                  When a row has a JSON value it is <strong style={{ color: 'var(--text)' }}>exclusive</strong> — the
                  columns and form values above are ignored for that row. Rows with a blank JSON cell fall back to
                  them as normal.
                </div>
                <div>
                  Keys are matched loosely and case-insensitively:{' '}
                  <code style={{ fontFamily: 'monospace' }}>address</code>/<code style={{ fontFamily: 'monospace' }}>street</code>,{' '}
                  <code style={{ fontFamily: 'monospace' }}>city</code>,{' '}
                  <code style={{ fontFamily: 'monospace' }}>state</code>,{' '}
                  <code style={{ fontFamily: 'monospace' }}>zip</code>/<code style={{ fontFamily: 'monospace' }}>postCode</code>,{' '}
                  <code style={{ fontFamily: 'monospace' }}>email</code>,{' '}
                  <code style={{ fontFamily: 'monospace' }}>phone</code>. Unrecognised keys are ignored and listed in
                  the result; a cell that isn't valid JSON fails that row.
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Update Job Management form block */}
      {useCase.jobManagementImport && (
        <div className="card">
          <div className="hd" style={{ fontWeight: 600 }}>Partner &amp; Manager Assignment</div>
          <div className="bd" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Mode toggle */}
            <div>
              <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 6 }}>Mode</div>
              <div style={{
                display: 'inline-flex', borderRadius: 6, overflow: 'hidden',
                border: '1px solid var(--border)',
              }}>
                {([
                  { v: 'single' as const, label: 'Update JP or JM', desc: 'One role, from a single StaffIndex column' },
                  { v: 'both'   as const, label: 'Update both JP and JM', desc: 'Both roles, from JPIndex and JMIndex columns' },
                ]).map((opt) => (
                  <button
                    key={opt.v}
                    title={opt.desc}
                    onClick={() => { setJobMgmtMode(opt.v); setTestResult(null); setTestError(null); }}
                    style={{
                      padding: '6px 16px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                      border: 'none',
                      background: jobMgmtMode === opt.v ? 'var(--accent)' : 'transparent',
                      color: jobMgmtMode === opt.v ? '#0b0d10' : 'var(--muted)',
                    }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <div className="muted" style={{ fontSize: 11, marginTop: 6 }}>
                Switching modes changes which columns you map below.
              </div>
            </div>

            {jobMgmtMode === 'single' ? (
              <>
                {/* Role picker */}
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>
                    Which role are you updating? <span style={{ color: 'var(--bad)' }}>*</span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxWidth: 460 }}>
                    {([
                      { v: 'JP' as const, label: 'JP — Job Partner',  desc: 'Writes Job_Partner on the management panel' },
                      { v: 'JM' as const, label: 'JM — Job Manager',  desc: 'Writes Job_Manager on the management panel' },
                    ]).map((opt) => (
                      <label
                        key={opt.v}
                        style={{
                          display: 'flex', alignItems: 'flex-start', gap: 10,
                          padding: '8px 10px', borderRadius: 6, cursor: 'pointer',
                          background: jobMgmtRole === opt.v ? 'rgba(122,162,255,.1)' : 'transparent',
                          border: `1px solid ${jobMgmtRole === opt.v ? 'rgba(122,162,255,.3)' : 'transparent'}`,
                        }}
                      >
                        <input
                          type="radio"
                          name="jobMgmtRole"
                          checked={jobMgmtRole === opt.v}
                          onChange={() => { setJobMgmtRole(opt.v); setTestResult(null); setTestError(null); }}
                          style={{ marginTop: 2, accentColor: 'var(--accent)' }}
                        />
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600 }}>{opt.label}</div>
                          <div style={{ fontSize: 11, color: 'var(--muted)' }}>{opt.desc}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Form-level fallback staff reference */}
                <div style={{ display: 'grid', gridTemplateColumns: '220px 1fr', alignItems: 'center', gap: 12 }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>
                      {jobMgmtRole === 'JP' ? 'Partner' : 'Manager'}
                    </div>
                    <div className="muted" style={{ fontSize: 11 }}>
                      Applied to every row. A mapped <code style={{ fontFamily: 'monospace' }}>StaffIndex</code> column wins per row.
                    </div>
                  </div>
                  <input
                    type="text"
                    value={jobMgmtStaffIndex}
                    onChange={(e) => setJobMgmtStaffIndex(e.target.value)}
                    placeholder={mapping['StaffIndex'] ? '— use the mapped column —' : 'index, name, or email'}
                    style={{ ...inStyle, maxWidth: 320 }}
                  />
                </div>

                {!jobMgmtStaffIndex.trim() && !mapping['StaffIndex'] && (
                  <div style={{ fontSize: 12, color: 'var(--warn)', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <AlertCircle size={13} /> Enter a staff index, name, or email here — or map a <code style={{ fontFamily: 'monospace' }}>StaffIndex</code> column below.
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="muted" style={{ fontSize: 12 }}>
                  Both roles are written in a single SaveManagement call per job. Values below apply to every row;
                  a mapped column wins per row.
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>
                      JP — Job Partner <span style={{ color: 'var(--bad)' }}>*</span>
                    </div>
                    <input
                      type="text"
                      value={jobMgmtJPIndex}
                      onChange={(e) => setJobMgmtJPIndex(e.target.value)}
                      placeholder={mapping['JPIndex'] ? '— use the mapped column —' : 'index, name, or email'}
                      style={inStyle}
                    />
                  </div>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>
                      JM — Job Manager <span style={{ color: 'var(--bad)' }}>*</span>
                    </div>
                    <input
                      type="text"
                      value={jobMgmtJMIndex}
                      onChange={(e) => setJobMgmtJMIndex(e.target.value)}
                      placeholder={mapping['JMIndex'] ? '— use the mapped column —' : 'index, name, or email'}
                      style={inStyle}
                    />
                  </div>
                </div>

                {(() => {
                  const missing = [
                    !jobMgmtJPIndex.trim() && !mapping['JPIndex'] ? 'JPIndex' : null,
                    !jobMgmtJMIndex.trim() && !mapping['JMIndex'] ? 'JMIndex' : null,
                  ].filter(Boolean);
                  if (missing.length === 0) return null;
                  return (
                    <div style={{ fontSize: 12, color: 'var(--warn)', display: 'flex', alignItems: 'center', gap: 6 }}>
                      <AlertCircle size={13} />
                      Set a value or map a column for {missing.join(' and ')} — “Update both” needs a source for each role.
                    </div>
                  );
                })()}
              </>
            )}

            <div style={{
              fontSize: 11, color: 'var(--muted)',
              background: 'rgba(122,162,255,.06)', border: '1px solid rgba(122,162,255,.15)',
              borderRadius: 6, padding: 10,
              display: 'flex', flexDirection: 'column', gap: 6,
            }}>
              <div>
                <strong style={{ color: 'var(--text)' }}>Each staff value can be an index, a name, or an email.</strong>{' '}
                How it's read depends on the value:
              </div>
              <ul style={{ margin: 0, paddingLeft: 16, display: 'flex', flexDirection: 'column', gap: 3 }}>
                <li>
                  <strong style={{ color: 'var(--text)' }}>All digits</strong> → used as a{' '}
                  <code style={{ fontFamily: 'monospace' }}>StaffIndex</code> as-is. Not checked against the roster, so
                  a bad index surfaces as a SaveManagement error on that row.
                </li>
                <li>
                  <strong style={{ color: 'var(--text)' }}>Contains an @</strong> → matched against{' '}
                  <code style={{ fontFamily: 'monospace' }}>tblStaff.StaffEMail</code>, then{' '}
                  <code style={{ fontFamily: 'monospace' }}>StaffUser</code>.
                </li>
                <li>
                  <strong style={{ color: 'var(--text)' }}>Anything else</strong> → matched against{' '}
                  <code style={{ fontFamily: 'monospace' }}>tblStaff.StaffName</code>. Word order doesn't matter, so
                  “Jane Doe” finds “Doe, Jane”.
                </li>
              </ul>
              <div>
                A name or email that matches nothing — or more than one person — <strong>fails that row</strong> with the
                candidates listed, rather than guessing. Use{' '}
                <code style={{ fontFamily: 'monospace' }}>0</code> to clear an assignment; a blank cell leaves the role
                untouched. Jobs already assigned to the requested staff member are <strong>skipped</strong> rather than
                re-saved, and a departed staff member is flagged but still allowed. Run the single-record test and
                confirm the job in PE before executing the batch.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Update Job Roles form block */}
      {useCase.jobRolesImport && (
        <div className="card">
          <div className="hd" style={{ fontWeight: 600 }}>Job Role Assignment</div>
          <div className="bd" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Role picker */}
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>
                Which role are you updating? <span style={{ color: 'var(--bad)' }}>*</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <select
                  value={jobRolesRoleIndex}
                  onChange={(e) => { setJobRolesRoleIndex(e.target.value); setTestResult(null); setTestError(null); }}
                  disabled={roleTypes.length === 0}
                  style={{ ...selStyle, maxWidth: 300 }}
                >
                  <option value="">
                    {roleTypesLoading ? 'Loading roles…' : roleTypes.length === 0 ? '— load data first —' : '— select a role —'}
                  </option>
                  {roleTypes.map((t) => (
                    <option key={t.RoleIndex} value={String(t.RoleIndex)}>{t.RoleName}</option>
                  ))}
                </select>
                {roleTypesLoading && <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />}
              </div>
              {roleTypes.length === 0 && !roleTypesLoading && !roleTypesError && (
                <div className="muted" style={{ fontSize: 11, marginTop: 6 }}>
                  Roles load from PE once you've loaded rows and mapped <code style={{ fontFamily: 'monospace' }}>Job_Idx</code> below.
                </div>
              )}
              {roleTypesError && (
                <div style={{ fontSize: 12, color: 'var(--bad)', marginTop: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <AlertCircle size={13} /> Couldn't load roles: {roleTypesError}
                </div>
              )}
            </div>

            {/* Mode */}
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>
                What should each row do? <span style={{ color: 'var(--bad)' }}>*</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxWidth: 520 }}>
                {([
                  { v: 'replace' as const, label: 'Replace', desc: 'The role ends up holding only the staff member in the row. Anyone else in that role is removed.' },
                  { v: 'add'     as const, label: 'Add',     desc: 'The staff member is added to the role. Anyone already in it stays, and so does the current primary.' },
                ]).map((opt) => (
                  <label
                    key={opt.v}
                    style={{
                      display: 'flex', alignItems: 'flex-start', gap: 10,
                      padding: '8px 10px', borderRadius: 6, cursor: 'pointer',
                      background: jobRolesMode === opt.v ? 'rgba(122,162,255,.1)' : 'transparent',
                      border: `1px solid ${jobRolesMode === opt.v ? 'rgba(122,162,255,.3)' : 'transparent'}`,
                    }}
                  >
                    <input
                      type="radio"
                      name="jobRolesMode"
                      checked={jobRolesMode === opt.v}
                      onChange={() => { setJobRolesMode(opt.v); setTestResult(null); setTestError(null); }}
                      style={{ marginTop: 2, accentColor: 'var(--accent)' }}
                    />
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>{opt.label}</div>
                      <div style={{ fontSize: 11, color: 'var(--muted)' }}>{opt.desc}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Form-level fallback staff reference */}
            <div style={{ display: 'grid', gridTemplateColumns: '220px 1fr', alignItems: 'center', gap: 12 }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 13 }}>Staff</div>
                <div className="muted" style={{ fontSize: 11 }}>
                  Applied to every row. A mapped <code style={{ fontFamily: 'monospace' }}>StaffIndex</code> column wins per row.
                </div>
              </div>
              <input
                type="text"
                value={jobRolesStaff}
                onChange={(e) => setJobRolesStaff(e.target.value)}
                placeholder={mapping['StaffIndex'] ? '— use the mapped column —' : 'index, name, or email'}
                style={{ ...inStyle, maxWidth: 320 }}
              />
            </div>

            {!jobRolesStaff.trim() && !mapping['StaffIndex'] && (
              <div style={{ fontSize: 12, color: 'var(--warn)', display: 'flex', alignItems: 'center', gap: 6 }}>
                <AlertCircle size={13} /> Enter a staff index, name, or email here — or map a <code style={{ fontFamily: 'monospace' }}>StaffIndex</code> column below.
              </div>
            )}

            <div style={{
              fontSize: 11, color: 'var(--muted)',
              background: 'rgba(122,162,255,.06)', border: '1px solid rgba(122,162,255,.15)',
              borderRadius: 6, padding: 10,
              display: 'flex', flexDirection: 'column', gap: 6,
            }}>
              <div>
                <strong style={{ color: 'var(--text)' }}>Each staff value can be an index, a name, or an email.</strong>{' '}
                A name or email that matches nothing — or more than one person — <strong>fails that row</strong> with the
                candidates listed, rather than guessing. A numeric index must exist in PE: PE itself accepts a
                nonexistent index without complaint, so it is checked here first.
              </div>
              <div>
                Use <code style={{ fontFamily: 'monospace' }}>0</code> to unassign the role (Replace mode only); a blank
                cell leaves the role untouched. Jobs already set the requested way are <strong>skipped</strong>, and a
                departed staff member is flagged but still allowed. A role the job doesn't have yet is created.
              </div>
              <div>
                In <strong>Replace</strong> mode a Job_Idx can appear on only one row — a second row for the same job
                fails rather than overwrite the first. Use <strong>Add</strong> to put several people in one role (one row
                per person). Run the single-record test and confirm the job in PE before executing the batch.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Client status form block (only for update-client-status use case) */}
      {useCase.clientStatusImport && (
        <div className="card">
          <div className="hd" style={{ fontWeight: 600 }}>Client Status</div>
          <div className="bd" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div className="muted" style={{ fontSize: 12 }}>
              Applied to every ContIndex in your list. Clients already on this status are skipped rather
              than re-saved.
            </div>
            <select
              value={clientStatus}
              onChange={(e) => setClientStatus(e.target.value)}
              style={{ ...selStyle, maxWidth: 300 }}
            >
              <option value="">— select a status —</option>
              {CLIENT_STATUSES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
            {!clientStatus && (
              <div style={{ fontSize: 12, color: 'var(--warn)', display: 'flex', alignItems: 'center', gap: 6 }}>
                <AlertCircle size={13} /> Pick a status before testing or executing.
              </div>
            )}
            {(clientStatus === 'LOST' || clientStatus === 'SUSPENDED') && (
              <div style={{
                background: 'rgba(255,193,7,.06)', border: '1px solid rgba(255,193,7,.2)',
                borderRadius: 6, padding: 10, fontSize: 12,
              }}>
                <strong style={{ color: 'var(--warn)' }}>
                  <AlertTriangle size={13} style={{ verticalAlign: -2 }} /> {clientStatus} is a disengagement status.
                </strong>{' '}
                <span className="muted">
                  This writes the full client record back to PE with only ClientStatus changed — it does not set
                  ContDateInactive or touch jobs. Confirm that's the whole intent before running a batch.
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Complexity form block (only for update-job-complexity use case) */}
      {useCase.jobComplexityImport && (
        <div className="card">
          <div className="hd" style={{ fontWeight: 600 }}>Complexity Level</div>
          <div className="bd" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div className="muted" style={{ fontSize: 12 }}>
              Level applied to every job in your list. If you also map a{' '}
              <code style={{ fontFamily: 'monospace' }}>Job_Complexity</code> column, the column value wins for
              rows that have one.
            </div>
            <select
              value={jobComplexity}
              onChange={(e) => setJobComplexity(e.target.value)}
              style={{ ...selStyle, maxWidth: 300 }}
            >
              <option value="">
                {mapping['Job_Complexity'] ? '— use the mapped column —' : '— select a level —'}
              </option>
              {JOB_COMPLEXITY_LEVELS.map((lvl) => (
                <option key={lvl.value} value={lvl.value}>{lvl.label}</option>
              ))}
            </select>
            {!jobComplexity && !mapping['Job_Complexity'] && (
              <div style={{ fontSize: 12, color: 'var(--warn)', display: 'flex', alignItems: 'center', gap: 6 }}>
                <AlertCircle size={13} /> Pick a level here or map a <code style={{ fontFamily: 'monospace' }}>Job_Complexity</code> column below.
              </div>
            )}
            {mapping['Job_Complexity'] && (
              <div className="muted" style={{ fontSize: 11 }}>
                Rows with a blank <code style={{ fontFamily: 'monospace' }}>Job_Complexity</code> cell are
                {jobComplexity ? ' set to the level above.' : ' skipped.'}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Budget notes (only for update-job-budget use case) */}
      {useCase.jobBudgetImport && (
        <div className="card">
          <div className="hd" style={{ fontWeight: 600 }}>How Budget Rows Are Applied</div>
          <div className="bd" style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 12 }}>
            <div className="muted">
              PE saves the whole budget grid at once, so rows are grouped by{' '}
              <code style={{ fontFamily: 'monospace' }}>Job_Idx</code> and each job is written once — list the
              same Job_Idx on as many rows as it has tasks to budget.
            </div>
            <ul className="muted" style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 4 }}>
              <li>Hours are <strong style={{ color: 'var(--text)' }}>absolute</strong> — they replace the task's current budget, not add to it.</li>
              <li>Tasks you don't list keep their existing hours, ETC, and value.</li>
              <li>ETC is set to match the hours on the tasks you do list.</li>
              <li>A task name that doesn't exist on the job fails that row only — the job's other rows still save.</li>
              <li>Jobs not budgeted at task level (<code style={{ fontFamily: 'monospace' }}>Job_BudgetDetail</code> ≠ 2) are rejected rather than mis-saved.</li>
            </ul>
          </div>
        </div>
      )}

      {/* Data Source */}
      <div className="card">
        <div className="hd" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {dataSource === 'csv' ? <FileSpreadsheet size={16} /> : <Database size={16} />}
          <span style={{ fontWeight: 600, flex: 1 }}>Data Source</span>
          <div style={{
            display: 'inline-flex', borderRadius: 6, overflow: 'hidden',
            border: '1px solid var(--border)',
          }}>
            {(['csv', 'sql'] as DataSource[]).map((ds) => (
              <button
                key={ds}
                onClick={() => switchDataSource(ds)}
                style={{
                  padding: '5px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                  border: 'none', transition: 'all .15s',
                  background: dataSource === ds ? 'var(--accent)' : 'transparent',
                  color: dataSource === ds ? '#0b0d10' : 'var(--muted)',
                  display: 'flex', alignItems: 'center', gap: 5,
                }}
              >
                {ds === 'csv' ? <><FileSpreadsheet size={12} /> CSV</> : <><Database size={12} /> SQL</>}
              </button>
            ))}
          </div>
        </div>
        <div className="bd">
          {dataSource === 'csv' ? (
            <div
              onDrop={onDrop}
              onDragOver={(e) => e.preventDefault()}
              onClick={() => fileRef.current?.click()}
              style={{
                border: '2px dashed var(--border)', borderRadius: 8, padding: '32px 20px',
                textAlign: 'center', cursor: 'pointer', transition: 'border-color .15s',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.borderColor = 'var(--accent)')}
              onMouseLeave={(e) => (e.currentTarget.style.borderColor = 'var(--border)')}
            >
              <Upload size={28} style={{ color: 'var(--muted)', marginBottom: 8 }} />
              <div style={{ fontSize: 14 }}>
                {fileName
                  ? <><strong>{fileName}</strong> — {rows.length} row{rows.length !== 1 ? 's' : ''} loaded</>
                  : 'Drop a CSV file here or click to browse'}
              </div>
              <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" onChange={onFileChange} style={{ display: 'none' }} />
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <textarea
                value={sql}
                onChange={(e) => setSql(e.target.value)}
                placeholder={'SELECT jws.Job_Idx\nFROM dbo.JobsWithStatus jws\nWHERE ...'}
                spellCheck={false}
                style={{
                  background: '#0d1117', color: '#c9d1d9', border: '1px solid var(--border)',
                  borderRadius: 6, padding: 12, fontSize: 13, fontFamily: 'Consolas, Monaco, monospace',
                  minHeight: 180, resize: 'vertical', lineHeight: 1.5,
                  tabSize: 2,
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                    e.preventDefault();
                    handleRunQuery();
                  }
                  if (e.key === 'Tab') {
                    e.preventDefault();
                    const ta = e.currentTarget;
                    const start = ta.selectionStart;
                    const end = ta.selectionEnd;
                    setSql(sql.substring(0, start) + '  ' + sql.substring(end));
                    requestAnimationFrame(() => { ta.selectionStart = ta.selectionEnd = start + 2; });
                  }
                }}
              />
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <button
                  className="btn primary"
                  disabled={sqlLoading || !sql.trim()}
                  onClick={handleRunQuery}
                  style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                >
                  {sqlLoading
                    ? <><Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> Running...</>
                    : <><Play size={14} /> Run Query</>}
                </button>
                <span className="muted" style={{ fontSize: 11 }}>Ctrl+Enter to run</span>
                {sqlTotalCount !== null && (
                  <span style={{ fontSize: 12, color: 'var(--good)' }}>
                    <CheckCircle2 size={12} style={{ verticalAlign: -2 }} /> {sqlTotalCount} row{sqlTotalCount !== 1 ? 's' : ''} returned
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Field Mapping */}
      {headers.length > 0 && (
        <div className="card">
          <div className="hd" style={{ fontWeight: 600 }}>Field Mapping</div>
          <div className="bd" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {mappableFields.map((field, i) => {
              const isOptional = i >= effectiveRequiredFields.length;
              return (
                <div key={field.key} style={{ display: 'grid', gridTemplateColumns: '180px 1fr', alignItems: 'center', gap: 12 }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>
                      {field.label}
                      {isOptional && <span className="muted" style={{ fontWeight: 400, fontSize: 11 }}> (optional)</span>}
                    </div>
                    <div className="muted" style={{ fontSize: 11 }}>{field.description}</div>
                  </div>
                  <select
                    value={mapping[field.key] || ''}
                    onChange={(e) => setMapping((m) => ({ ...m, [field.key]: e.target.value }))}
                    style={{
                      background: 'var(--bg)', color: 'var(--text)', border: '1px solid var(--border)',
                      borderRadius: 6, padding: '7px 10px', fontSize: 13, maxWidth: 300,
                    }}
                  >
                    <option value="">— {isOptional ? 'not mapped' : 'select column'} —</option>
                    {headers.map((h) => (
                      <option key={h} value={h}>{h}</option>
                    ))}
                  </select>
                </div>
              );
            })}
            {allMapped && (
              <div style={{ fontSize: 12, color: 'var(--good)', display: 'flex', alignItems: 'center', gap: 4 }}>
                <CheckCircle2 size={13} /> Required fields mapped
              </div>
            )}
          </div>
        </div>
      )}

      {/* Data Preview */}
      {rows.length > 0 && (
        <div className="card">
          <div className="hd" style={{ fontWeight: 600 }}>
            Data Preview <span className="muted" style={{ fontWeight: 400, fontSize: 12 }}>
              (first {Math.min(rows.length, 10)} of {sqlTotalCount ?? rows.length} rows)
            </span>
          </div>
          <div className="bd" style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr>
                  <th style={thStyle}>#</th>
                  {headers.map((h) => <th key={h} style={thStyle}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {rows.slice(0, 10).map((row, i) => (
                  <tr key={i} style={{ background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,.02)' }}>
                    <td style={tdStyle}>{i + 1}</td>
                    {headers.map((h) => <td key={h} style={tdStyle}><CellValue header={h} value={row[h]} /></td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Unit Test Mode */}
      {rows.length > 0 && allMapped && formValid && (
        <div className="card" style={{ border: testMode ? '1px solid var(--accent)' : undefined }}>
          <div
            className="hd"
            style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}
            onClick={() => setTestExpanded((v) => !v)}
          >
            <FlaskConical size={16} style={{ color: 'var(--accent)' }} />
            <span style={{ fontWeight: 600, flex: 1 }}>Unit Test Mode</span>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }} onClick={(e) => e.stopPropagation()}>
              <input
                type="checkbox"
                checked={testMode}
                onChange={(e) => setTestMode(e.target.checked)}
                style={{ accentColor: 'var(--accent)' }}
              />
              Enabled
            </label>
            {testExpanded ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
          </div>
          {testExpanded && (
            <div className="bd" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {/* Row selector */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <label style={{ fontSize: 13, fontWeight: 600 }}>Test Record:</label>
                <select
                  value={testRowIdx}
                  onChange={(e) => { setTestRowIdx(Number(e.target.value)); setTestResult(null); setTestError(null); }}
                  style={{
                    background: 'var(--bg)', color: 'var(--text)', border: '1px solid var(--border)',
                    borderRadius: 6, padding: '6px 10px', fontSize: 13,
                  }}
                >
                  {rows.map((_, i) => (
                    <option key={i} value={i}>Row {i + 1}</option>
                  ))}
                </select>
              </div>

              {/* Mapped values preview */}
              {testRecord && (
                <div style={{
                  background: 'rgba(122,162,255,.06)', border: '1px solid rgba(122,162,255,.15)',
                  borderRadius: 6, padding: 12,
                }}>
                  <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6, color: 'var(--accent)' }}>
                    Mapped Values for Row {testRowIdx + 1}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '4px 12px', fontSize: 13 }}>
                    {effectiveRequiredFields.map((f) => (
                      <React.Fragment key={f.key}>
                        <span className="muted">{f.label}:</span>
                        <span style={{ fontFamily: 'monospace' }}>
                          {f.key === 'Job_Idx' && testRecord[f.key]
                            ? <JobLink jobIdx={testRecord[f.key]} />
                            : testRecord[f.key]}
                        </span>
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              )}

              <button
                className="btn primary"
                disabled={testLoading}
                onClick={handleTest}
                style={{ alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: 6 }}
              >
                {testLoading
                  ? <><Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> Running...</>
                  : <><Play size={14} /> Test This Record</>}
              </button>

              {testError && (
                <div style={{
                  background: 'rgba(255,107,107,.08)', border: '1px solid rgba(255,107,107,.2)',
                  borderRadius: 6, padding: 12, fontSize: 13, color: 'var(--bad)',
                }}>
                  <strong>Error:</strong> {testError}
                </div>
              )}

              {testResult && useCaseId === 'roll-jurisdictions' && (
                <RollJurisdictionsTestResultView result={testResult as any} />
              )}
              {testResult && useCaseId === 'set-client-attributes' && (
                <SetClientAttributesTestResultView result={testResult as any} />
              )}
              {testResult && useCaseId === 'writeoff-disbursements' && (
                <WriteoffDisbursementsTestResultView result={testResult as any} />
              )}
              {testResult && useCaseId === 'update-staff-budget' && (
                <StaffBudgetTestResultView result={testResult as any} />
              )}
              {testResult && useCaseId === 'create-jobs' && (
                <CreateJobsTestResultView result={testResult as any} />
              )}
              {testResult && useCaseId === 'update-client-record' && (
                <UpdateClientRecordTestResultView result={testResult as any} />
              )}
              {testResult && useCaseId === 'modify-jobs' && (
                <ModifyJobsTestResultView result={testResult as any} />
              )}
              {testResult && useCaseId === 'update-client-status' && (
                <ClientStatusTestResultView result={testResult as any} />
              )}
              {testResult && useCaseId === 'update-contact-status' && (
                <ContactStatusTestResultView result={testResult as any} />
              )}
              {testResult && useCaseId === 'update-contact-record' && (
                <ContactRecordTestResultView result={testResult as any} />
              )}
              {testResult && useCaseId === 'update-job-budget' && (
                <JobBudgetTestResultView result={testResult as any} />
              )}
              {testResult && useCaseId === 'update-job-complexity' && (
                <JobComplexityTestResultView result={testResult as any} />
              )}
              {testResult && useCaseId === 'update-job-management' && (
                <JobManagementTestResultView result={testResult as any} />
              )}
              {testResult && useCaseId === 'update-job-roles' && (
                <JobRolesTestResultView result={testResult as any} />
              )}
              {testResult && useCaseId === 'update-service-roles' && (
                <ServiceRolesTestResultView result={testResult as any} />
              )}
              {testResult && useCaseId === 'create-client' && (
                <CreateClientTestResultView result={testResult as any} />
              )}
            </div>
          )}
        </div>
      )}

      {/* Execute */}
      {rows.length > 0 && allMapped && formValid && (
        <div className="card">
          <div className="hd" style={{ fontWeight: 600 }}>Execute Import</div>
          <div className="bd" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {testMode && !testResult && (
              <div className="muted" style={{ fontSize: 13 }}>
                Test mode is enabled — run a test on a single record above before executing the full batch.
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <button
                className="btn primary"
                disabled={executing || (testMode && !testResult)}
                onClick={handleExecuteClick}
                style={{ display: 'flex', alignItems: 'center', gap: 6 }}
              >
                {executing
                  ? <><Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> Processing...</>
                  : <><Play size={14} /> Execute All {rows.length} Record{rows.length !== 1 ? 's' : ''}</>}
              </button>
              {testMode && !testResult && (
                <span className="muted" style={{ fontSize: 12 }}>
                  (complete a test run first)
                </span>
              )}
            </div>

            {execSummary && (
              <div style={{
                background: execSummary.failed > 0 ? 'rgba(255,107,107,.08)' : 'rgba(87,211,140,.08)',
                border: `1px solid ${execSummary.failed > 0 ? 'rgba(255,107,107,.2)' : 'rgba(87,211,140,.2)'}`,
                borderRadius: 6, padding: 12,
                display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
              }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>
                    {execSummary.failed === 0
                      ? <><CheckCircle2 size={14} style={{ verticalAlign: -2, color: 'var(--good)' }} /> All records processed</>
                      : <><XCircle size={14} style={{ verticalAlign: -2, color: 'var(--bad)' }} /> {execSummary.failed} of {execSummary.total} failed</>}
                  </div>
                  <div className="muted" style={{ fontSize: 12 }}>
                    {execSummary.succeeded} succeeded, {execSummary.failed} failed{execSummary.skipped ? `, ${execSummary.skipped} skipped` : ''}
                  </div>
                </div>
                {execResults && execResults.length > 0 && (
                  <button
                    className="btn"
                    onClick={() => downloadExecuteResultsCsv(useCaseId, execResults)}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}
                  >
                    <FileSpreadsheet size={13} /> Download as Excel
                  </button>
                )}
              </div>
            )}

            {execResults && useCaseId === 'roll-jurisdictions' && (
              <RollJurisdictionsExecuteTable results={execResults} />
            )}
            {execResults && useCaseId === 'set-client-attributes' && (
              <SetClientAttributesExecuteTable results={execResults} />
            )}
            {execResults && useCaseId === 'writeoff-disbursements' && (
              <WriteoffDisbursementsExecuteTable results={execResults} />
            )}
            {execResults && useCaseId === 'update-staff-budget' && (
              <StaffBudgetExecuteTable results={execResults} />
            )}
            {execResults && useCaseId === 'create-jobs' && (
              <CreateJobsExecuteTable results={execResults} />
            )}
            {execResults && useCaseId === 'update-client-record' && (
              <UpdateClientRecordExecuteTable results={execResults} />
            )}
            {execResults && useCaseId === 'modify-jobs' && (
              <ModifyJobsExecuteTable results={execResults} />
            )}
            {execResults && useCaseId === 'update-client-status' && (
              <ClientStatusExecuteTable results={execResults} />
            )}
            {execResults && useCaseId === 'update-contact-status' && (
              <ContactStatusExecuteTable results={execResults} />
            )}
            {execResults && useCaseId === 'update-contact-record' && (
              <ContactRecordExecuteTable results={execResults} />
            )}
            {execResults && useCaseId === 'update-job-budget' && (
              <JobBudgetExecuteTable results={execResults} />
            )}
            {execResults && useCaseId === 'update-job-complexity' && (
              <JobComplexityExecuteTable results={execResults} />
            )}
            {execResults && useCaseId === 'update-job-management' && (
              <JobManagementExecuteTable results={execResults} />
            )}
            {execResults && useCaseId === 'update-job-roles' && (
              <JobRolesExecuteTable results={execResults} />
            )}
            {execResults && useCaseId === 'update-service-roles' && (
              <ServiceRolesExecuteTable results={execResults} />
            )}
            {execResults && useCaseId === 'create-client' && (
              <CreateClientExecuteTable results={execResults} />
            )}
          </div>
        </div>
      )}

      {showExecModal && (
        <ExecConfirmModal
          totalRows={rows.length}
          testedRowNum={testMode && testResult ? testRowIdx + 1 : null}
          onConfirm={handleExecuteConfirm}
          onCancel={() => setShowExecModal(false)}
        />
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
    </div>
  );
}

// ── Value input (constant mode, per FieldId type) ───────────────────────────

function ValueInput({ fieldId, value, onChange }: {
  fieldId: AttributeFieldOption['fieldId'];
  value: string;
  onChange: (v: string) => void;
}) {
  const inputStyle: React.CSSProperties = {
    background: 'var(--bg)', color: 'var(--text)', border: '1px solid var(--border)',
    borderRadius: 6, padding: '7px 10px', fontSize: 13, maxWidth: 400, width: '100%',
  };

  if (fieldId === 'AttrBool') {
    const truthy = ['true', '1', 'yes', 'y', 't'].includes(value.toLowerCase());
    return (
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
        <input
          type="checkbox"
          checked={truthy}
          onChange={(e) => onChange(e.target.checked ? 'true' : 'false')}
          style={{ accentColor: 'var(--accent)' }}
        />
        {truthy ? 'true' : 'false'}
      </label>
    );
  }
  if (fieldId === 'AttrNumber') {
    return <input type="number" step="1" value={value} onChange={(e) => onChange(e.target.value)} style={inputStyle} />;
  }
  if (fieldId === 'AttrCur') {
    return <input type="number" step="0.01" value={value} onChange={(e) => onChange(e.target.value)} style={inputStyle} />;
  }
  if (fieldId === 'AttrDate') {
    return <input type="date" value={value} onChange={(e) => onChange(e.target.value)} style={inputStyle} />;
  }
  // AttrText / AttrValid
  return <input type="text" value={value} onChange={(e) => onChange(e.target.value)} style={inputStyle} />;
}

// ── Result views ────────────────────────────────────────────────────────────

function RollJurisdictionsTestResultView({ result }: { result: any }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{
        background: 'rgba(87,211,140,.08)', border: '1px solid rgba(87,211,140,.2)',
        borderRadius: 6, padding: 12,
      }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--good)', marginBottom: 4 }}>
          <CheckCircle2 size={13} style={{ verticalAlign: -2 }} /> Test record saved to PE
        </div>
        <div className="muted" style={{ fontSize: 12 }}>
          Job <JobLink jobIdx={result.jobIdx} /> — {result.jurisdictionCount} jurisdiction(s) — Roll Forward
          {' '}— verify in PE then execute the full batch
        </div>
        {result.peResponse && (
          <div className="muted" style={{ fontSize: 11, marginTop: 4, fontFamily: 'monospace' }}>
            PE response: {result.peResponse}
          </div>
        )}
      </div>

      <div style={{ fontSize: 12 }}>
        <div style={{ fontWeight: 600, marginBottom: 6 }}>Jurisdiction Changes Preview</div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
            <thead>
              <tr>
                <th style={thStyle}>Jurisdiction</th>
                <th style={thStyle}>Original Due Date</th>
                <th style={thStyle}>New Due Date</th>
                <th style={thStyle}>Old Status</th>
                <th style={thStyle}>New Status</th>
                <th style={thStyle}>Extended Count</th>
              </tr>
            </thead>
            <tbody>
              {result.originalJurisdictions.map((orig: any, i: number) => {
                const mod: any = result.modifiedJurisdictions[i];
                return (
                  <tr key={i} style={{ background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,.02)' }}>
                    <td style={tdStyle}>{orig.JurisdictionName || orig.Description || `#${i + 1}`}</td>
                    <td style={tdStyle}>{fmtDateVal(orig.CurrentDueDate)}</td>
                    <td style={{ ...tdStyle, color: orig.CurrentDueDate !== mod.CurrentDueDate ? 'var(--accent)' : undefined }}>
                      {fmtDateVal(mod.CurrentDueDate)}
                    </td>
                    <td style={tdStyle}>{orig.StatusIndex}</td>
                    <td style={{ ...tdStyle, color: orig.StatusIndex !== mod.StatusIndex ? 'var(--accent)' : undefined }}>
                      {mod.StatusIndex}
                    </td>
                    <td style={{ ...tdStyle, color: orig.ExtendedCount !== mod.ExtendedCount ? 'var(--accent)' : undefined }}>
                      {orig.ExtendedCount} → {mod.ExtendedCount}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <details style={{ fontSize: 12 }}>
        <summary style={{ cursor: 'pointer', color: 'var(--muted)' }}>Raw SaveJurisdictions payload</summary>
        <pre style={{
          background: 'rgba(0,0,0,.3)', borderRadius: 6, padding: 12,
          overflowX: 'auto', fontSize: 11, marginTop: 6, maxHeight: 300,
        }}>
          {JSON.stringify(result.savePayload, null, 2)}
        </pre>
      </details>

      {result._debug && (
        <details style={{ fontSize: 12 }}>
          <summary style={{ cursor: 'pointer', color: 'var(--warn)' }}>Debug: PE API response shape</summary>
          <pre style={{
            background: 'rgba(0,0,0,.3)', borderRadius: 6, padding: 12,
            overflowX: 'auto', fontSize: 11, marginTop: 6, maxHeight: 200,
          }}>
            {JSON.stringify(result._debug, null, 2)}
          </pre>
        </details>
      )}
    </div>
  );
}

function SetClientAttributesTestResultView({ result }: { result: any }) {
  const item = result.submitItem || {};
  const fieldId = item.FieldId;
  const submittedValue = fieldId ? item[fieldId] : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{
        background: 'rgba(87,211,140,.08)', border: '1px solid rgba(87,211,140,.2)',
        borderRadius: 6, padding: 12,
      }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--good)', marginBottom: 4 }}>
          <CheckCircle2 size={13} style={{ verticalAlign: -2 }} /> Test record saved to PE
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '4px 12px', fontSize: 12, marginTop: 6 }}>
          <span className="muted">Client:</span>
          <span><ClientLink clientCode={result.clientCode}>{result.clientCode}{result.clientName ? ` — ${result.clientName}` : ''}</ClientLink></span>
          <span className="muted">EntityId:</span>
          <span style={{ fontFamily: 'monospace' }}>{result.entityId}</span>
          <span className="muted">AttrIndex:</span>
          <span style={{ fontFamily: 'monospace' }}>{item.AttrIndex}</span>
          <span className="muted">FieldId:</span>
          <span style={{ fontFamily: 'monospace' }}>{fieldId}</span>
          <span className="muted">Value submitted:</span>
          <span style={{ fontFamily: 'monospace' }}>{String(submittedValue)}</span>
        </div>
        {result.peResponse !== undefined && (
          <div className="muted" style={{ fontSize: 11, marginTop: 6, fontFamily: 'monospace' }}>
            PE response: {result.peResponse || '(empty — typical for SaveAttributes)'}
          </div>
        )}
      </div>

      <details style={{ fontSize: 12 }}>
        <summary style={{ cursor: 'pointer', color: 'var(--muted)' }}>Raw SaveAttributes payload</summary>
        <pre style={{
          background: 'rgba(0,0,0,.3)', borderRadius: 6, padding: 12,
          overflowX: 'auto', fontSize: 11, marginTop: 6, maxHeight: 300,
        }}>
          {JSON.stringify([item], null, 2)}
        </pre>
      </details>
    </div>
  );
}

function RollJurisdictionsExecuteTable({ results }: { results: ExecuteRowResult[] }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr>
            <th style={thStyle}>Row</th>
            <th style={thStyle}>Job Idx</th>
            <th style={thStyle}>Jurisdictions</th>
            <th style={thStyle}>Status</th>
            <th style={thStyle}>PE Response</th>
          </tr>
        </thead>
        <tbody>
          {results.map((r) => (
            <tr key={r.row} style={{ background: r.row % 2 === 0 ? 'transparent' : 'rgba(255,255,255,.02)' }}>
              <td style={tdStyle}>{r.row}</td>
              <td style={tdStyle}><JobLink jobIdx={r.jobIdx ?? ''} /></td>
              <td style={tdStyle}>{r.jurisdictionCount ?? '—'}</td>
              <td style={tdStyle}>
                {r.status === 'success'
                  ? <span style={{ color: 'var(--good)' }}><CheckCircle2 size={12} style={{ verticalAlign: -2 }} /> Success</span>
                  : <span style={{ color: 'var(--bad)' }}><XCircle size={12} style={{ verticalAlign: -2 }} /> Failed</span>}
              </td>
              <td style={{ ...tdStyle, maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', fontSize: 11, color: r.error ? 'var(--bad)' : 'var(--muted)' }}>
                {r.error || r.peResponse || '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SetClientAttributesExecuteTable({ results }: { results: ExecuteRowResult[] }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr>
            <th style={thStyle}>Row</th>
            <th style={thStyle}>Client</th>
            <th style={thStyle}>EntityId</th>
            <th style={thStyle}>Field</th>
            <th style={thStyle}>Value</th>
            <th style={thStyle}>Status</th>
            <th style={thStyle}>Error / PE Response</th>
          </tr>
        </thead>
        <tbody>
          {results.map((r) => (
            <tr key={r.row} style={{ background: r.row % 2 === 0 ? 'transparent' : 'rgba(255,255,255,.02)' }}>
              <td style={tdStyle}>{r.row}</td>
              <td style={tdStyle}>
                {r.clientCode
                  ? <ClientLink clientCode={r.clientCode}>{r.clientCode}{r.clientName ? ` — ${r.clientName}` : ''}</ClientLink>
                  : '—'}
              </td>
              <td style={tdStyle}>{r.entityId ?? '—'}</td>
              <td style={tdStyle}>{r.fieldId ?? '—'}</td>
              <td style={tdStyle}>{r.submittedValue == null ? '—' : String(r.submittedValue)}</td>
              <td style={tdStyle}>
                {r.status === 'success'
                  ? <span style={{ color: 'var(--good)' }}><CheckCircle2 size={12} style={{ verticalAlign: -2 }} /> Success</span>
                  : <span style={{ color: 'var(--bad)' }}><XCircle size={12} style={{ verticalAlign: -2 }} /> Failed</span>}
              </td>
              <td style={{ ...tdStyle, maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', fontSize: 11, color: r.error ? 'var(--bad)' : 'var(--muted)' }}>
                {r.error || r.peResponse || '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Writeoff Disbursements result views ────────────────────────────────────

function WriteoffDisbursementsTestResultView({ result }: { result: any }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{
        background: 'rgba(87,211,140,.08)', border: '1px solid rgba(87,211,140,.2)',
        borderRadius: 6, padding: 12,
      }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--good)', marginBottom: 4 }}>
          <CheckCircle2 size={13} style={{ verticalAlign: -2 }} /> Test draft created and submitted (not yet confirmed)
        </div>
        <div className="muted" style={{ fontSize: 12 }}>
          The draft sits in PE in <strong>SUBMITTED</strong> state — open it, verify the WIP/woff numbers,
          then run the full batch when you're satisfied. Confirmation queue was intentionally skipped for the test.
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: '4px 12px', fontSize: 12, marginTop: 8 }}>
          <span className="muted">Client:</span>
          <span><ClientLink clientCode={result.clientCode}>{result.clientCode}{result.clientName ? ` — ${result.clientName}` : ''}</ClientLink></span>
          <span className="muted">ContIndex:</span>
          <span style={{ fontFamily: 'monospace' }}>{result.contIndex}</span>
          <span className="muted">WIP Index (target):</span>
          <span style={{ fontFamily: 'monospace' }}>{result.wipIndex}</span>
          <span className="muted">Job (ServPeriod):</span>
          <span style={{ fontFamily: 'monospace' }}>{result.servPeriod}</span>
          <span className="muted">Outstanding:</span>
          <span style={{ fontFamily: 'monospace' }}>${Number(result.wipOutstanding ?? 0).toFixed(2)}</span>
          <span className="muted">PE pulled / kept:</span>
          <span style={{ fontFamily: 'monospace' }}>{result.pulledWipsTotal ?? '?'} pulled, {result.targetWipsTotal ?? '?'} kept</span>
          <span className="muted">Draft Idx:</span>
          <span><DraftFeeLink draftIdx={result.draftIdx}>#{result.draftIdx}</DraftFeeLink></span>
          <span className="muted">Stage:</span>
          <span style={{ fontFamily: 'monospace' }}>{result.stage}</span>
        </div>
      </div>
    </div>
  );
}

function WriteoffDisbursementsExecuteTable({ results }: { results: ExecuteRowResult[] }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr>
            <th style={thStyle}>Row</th>
            <th style={thStyle}>Client</th>
            <th style={thStyle}>WIP Index</th>
            <th style={thStyle}>Job</th>
            <th style={thStyle}>Amount</th>
            <th style={thStyle}>Draft</th>
            <th style={thStyle}>Status</th>
            <th style={thStyle}>Error / Stage</th>
          </tr>
        </thead>
        <tbody>
          {results.map((r) => (
            <tr key={r.row} style={{ background: r.row % 2 === 0 ? 'transparent' : 'rgba(255,255,255,.02)' }}>
              <td style={tdStyle}>{r.row}</td>
              <td style={tdStyle}>
                {r.clientCode
                  ? <ClientLink clientCode={r.clientCode}>{r.clientCode}{r.clientName ? ` — ${r.clientName}` : ''}</ClientLink>
                  : '—'}
              </td>
              <td style={tdStyle}>{r.wipIndex ?? '—'}</td>
              <td style={tdStyle}>{r.servPeriod ?? '—'}</td>
              <td style={tdStyle}>{r.wipOutstanding == null ? '—' : `$${Number(r.wipOutstanding).toFixed(2)}`}</td>
              <td style={tdStyle}>
                {r.draftIdx ? <DraftFeeLink draftIdx={r.draftIdx}>#{r.draftIdx}</DraftFeeLink> : '—'}
              </td>
              <td style={tdStyle}>
                {r.status === 'success'
                  ? <span style={{ color: 'var(--good)' }}><CheckCircle2 size={12} style={{ verticalAlign: -2 }} /> Success</span>
                  : <span style={{ color: 'var(--bad)' }}><XCircle size={12} style={{ verticalAlign: -2 }} /> Failed</span>}
              </td>
              <td style={{ ...tdStyle, maxWidth: 320, whiteSpace: 'normal', fontSize: 11, color: r.error ? 'var(--bad)' : 'var(--muted)' }}>
                {r.error || r.stage || '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Staff Budget result views ──────────────────────────────────────────────

function StaffBudgetTestResultView({ result }: { result: any }) {
  const rows: any[] = result.resolvedRows || [];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{
        background: 'rgba(87,211,140,.08)', border: '1px solid rgba(87,211,140,.2)',
        borderRadius: 6, padding: 12,
      }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--good)', marginBottom: 4 }}>
          <CheckCircle2 size={13} style={{ verticalAlign: -2 }} /> Test record saved to PE
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: '4px 12px', fontSize: 12, marginTop: 6 }}>
          <span className="muted">Employee:</span>
          <span style={{ fontFamily: 'monospace' }}>{result.employeeName} (StaffIndex {result.staffIndex})</span>
          <span className="muted">Budget year:</span>
          <span style={{ fontFamily: 'monospace' }}>{result.budgetYear}</span>
          <span className="muted">Months written:</span>
          <span>{(result.monthsProvided || []).join(', ') || '—'}</span>
          <span className="muted">Periods updated:</span>
          <span style={{ fontFamily: 'monospace' }}>{result.periodsResolved}</span>
        </div>
        {result.monthsSkipped && result.monthsSkipped.length > 0 && (
          <div style={{ fontSize: 11, color: 'var(--warn)', marginTop: 6 }}>
            <AlertTriangle size={11} style={{ verticalAlign: -1 }} /> Skipped (incomplete): {result.monthsSkipped.join('; ')}
          </div>
        )}
        {result.monthsUnmatched && result.monthsUnmatched.length > 0 && (
          <div style={{ fontSize: 11, color: 'var(--warn)', marginTop: 4 }}>
            <AlertTriangle size={11} style={{ verticalAlign: -1 }} /> No PE period matched: {result.monthsUnmatched.join('; ')}
          </div>
        )}
        {result.peResponse !== undefined && (
          <div className="muted" style={{ fontSize: 11, marginTop: 6, fontFamily: 'monospace' }}>
            PE response: {result.peResponse || '(empty — typical for UpdateStaffBudgets)'}
          </div>
        )}
      </div>

      {rows.length > 0 && (
        <div style={{ fontSize: 12 }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>Resolved budget rows (sent to PE)</div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
              <thead>
                <tr>
                  <th style={thStyle}>BudgetIndex</th>
                  <th style={thStyle}>PeriodIndex</th>
                  <th style={thStyle}>StaffIndex</th>
                  <th style={thStyle}>PracID</th>
                  <th style={thStyle}>ProdHrs (billable)</th>
                  <th style={thStyle}>StdHrs (total)</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} style={{ background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,.02)' }}>
                    <td style={tdStyle}>{r.BudgetIndex}</td>
                    <td style={tdStyle}>{r.PeriodIndex}</td>
                    <td style={tdStyle}>{r.StaffIndex}</td>
                    <td style={tdStyle}>{r.PracID}</td>
                    <td style={tdStyle}>{r.ProdHrs}</td>
                    <td style={tdStyle}>{r.StdHrs}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

    </div>
  );
}

function StaffBudgetExecuteTable({ results }: { results: ExecuteRowResult[] }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr>
            <th style={thStyle}>Row</th>
            <th style={thStyle}>Employee</th>
            <th style={thStyle}>Year</th>
            <th style={thStyle}>Periods Updated</th>
            <th style={thStyle}>Status</th>
            <th style={thStyle}>Error / PE Response</th>
          </tr>
        </thead>
        <tbody>
          {results.map((r) => (
            <tr key={r.row} style={{ background: r.row % 2 === 0 ? 'transparent' : 'rgba(255,255,255,.02)' }}>
              <td style={tdStyle}>{r.row}</td>
              <td style={tdStyle}>{r.employeeName ?? '—'}</td>
              <td style={tdStyle}>{r.budgetYear ?? '—'}</td>
              <td style={tdStyle}>{r.periodsUpdated ?? '—'}</td>
              <td style={tdStyle}>
                {r.status === 'success'
                  ? <span style={{ color: 'var(--good)' }}><CheckCircle2 size={12} style={{ verticalAlign: -2 }} /> Success</span>
                  : <span style={{ color: 'var(--bad)' }}><XCircle size={12} style={{ verticalAlign: -2 }} /> Failed</span>}
              </td>
              <td style={{ ...tdStyle, maxWidth: 320, whiteSpace: 'normal', fontSize: 11, color: r.error ? 'var(--bad)' : 'var(--muted)' }}>
                {r.error || r.peResponse || '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Create Jobs result views ────────────────────────────────────────────────

function CreateJobsTestResultView({ result }: { result: any }) {
  const skipped = result.status === 'skipped';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{
        background: skipped ? 'rgba(255,193,7,.08)' : 'rgba(87,211,140,.08)',
        border: `1px solid ${skipped ? 'rgba(255,193,7,.25)' : 'rgba(87,211,140,.2)'}`,
        borderRadius: 6, padding: 12,
      }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: skipped ? 'var(--warn)' : 'var(--good)', marginBottom: 4 }}>
          {skipped
            ? <><AlertTriangle size={13} style={{ verticalAlign: -2 }} /> Skipped — job already exists</>
            : <><CheckCircle2 size={13} style={{ verticalAlign: -2 }} /> Job created in PE</>}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '4px 12px', fontSize: 12, marginTop: 6 }}>
          <span className="muted">Client:</span>
          <span><ClientLink clientCode={result.clientCode}>{result.clientCode}{result.clientName ? ` — ${result.clientName}` : ''}</ClientLink> <span className="muted">(ContIndex {result.contIndex})</span></span>
          <span className="muted">Job Name:</span>
          <span style={{ fontFamily: 'monospace' }}>{result.jobName}</span>
          <span className="muted">Job Code:</span>
          <span style={{ fontFamily: 'monospace' }}>{result.jobCode}</span>
          <span className="muted">Partner / Manager:</span>
          <span>{result.partnerName ?? result.partner} / {result.managerName ?? result.manager}</span>
          <span className="muted">Service:</span>
          <span>{result.serviceActive
            ? 'active — service partner/manager used'
            : 'not active — AddService=true, client partner/manager used'}</span>
          {skipped && (<><span className="muted">Reason:</span><span>{result.reason}</span></>)}
        </div>
        {!skipped && (
          <div className="muted" style={{ fontSize: 11, marginTop: 6 }}>
            Verify the job in PE, then run the batch — the tested client can be excluded.
          </div>
        )}
      </div>

      {result.payload && (
        <details style={{ fontSize: 12 }}>
          <summary style={{ cursor: 'pointer', color: 'var(--muted)' }}>CreateTemplateJob payload</summary>
          <pre style={{
            background: 'rgba(0,0,0,.3)', borderRadius: 6, padding: 12,
            overflowX: 'auto', fontSize: 11, marginTop: 6, maxHeight: 300,
          }}>
            {JSON.stringify(result.payload, null, 2)}
          </pre>
        </details>
      )}
    </div>
  );
}

function CreateJobsExecuteTable({ results }: { results: ExecuteRowResult[] }) {
  const statusCell = (r: ExecuteRowResult) => {
    if (r.status === 'success') return <span style={{ color: 'var(--good)' }}><CheckCircle2 size={12} style={{ verticalAlign: -2 }} /> Created</span>;
    if (r.status === 'skipped') return <span style={{ color: 'var(--warn)' }}><AlertTriangle size={12} style={{ verticalAlign: -2 }} /> Skipped</span>;
    return <span style={{ color: 'var(--bad)' }}><XCircle size={12} style={{ verticalAlign: -2 }} /> Failed</span>;
  };
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr>
            <th style={thStyle}>Row</th>
            <th style={thStyle}>Client</th>
            <th style={thStyle}>Job Code</th>
            <th style={thStyle}>Partner / Manager</th>
            <th style={thStyle}>Status</th>
            <th style={thStyle}>Detail</th>
          </tr>
        </thead>
        <tbody>
          {results.map((r) => (
            <tr key={r.row} style={{ background: r.row % 2 === 0 ? 'transparent' : 'rgba(255,255,255,.02)' }}>
              <td style={tdStyle}>{r.row}</td>
              <td style={tdStyle}>
                {r.clientCode
                  ? <ClientLink clientCode={r.clientCode}>{r.clientCode}{r.clientName ? ` — ${r.clientName}` : ''}</ClientLink>
                  : '—'}
              </td>
              <td style={tdStyle}>{r.jobCode ?? '—'}</td>
              <td style={tdStyle}>
                {r.partnerName || r.managerName
                  ? <>{r.partnerName ?? '—'} / {r.managerName ?? '—'}{r.addService ? <span className="muted"> (+svc)</span> : ''}</>
                  : '—'}
              </td>
              <td style={tdStyle}>{statusCell(r)}</td>
              <td style={{ ...tdStyle, maxWidth: 320, whiteSpace: 'normal', fontSize: 11, color: r.error ? 'var(--bad)' : 'var(--muted)' }}>
                {r.error || r.reason || r.peResponse || '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Update Client Record result views ───────────────────────────────────────

function UpdateClientRecordTestResultView({ result }: { result: any }) {
  const updates: Record<string, string> = result.updates || {};
  const before: Record<string, unknown> = result.before || {};
  const fields = Object.keys(updates);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{
        background: 'rgba(87,211,140,.08)', border: '1px solid rgba(87,211,140,.2)',
        borderRadius: 6, padding: 12,
      }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--good)', marginBottom: 4 }}>
          <CheckCircle2 size={13} style={{ verticalAlign: -2 }} /> Client record saved to PE
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '4px 12px', fontSize: 12, marginTop: 6 }}>
          <span className="muted">Client:</span>
          <span><ClientLink clientCode={result.clientCode}>{result.clientCode}{result.clientName ? ` — ${result.clientName}` : ''}</ClientLink> <span className="muted">(ContIndex {result.contIndex})</span></span>
          <span className="muted">Fields updated:</span>
          <span style={{ fontFamily: 'monospace' }}>{fields.length > 0 ? fields.join(', ') : '—'}</span>
        </div>
        {result.peResponse !== undefined && (
          <div className="muted" style={{ fontSize: 11, marginTop: 6, fontFamily: 'monospace' }}>
            PE response: {result.peResponse || '(empty)'}
          </div>
        )}
      </div>

      {fields.length > 0 && (
        <div style={{ fontSize: 12 }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>Field Changes</div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
            <thead>
              <tr>
                <th style={thStyle}>Field</th>
                <th style={thStyle}>Before</th>
                <th style={thStyle}>After</th>
              </tr>
            </thead>
            <tbody>
              {fields.map((field, i) => (
                <tr key={field} style={{ background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,.02)' }}>
                  <td style={{ ...tdStyle, fontFamily: 'monospace' }}>{field}</td>
                  <td style={{ ...tdStyle, color: 'var(--muted)' }}>{before[field] != null ? String(before[field]) : '—'}</td>
                  <td style={{ ...tdStyle, color: 'var(--accent)' }}>{updates[field]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <details style={{ fontSize: 12 }}>
        <summary style={{ cursor: 'pointer', color: 'var(--muted)' }}>Raw updates payload</summary>
        <pre style={{
          background: 'rgba(0,0,0,.3)', borderRadius: 6, padding: 12,
          overflowX: 'auto', fontSize: 11, marginTop: 6, maxHeight: 300,
        }}>
          {JSON.stringify(updates, null, 2)}
        </pre>
      </details>
    </div>
  );
}

function UpdateClientRecordExecuteTable({ results }: { results: ExecuteRowResult[] }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr>
            <th style={thStyle}>Row</th>
            <th style={thStyle}>Client Code</th>
            <th style={thStyle}>Client Name</th>
            <th style={thStyle}>ContIndex</th>
            <th style={thStyle}>Fields Updated</th>
            <th style={thStyle}>Status</th>
            <th style={thStyle}>Error</th>
          </tr>
        </thead>
        <tbody>
          {results.map((r) => (
            <tr key={r.row} style={{ background: r.row % 2 === 0 ? 'transparent' : 'rgba(255,255,255,.02)' }}>
              <td style={tdStyle}>{r.row}</td>
              <td style={tdStyle}>{r.clientCode ? <ClientLink clientCode={r.clientCode}>{r.clientCode}</ClientLink> : '—'}</td>
              <td style={tdStyle}>{r.clientName ?? '—'}</td>
              <td style={{ ...tdStyle, fontFamily: 'monospace' }}>{r.contIndex ?? '—'}</td>
              <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: 11 }}>{(r as any).updatedFields ?? (r as any).updates ? Object.keys((r as any).updates || {}).join(', ') : '—'}</td>
              <td style={tdStyle}>
                {r.status === 'success'
                  ? <span style={{ color: 'var(--good)' }}><CheckCircle2 size={12} style={{ verticalAlign: -2 }} /> Saved</span>
                  : <span style={{ color: 'var(--bad)' }}><XCircle size={12} style={{ verticalAlign: -2 }} /> Failed</span>}
              </td>
              <td style={{ ...tdStyle, maxWidth: 320, whiteSpace: 'normal', fontSize: 11, color: r.error ? 'var(--bad)' : 'var(--muted)' }}>
                {r.error || '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Modify Jobs result views ─────────────────────────────────────────────────

function ModifyJobsTestResultView({ result }: { result: any }) {
  const updates: Record<string, string> = result.updates || {};
  const before: Record<string, unknown> = result.before || {};
  const fields = Object.keys(updates);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{
        background: 'rgba(87,211,140,.08)', border: '1px solid rgba(87,211,140,.2)',
        borderRadius: 6, padding: 12,
      }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--good)', marginBottom: 4 }}>
          <CheckCircle2 size={13} style={{ verticalAlign: -2 }} /> Job saved to PE
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '4px 12px', fontSize: 12, marginTop: 6 }}>
          <span className="muted">Job_Idx:</span>
          <span><JobLink jobIdx={result.jobIdx} /></span>
          <span className="muted">Client:</span>
          <span>{result.clientCode ? <ClientLink clientCode={result.clientCode}>{result.clientCode}</ClientLink> : '—'}</span>
          <span className="muted">Job Name:</span>
          <span>{result.jobName || '—'}</span>
          <span className="muted">Fields updated:</span>
          <span style={{ fontFamily: 'monospace' }}>{fields.length > 0 ? fields.join(', ') : '—'}</span>
        </div>
        {result.peResponse !== undefined && (
          <div className="muted" style={{ fontSize: 11, marginTop: 6, fontFamily: 'monospace' }}>
            PE response: {result.peResponse || '(empty)'}
          </div>
        )}
      </div>

      {fields.length > 0 && (
        <div style={{ fontSize: 12 }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>Field Changes</div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
            <thead>
              <tr>
                <th style={thStyle}>Field</th>
                <th style={thStyle}>Before</th>
                <th style={thStyle}>After</th>
              </tr>
            </thead>
            <tbody>
              {fields.map((field, i) => (
                <tr key={field} style={{ background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,.02)' }}>
                  <td style={{ ...tdStyle, fontFamily: 'monospace' }}>{field}</td>
                  <td style={{ ...tdStyle, color: 'var(--muted)' }}>{before[field] != null ? String(before[field]) : '—'}</td>
                  <td style={{ ...tdStyle, color: 'var(--accent)' }}>{updates[field]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <details style={{ fontSize: 12 }}>
        <summary style={{ cursor: 'pointer', color: 'var(--muted)' }}>Raw updates payload</summary>
        <pre style={{
          background: 'rgba(0,0,0,.3)', borderRadius: 6, padding: 12,
          overflowX: 'auto', fontSize: 11, marginTop: 6, maxHeight: 300,
        }}>
          {JSON.stringify(updates, null, 2)}
        </pre>
      </details>
    </div>
  );
}

function ModifyJobsExecuteTable({ results }: { results: ExecuteRowResult[] }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr>
            <th style={thStyle}>Row</th>
            <th style={thStyle}>Job_Idx</th>
            <th style={thStyle}>Client Code</th>
            <th style={thStyle}>Job Name</th>
            <th style={thStyle}>Fields Updated</th>
            <th style={thStyle}>Status</th>
            <th style={thStyle}>Error</th>
          </tr>
        </thead>
        <tbody>
          {results.map((r) => (
            <tr key={r.row} style={{ background: r.row % 2 === 0 ? 'transparent' : 'rgba(255,255,255,.02)' }}>
              <td style={tdStyle}>{r.row}</td>
              <td style={{ ...tdStyle, fontFamily: 'monospace' }}>{r.jobIdx ? <JobLink jobIdx={r.jobIdx} /> : '—'}</td>
              <td style={tdStyle}>{r.clientCode ? <ClientLink clientCode={r.clientCode}>{r.clientCode}</ClientLink> : '—'}</td>
              <td style={tdStyle}>{(r as any).jobName ?? '—'}</td>
              <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: 11 }}>{(r as any).updates ? Object.keys((r as any).updates || {}).join(', ') : '—'}</td>
              <td style={tdStyle}>
                {r.status === 'success'
                  ? <span style={{ color: 'var(--good)' }}><CheckCircle2 size={12} style={{ verticalAlign: -2 }} /> Saved</span>
                  : <span style={{ color: 'var(--bad)' }}><XCircle size={12} style={{ verticalAlign: -2 }} /> Failed</span>}
              </td>
              <td style={{ ...tdStyle, maxWidth: 320, whiteSpace: 'normal', fontSize: 11, color: r.error ? 'var(--bad)' : 'var(--muted)' }}>
                {r.error || '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Client Status result views ───────────────────────────────────────────────

function ClientStatusTestResultView({ result }: { result: any }) {
  if (result.status === 'skipped') {
    return (
      <div style={{
        background: 'rgba(255,255,255,.03)', border: '1px solid var(--border)',
        borderRadius: 6, padding: 12, fontSize: 13,
      }}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>Row skipped — nothing saved</div>
        <div className="muted" style={{ fontSize: 12 }}>{result.reason || 'No status change needed'}</div>
      </div>
    );
  }

  return (
    <div style={{
      background: 'rgba(87,211,140,.08)', border: '1px solid rgba(87,211,140,.2)',
      borderRadius: 6, padding: 12,
    }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--good)', marginBottom: 4 }}>
        <CheckCircle2 size={13} style={{ verticalAlign: -2 }} /> Client status saved to PE
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '4px 12px', fontSize: 12, marginTop: 6 }}>
        <span className="muted">ContIndex:</span>
        <span style={{ fontFamily: 'monospace' }}>{result.contIndex}</span>
        <span className="muted">Client:</span>
        <span>
          {result.clientCode
            ? <ClientLink clientCode={result.clientCode}>{result.clientCode}</ClientLink>
            : '—'}
          {result.clientName ? ` — ${result.clientName}` : ''}
        </span>
        <span className="muted">Status:</span>
        <span>
          <span style={{ color: 'var(--muted)' }}>{result.statusBefore || '—'}</span>
          {' → '}
          <span style={{ color: 'var(--accent)', fontWeight: 600 }}>{result.statusAfter}</span>
        </span>
      </div>
      {result.peResponse !== undefined && (
        <div className="muted" style={{ fontSize: 11, marginTop: 6, fontFamily: 'monospace' }}>
          PE response: {result.peResponse || '(empty)'}
        </div>
      )}
    </div>
  );
}

function ClientStatusExecuteTable({ results }: { results: ExecuteRowResult[] }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr>
            <th style={thStyle}>Row</th>
            <th style={thStyle}>ContIndex</th>
            <th style={thStyle}>Client Code</th>
            <th style={thStyle}>Client Name</th>
            <th style={thStyle}>Before</th>
            <th style={thStyle}>After</th>
            <th style={thStyle}>Status</th>
            <th style={thStyle}>Detail</th>
          </tr>
        </thead>
        <tbody>
          {results.map((r) => (
            <tr key={r.row} style={{ background: r.row % 2 === 0 ? 'transparent' : 'rgba(255,255,255,.02)' }}>
              <td style={tdStyle}>{r.row}</td>
              <td style={{ ...tdStyle, fontFamily: 'monospace' }}>{r.contIndex ?? '—'}</td>
              <td style={tdStyle}>{r.clientCode ? <ClientLink clientCode={r.clientCode}>{r.clientCode}</ClientLink> : '—'}</td>
              <td style={tdStyle}>{r.clientName || '—'}</td>
              <td style={{ ...tdStyle, color: 'var(--muted)' }}>{r.statusBefore || '—'}</td>
              <td style={{ ...tdStyle, color: r.status === 'success' ? 'var(--accent)' : undefined }}>{r.statusAfter || '—'}</td>
              <td style={tdStyle}>
                {r.status === 'success'
                  ? <span style={{ color: 'var(--good)' }}><CheckCircle2 size={12} style={{ verticalAlign: -2 }} /> Saved</span>
                  : r.status === 'skipped'
                    ? <span className="muted">Skipped</span>
                    : <span style={{ color: 'var(--bad)' }}><XCircle size={12} style={{ verticalAlign: -2 }} /> Failed</span>}
              </td>
              <td style={{ ...tdStyle, maxWidth: 380, whiteSpace: 'normal', fontSize: 11, color: r.error ? 'var(--bad)' : 'var(--muted)' }}>
                {r.error || r.reason || '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Contact Status result views ──────────────────────────────────────────────

function ContactStatusTestResultView({ result }: { result: any }) {
  if (result.status === 'skipped') {
    return (
      <div style={{
        background: 'rgba(255,255,255,.03)', border: '1px solid var(--border)',
        borderRadius: 6, padding: 12, fontSize: 13,
      }}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>Row skipped — nothing saved</div>
        <div className="muted" style={{ fontSize: 12 }}>{result.reason || 'No status change needed'}</div>
      </div>
    );
  }

  return (
    <div style={{
      background: 'rgba(87,211,140,.08)', border: '1px solid rgba(87,211,140,.2)',
      borderRadius: 6, padding: 12,
    }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--good)', marginBottom: 4 }}>
        <CheckCircle2 size={13} style={{ verticalAlign: -2 }} /> Contact status saved to PE
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '150px 1fr', gap: '4px 12px', fontSize: 12, marginTop: 6 }}>
        <span className="muted">ContIndex:</span>
        <span style={{ fontFamily: 'monospace' }}>{result.contIndex}</span>
        <span className="muted">Contact:</span>
        <span>{result.contName || '—'}</span>
        <span className="muted">ContStatus:</span>
        <span>
          <span style={{ color: 'var(--muted)' }}>
            {result.statusBefore}{result.statusBeforeName ? ` (${result.statusBeforeName})` : ''}
          </span>
          {' → '}
          <span style={{ color: 'var(--accent)', fontWeight: 600 }}>
            {result.statusAfter}{result.statusAfterName ? ` (${result.statusAfterName})` : ''}
          </span>
        </span>
        <span className="muted">Loaded via:</span>
        <span>
          {result.loadedVia === 'client'
            ? 'Clients/LoadClient — this ContIndex is also a client, so the full client record round-tripped'
            : 'Contacts/LoadContact — standalone contact'}
        </span>
      </div>
      {result.peResponse !== undefined && (
        <div className="muted" style={{ fontSize: 11, marginTop: 6, fontFamily: 'monospace' }}>
          PE response: {result.peResponse || '(empty)'}
        </div>
      )}
    </div>
  );
}

function ContactStatusExecuteTable({ results }: { results: ExecuteRowResult[] }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr>
            <th style={thStyle}>Row</th>
            <th style={thStyle}>ContIndex</th>
            <th style={thStyle}>Contact</th>
            <th style={thStyle}>Before</th>
            <th style={thStyle}>After</th>
            <th style={thStyle}>Loaded Via</th>
            <th style={thStyle}>Status</th>
            <th style={thStyle}>Detail</th>
          </tr>
        </thead>
        <tbody>
          {results.map((r) => (
            <tr key={r.row} style={{ background: r.row % 2 === 0 ? 'transparent' : 'rgba(255,255,255,.02)' }}>
              <td style={tdStyle}>{r.row}</td>
              <td style={{ ...tdStyle, fontFamily: 'monospace' }}>{r.contIndex ?? '—'}</td>
              <td style={tdStyle}>{r.contName || '—'}</td>
              <td style={{ ...tdStyle, color: 'var(--muted)' }}>{r.statusBeforeName || r.statusBefore || '—'}</td>
              <td style={{ ...tdStyle, color: r.status === 'success' ? 'var(--accent)' : undefined }}>
                {r.statusAfterName || r.statusAfter || '—'}
              </td>
              <td style={{ ...tdStyle, color: 'var(--muted)', fontSize: 11 }}>{r.loadedVia || '—'}</td>
              <td style={tdStyle}>
                {r.status === 'success'
                  ? <span style={{ color: 'var(--good)' }}><CheckCircle2 size={12} style={{ verticalAlign: -2 }} /> Saved</span>
                  : r.status === 'skipped'
                    ? <span className="muted">Skipped</span>
                    : <span style={{ color: 'var(--bad)' }}><XCircle size={12} style={{ verticalAlign: -2 }} /> Failed</span>}
              </td>
              <td style={{ ...tdStyle, maxWidth: 340, whiteSpace: 'normal', fontSize: 11, color: r.error ? 'var(--bad)' : 'var(--muted)' }}>
                {r.error || r.reason || '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Contact Record result views ──────────────────────────────────────────────

function ContactRecordTestResultView({ result }: { result: any }) {
  const updates: Record<string, string> = result.updates || {};
  const before: Record<string, unknown> = result.before || {};
  const fields = Object.keys(updates);
  const ignored: string[] = result.ignoredKeys || [];

  if (result.status === 'skipped') {
    return (
      <div style={{
        background: 'rgba(255,255,255,.03)', border: '1px solid var(--border)',
        borderRadius: 6, padding: 12, fontSize: 13,
      }}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>Row skipped — nothing saved</div>
        <div className="muted" style={{ fontSize: 12 }}>{result.reason || 'No field values to apply'}</div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{
        background: 'rgba(87,211,140,.08)', border: '1px solid rgba(87,211,140,.2)',
        borderRadius: 6, padding: 12,
      }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--good)', marginBottom: 4 }}>
          <CheckCircle2 size={13} style={{ verticalAlign: -2 }} /> Contact record saved to PE
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '150px 1fr', gap: '4px 12px', fontSize: 12, marginTop: 6 }}>
          <span className="muted">ContIndex:</span>
          <span style={{ fontFamily: 'monospace' }}>{result.contIndex}</span>
          <span className="muted">Contact:</span>
          <span>{result.contName || '—'}</span>
          <span className="muted">Fields updated:</span>
          <span style={{ fontFamily: 'monospace' }}>{fields.length > 0 ? fields.join(', ') : '—'}</span>
          <span className="muted">Values from:</span>
          <span>{result.valueSource === 'json' ? 'the row’s JSON object' : 'mapped columns / form'}</span>
          <span className="muted">Loaded via:</span>
          <span>
            {result.loadedVia === 'client'
              ? 'Clients/LoadClient — this ContIndex is also a client, so the full client record round-tripped'
              : 'Contacts/LoadContact — standalone contact'}
          </span>
        </div>
        {result.peResponse !== undefined && (
          <div className="muted" style={{ fontSize: 11, marginTop: 6, fontFamily: 'monospace' }}>
            PE response: {result.peResponse || '(empty)'}
          </div>
        )}
      </div>

      {(result.alreadyMatching?.length > 0 || ignored.length > 0) && (
        <div style={{
          background: 'rgba(255,193,7,.06)', border: '1px solid rgba(255,193,7,.2)',
          borderRadius: 6, padding: 10, fontSize: 12,
        }}>
          {result.alreadyMatching?.length > 0 && (
            <div className="muted">
              <strong style={{ color: 'var(--text)' }}>Already matched PE, not re-sent:</strong>{' '}
              <span style={{ fontFamily: 'monospace' }}>{result.alreadyMatching.join(', ')}</span>
            </div>
          )}
          {ignored.length > 0 && (
            <div className="muted" style={{ marginTop: result.alreadyMatching?.length > 0 ? 4 : 0 }}>
              <strong style={{ color: 'var(--warn)' }}>Unrecognised JSON keys, ignored:</strong>{' '}
              <span style={{ fontFamily: 'monospace' }}>{ignored.join(', ')}</span>
            </div>
          )}
        </div>
      )}

      {fields.length > 0 && (
        <div style={{ fontSize: 12 }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>Field Changes</div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
            <thead>
              <tr>
                <th style={thStyle}>Field</th>
                <th style={thStyle}>Before</th>
                <th style={thStyle}>After</th>
              </tr>
            </thead>
            <tbody>
              {fields.map((field, i) => (
                <tr key={field} style={{ background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,.02)' }}>
                  <td style={{ ...tdStyle, fontFamily: 'monospace' }}>{field}</td>
                  <td style={{ ...tdStyle, color: 'var(--muted)' }}>{before[field] != null && before[field] !== '' ? String(before[field]) : '—'}</td>
                  <td style={{ ...tdStyle, color: 'var(--accent)' }}>{updates[field]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ContactRecordExecuteTable({ results }: { results: ExecuteRowResult[] }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr>
            <th style={thStyle}>Row</th>
            <th style={thStyle}>ContIndex</th>
            <th style={thStyle}>Contact</th>
            <th style={thStyle}>Fields Updated</th>
            <th style={thStyle}>Source</th>
            <th style={thStyle}>Loaded Via</th>
            <th style={thStyle}>Status</th>
            <th style={thStyle}>Detail</th>
          </tr>
        </thead>
        <tbody>
          {results.map((r) => (
            <tr key={r.row} style={{ background: r.row % 2 === 0 ? 'transparent' : 'rgba(255,255,255,.02)' }}>
              <td style={tdStyle}>{r.row}</td>
              <td style={{ ...tdStyle, fontFamily: 'monospace' }}>{r.contIndex ?? '—'}</td>
              <td style={tdStyle}>{r.contName || '—'}</td>
              <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: 11 }}>{r.updatedFields || '—'}</td>
              <td style={{ ...tdStyle, color: 'var(--muted)', fontSize: 11 }}>{r.valueSource === 'json' ? 'JSON' : r.valueSource ? 'columns/form' : '—'}</td>
              <td style={{ ...tdStyle, color: 'var(--muted)', fontSize: 11 }}>{r.loadedVia || '—'}</td>
              <td style={tdStyle}>
                {r.status === 'success'
                  ? <span style={{ color: 'var(--good)' }}><CheckCircle2 size={12} style={{ verticalAlign: -2 }} /> Saved</span>
                  : r.status === 'skipped'
                    ? <span className="muted">Skipped</span>
                    : <span style={{ color: 'var(--bad)' }}><XCircle size={12} style={{ verticalAlign: -2 }} /> Failed</span>}
              </td>
              <td style={{ ...tdStyle, maxWidth: 320, whiteSpace: 'normal', fontSize: 11, color: r.error ? 'var(--bad)' : 'var(--muted)' }}>
                {r.error || r.reason || (r.ignoredKeys?.length ? `Ignored keys: ${r.ignoredKeys.join(', ')}` : '—')}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Job Management result views ──────────────────────────────────────────────

/** "38933 (Doe, Jane)" when a name is known, else just the index. */
function staffLabel(idx: number | null | undefined, name?: string): string {
  if (idx == null) return '—';
  return name ? `${idx} (${name})` : String(idx);
}

// How the backend interpreted each supplied staff value.
const MATCHED_ON_LABELS: Record<string, string> = {
  'index':          'StaffIndex',
  'email':          'email',
  'name':           'name',
  'name-reordered': 'name (reordered)',
  'user':           'login',
};

function JobManagementTestResultView({ result }: { result: any }) {
  const changes: any[] = result.changes || [];

  if (result.status === 'skipped') {
    return (
      <div style={{
        background: 'rgba(255,255,255,.03)', border: '1px solid var(--border)',
        borderRadius: 6, padding: 12, fontSize: 13,
      }}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>Row skipped — nothing saved</div>
        <div className="muted" style={{ fontSize: 12 }}>{result.reason || 'No assignment change needed'}</div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{
        background: 'rgba(87,211,140,.08)', border: '1px solid rgba(87,211,140,.2)',
        borderRadius: 6, padding: 12,
      }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--good)', marginBottom: 4 }}>
          <CheckCircle2 size={13} style={{ verticalAlign: -2 }} /> Job management saved to PE
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '150px 1fr', gap: '4px 12px', fontSize: 12, marginTop: 6 }}>
          <span className="muted">Job_Idx:</span>
          <span><JobLink jobIdx={result.jobIdx} /></span>
          <span className="muted">Mode:</span>
          <span>{result.mode === 'both' ? 'Update both JP and JM' : 'Update JP or JM'}</span>
          <span className="muted">Roles updated:</span>
          <span>{result.rolesUpdated || '—'}</span>
          <span className="muted">Resolved by:</span>
          <span style={{ fontFamily: 'monospace', fontSize: 11 }}>{result.matchedOn || '—'}</span>
        </div>
        {result.peResponse !== undefined && (
          <div className="muted" style={{ fontSize: 11, marginTop: 6, fontFamily: 'monospace' }}>
            PE response: {result.peResponse || '(empty)'}
          </div>
        )}
      </div>

      {result.warnings?.length > 0 && (
        <div style={{
          background: 'rgba(255,193,7,.06)', border: '1px solid rgba(255,193,7,.2)',
          borderRadius: 6, padding: 10, fontSize: 12,
        }}>
          {result.warnings.map((w: string, i: number) => (
            <div key={i} className="muted">
              <AlertTriangle size={12} style={{ verticalAlign: -2, color: 'var(--warn)' }} /> {w}
            </div>
          ))}
        </div>
      )}

      {changes.length > 0 && (
        <div style={{ fontSize: 12 }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>Assignment Changes</div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
            <thead>
              <tr>
                <th style={thStyle}>Role</th>
                <th style={thStyle}>PE Field</th>
                <th style={thStyle}>Before</th>
                <th style={thStyle}>After</th>
                <th style={thStyle}>Matched On</th>
                <th style={thStyle}>Value From</th>
              </tr>
            </thead>
            <tbody>
              {changes.map((c, i) => (
                <tr key={c.role} style={{ background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,.02)' }}>
                  <td style={tdStyle}>{c.roleLabel}</td>
                  <td style={{ ...tdStyle, fontFamily: 'monospace' }}>{c.field}</td>
                  <td style={{ ...tdStyle, color: 'var(--muted)' }}>{staffLabel(c.before, c.beforeName)}</td>
                  <td style={{ ...tdStyle, color: 'var(--accent)' }}>
                    {staffLabel(c.after, c.afterName)}
                    {c.afterEmail && <span className="muted"> · {c.afterEmail}</span>}
                  </td>
                  <td style={{ ...tdStyle, color: 'var(--muted)' }}>{MATCHED_ON_LABELS[c.matchedOn] ?? c.matchedOn ?? '—'}</td>
                  <td style={{ ...tdStyle, color: 'var(--muted)' }}>{c.source === 'row' ? 'CSV column' : 'form'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {result.unchanged?.length > 0 && (
        <div className="muted" style={{ fontSize: 11 }}>
          Left alone (already assigned):{' '}
          {result.unchanged.map((u: any) => `${u.roleLabel} ${staffLabel(u.after, u.afterName)}`).join(' · ')}
        </div>
      )}

      <details style={{ fontSize: 12 }}>
        <summary style={{ cursor: 'pointer', color: 'var(--muted)' }}>Raw SaveManagement payload</summary>
        <pre style={{
          background: 'rgba(0,0,0,.3)', borderRadius: 6, padding: 12,
          overflowX: 'auto', fontSize: 11, marginTop: 6, maxHeight: 300,
        }}>
          {JSON.stringify(result.savePayload ?? {}, null, 2)}
        </pre>
      </details>
    </div>
  );
}

function JobManagementExecuteTable({ results }: { results: ExecuteRowResult[] }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr>
            <th style={thStyle}>Row</th>
            <th style={thStyle}>Job_Idx</th>
            <th style={thStyle}>Partner Before</th>
            <th style={thStyle}>Partner After</th>
            <th style={thStyle}>Manager Before</th>
            <th style={thStyle}>Manager After</th>
            <th style={thStyle}>Roles Updated</th>
            <th style={thStyle}>Matched On</th>
            <th style={thStyle}>Status</th>
            <th style={thStyle}>Detail</th>
          </tr>
        </thead>
        <tbody>
          {results.map((r) => (
            <tr key={r.row} style={{ background: r.row % 2 === 0 ? 'transparent' : 'rgba(255,255,255,.02)' }}>
              <td style={tdStyle}>{r.row}</td>
              <td style={{ ...tdStyle, fontFamily: 'monospace' }}>{r.jobIdx ? <JobLink jobIdx={r.jobIdx} /> : '—'}</td>
              <td style={{ ...tdStyle, color: 'var(--muted)' }}>{staffLabel(r.partnerBefore, r.partnerBeforeName)}</td>
              <td style={{ ...tdStyle, color: r.status === 'success' ? 'var(--accent)' : undefined }}>{staffLabel(r.partnerAfter, r.partnerAfterName)}</td>
              <td style={{ ...tdStyle, color: 'var(--muted)' }}>{staffLabel(r.managerBefore, r.managerBeforeName)}</td>
              <td style={{ ...tdStyle, color: r.status === 'success' ? 'var(--accent)' : undefined }}>{staffLabel(r.managerAfter, r.managerAfterName)}</td>
              <td style={{ ...tdStyle, fontSize: 11 }}>{r.rolesUpdated || '—'}</td>
              <td style={{ ...tdStyle, color: 'var(--muted)', fontSize: 11 }}>{r.matchedOn || '—'}</td>
              <td style={tdStyle}>
                {r.status === 'success'
                  ? <span style={{ color: 'var(--good)' }}><CheckCircle2 size={12} style={{ verticalAlign: -2 }} /> Saved</span>
                  : r.status === 'skipped'
                    ? <span className="muted">Skipped</span>
                    : <span style={{ color: 'var(--bad)' }}><XCircle size={12} style={{ verticalAlign: -2 }} /> Failed</span>}
              </td>
              <td style={{ ...tdStyle, maxWidth: 320, whiteSpace: 'normal', fontSize: 11, color: r.error ? 'var(--bad)' : 'var(--muted)' }}>
                {r.error || r.reason || (r.warnings?.length ? r.warnings.join('; ') : '—')}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Service Roles result views ───────────────────────────────────────────────

function ServiceRolesTestResultView({ result }: { result: any }) {
  const changes: any[] = result.changes || [];

  if (result.status === 'skipped') {
    return (
      <div style={{
        background: 'rgba(255,255,255,.03)', border: '1px solid var(--border)',
        borderRadius: 6, padding: 12, fontSize: 13,
      }}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>Row skipped — nothing saved</div>
        <div className="muted" style={{ fontSize: 12 }}>{result.reason || 'No assignment change needed'}</div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{
        background: 'rgba(87,211,140,.08)', border: '1px solid rgba(87,211,140,.2)',
        borderRadius: 6, padding: 12,
      }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--good)', marginBottom: 4 }}>
          <CheckCircle2 size={13} style={{ verticalAlign: -2 }} /> Service roles saved to PE{result.verified ? ' and confirmed by read-back' : ''}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '150px 1fr', gap: '4px 12px', fontSize: 12, marginTop: 6 }}>
          <span className="muted">ContIndex:</span>
          <span style={{ fontFamily: 'monospace' }}>{result.contIndex}</span>
          <span className="muted">Service:</span>
          <span>{result.servTitle ? `${result.servTitle} (${result.servIndex})` : result.servIndex}</span>
          <span className="muted">Roles updated:</span>
          <span>{result.rolesUpdated || '—'}</span>
          <span className="muted">Resolved by:</span>
          <span style={{ fontFamily: 'monospace', fontSize: 11 }}>{result.matchedOn || '—'}</span>
        </div>
        {result.peResponse !== undefined && (
          <div className="muted" style={{ fontSize: 11, marginTop: 6, fontFamily: 'monospace' }}>
            PE response: {result.peResponse || '(empty)'}
          </div>
        )}
      </div>

      {result.warnings?.length > 0 && (
        <div style={{
          background: 'rgba(255,193,7,.06)', border: '1px solid rgba(255,193,7,.2)',
          borderRadius: 6, padding: 10, fontSize: 12,
        }}>
          {result.warnings.map((w: string, i: number) => (
            <div key={i} className="muted">
              <AlertTriangle size={12} style={{ verticalAlign: -2, color: 'var(--warn)' }} /> {w}
            </div>
          ))}
        </div>
      )}

      {changes.length > 0 && (
        <div style={{ fontSize: 12 }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>Assignment Changes</div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
            <thead>
              <tr>
                <th style={thStyle}>Role</th>
                <th style={thStyle}>PE Field</th>
                <th style={thStyle}>Before</th>
                <th style={thStyle}>After</th>
                <th style={thStyle}>Matched On</th>
              </tr>
            </thead>
            <tbody>
              {changes.map((c, i) => (
                <tr key={c.column} style={{ background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,.02)' }}>
                  <td style={tdStyle}>{c.roleLabel}</td>
                  <td style={{ ...tdStyle, fontFamily: 'monospace' }}>{c.column}</td>
                  <td style={{ ...tdStyle, color: 'var(--muted)' }}>{staffLabel(c.before)}</td>
                  <td style={{ ...tdStyle, color: 'var(--accent)' }}>
                    {staffLabel(c.after, c.afterName)}
                    {c.afterEmail && <span className="muted"> · {c.afterEmail}</span>}
                  </td>
                  <td style={{ ...tdStyle, color: 'var(--muted)' }}>{MATCHED_ON_LABELS[c.matchedOn] ?? c.matchedOn ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {result.unchanged?.length > 0 && (
        <div className="muted" style={{ fontSize: 11 }}>
          Left alone (already assigned):{' '}
          {result.unchanged.map((u: any) => `${u.roleLabel} ${staffLabel(u.after, u.afterName)}`).join(' · ')}
        </div>
      )}

      <details style={{ fontSize: 12 }}>
        <summary style={{ cursor: 'pointer', color: 'var(--muted)' }}>Raw UpdateClientService payload</summary>
        <pre style={{
          background: 'rgba(0,0,0,.3)', borderRadius: 6, padding: 12,
          overflowX: 'auto', fontSize: 11, marginTop: 6, maxHeight: 300,
        }}>
          {JSON.stringify(result.savePayload ?? {}, null, 2)}
        </pre>
      </details>
    </div>
  );
}

function ServiceRolesExecuteTable({ results }: { results: ExecuteRowResult[] }) {
  const after = (idx: number | null | undefined, name: string | undefined, ok: boolean) => (
    idx == null
      ? <span className="muted">—</span>
      : <span style={{ color: ok ? 'var(--accent)' : undefined }}>{staffLabel(idx, name)}</span>
  );
  const before = (idx: number | null | undefined, name?: string) => (idx == null ? '—' : staffLabel(idx, name));
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr>
            <th style={thStyle}>Row</th>
            <th style={thStyle}>ContIndex</th>
            <th style={thStyle}>Service</th>
            <th style={thStyle}>Manager Before</th>
            <th style={thStyle}>Manager After</th>
            <th style={thStyle}>Partner Before</th>
            <th style={thStyle}>Partner After</th>
            <th style={thStyle}>Roles Updated</th>
            <th style={thStyle}>Status</th>
            <th style={thStyle}>Detail</th>
          </tr>
        </thead>
        <tbody>
          {results.map((r) => (
            <tr key={r.row} style={{ background: r.row % 2 === 0 ? 'transparent' : 'rgba(255,255,255,.02)' }}>
              <td style={tdStyle}>{r.row}</td>
              <td style={{ ...tdStyle, fontFamily: 'monospace' }}>{r.contIndex ?? '—'}</td>
              <td style={{ ...tdStyle, fontFamily: 'monospace' }}>{r.servIndex ?? '—'}</td>
              <td style={{ ...tdStyle, color: 'var(--muted)' }}>{before(r.managerBefore, r.managerBeforeName)}</td>
              <td style={tdStyle}>{after(r.managerAfter, r.managerAfterName, r.status === 'success')}</td>
              <td style={{ ...tdStyle, color: 'var(--muted)' }}>{before(r.partnerBefore, r.partnerBeforeName)}</td>
              <td style={tdStyle}>{after(r.partnerAfter, r.partnerAfterName, r.status === 'success')}</td>
              <td style={{ ...tdStyle, fontSize: 11 }}>{r.rolesUpdated || '—'}</td>
              <td style={tdStyle}>
                {r.status === 'success'
                  ? <span style={{ color: 'var(--good)' }}><CheckCircle2 size={12} style={{ verticalAlign: -2 }} /> Saved</span>
                  : r.status === 'skipped'
                    ? <span className="muted">Skipped</span>
                    : <span style={{ color: 'var(--bad)' }}><XCircle size={12} style={{ verticalAlign: -2 }} /> Failed</span>}
              </td>
              <td style={{ ...tdStyle, maxWidth: 320, whiteSpace: 'normal', fontSize: 11, color: r.error ? 'var(--bad)' : 'var(--muted)' }}>
                {r.error || r.reason || (r.warnings?.length ? r.warnings.join('; ') : '—')}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Job Roles result views ───────────────────────────────────────────────────

const ROLE_ACTION_LABELS: Record<string, string> = {
  replace:  'Replace',
  add:      'Add',
  unassign: 'Unassign',
};

function JobRolesTestResultView({ result }: { result: any }) {
  if (result.status === 'skipped') {
    return (
      <div style={{
        background: 'rgba(255,255,255,.03)', border: '1px solid var(--border)',
        borderRadius: 6, padding: 12, fontSize: 13,
      }}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>Row skipped — nothing saved</div>
        <div className="muted" style={{ fontSize: 12 }}>{result.reason || 'No role change needed'}</div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{
        background: 'rgba(87,211,140,.08)', border: '1px solid rgba(87,211,140,.2)',
        borderRadius: 6, padding: 12,
      }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--good)', marginBottom: 4 }}>
          <CheckCircle2 size={13} style={{ verticalAlign: -2 }} /> Job role saved to PE
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '150px 1fr', gap: '4px 12px', fontSize: 12, marginTop: 6 }}>
          <span className="muted">Job_Idx:</span>
          <span><JobLink jobIdx={result.jobIdx} /></span>
          <span className="muted">Role:</span>
          <span>{result.roleName || result.roleIndex}</span>
          <span className="muted">Action:</span>
          <span>{ROLE_ACTION_LABELS[result.action] ?? result.action ?? '—'}</span>
          <span className="muted">Staff before:</span>
          <span style={{ color: 'var(--muted)' }}>{result.staffBefore || '(none)'}</span>
          <span className="muted">Staff after:</span>
          <span style={{ color: 'var(--accent)' }}>{result.staffAfter || '(none)'}</span>
          <span className="muted">Resolved by:</span>
          <span style={{ fontFamily: 'monospace', fontSize: 11 }}>
            {MATCHED_ON_LABELS[result.matchedOn] ?? result.matchedOn ?? '—'}
            {result.source ? ` · ${result.source === 'row' ? 'CSV column' : 'form'}` : ''}
          </span>
        </div>
        <div className="muted" style={{ fontSize: 11, marginTop: 6 }}>
          Read back from PE after the save and confirmed to match.
        </div>
      </div>

      {result.warnings?.length > 0 && (
        <div style={{
          background: 'rgba(255,193,7,.06)', border: '1px solid rgba(255,193,7,.2)',
          borderRadius: 6, padding: 10, fontSize: 12,
        }}>
          {result.warnings.map((w: string, i: number) => (
            <div key={i} className="muted">
              <AlertTriangle size={12} style={{ verticalAlign: -2, color: 'var(--warn)' }} /> {w}
            </div>
          ))}
        </div>
      )}

      <details style={{ fontSize: 12 }}>
        <summary style={{ cursor: 'pointer', color: 'var(--muted)' }}>Raw SaveRoles payload</summary>
        <pre style={{
          background: 'rgba(0,0,0,.3)', borderRadius: 6, padding: 12,
          overflowX: 'auto', fontSize: 11, marginTop: 6, maxHeight: 300,
        }}>
          {JSON.stringify(result.savePayload ?? {}, null, 2)}
        </pre>
      </details>
    </div>
  );
}

function JobRolesExecuteTable({ results }: { results: ExecuteRowResult[] }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr>
            <th style={thStyle}>Row</th>
            <th style={thStyle}>Job_Idx</th>
            <th style={thStyle}>Role</th>
            <th style={thStyle}>Action</th>
            <th style={thStyle}>Staff Before</th>
            <th style={thStyle}>Staff After</th>
            <th style={thStyle}>Matched On</th>
            <th style={thStyle}>Status</th>
            <th style={thStyle}>Detail</th>
          </tr>
        </thead>
        <tbody>
          {results.map((r) => (
            <tr key={r.row} style={{ background: r.row % 2 === 0 ? 'transparent' : 'rgba(255,255,255,.02)' }}>
              <td style={tdStyle}>{r.row}</td>
              <td style={{ ...tdStyle, fontFamily: 'monospace' }}>{r.jobIdx ? <JobLink jobIdx={r.jobIdx} /> : '—'}</td>
              <td style={tdStyle}>{r.roleName || '—'}</td>
              <td style={{ ...tdStyle, fontSize: 11 }}>{r.action ? (ROLE_ACTION_LABELS[r.action] ?? r.action) : '—'}</td>
              <td style={{ ...tdStyle, color: 'var(--muted)', whiteSpace: 'normal', fontSize: 11 }}>{r.staffBefore || '—'}</td>
              <td style={{ ...tdStyle, color: r.status === 'success' ? 'var(--accent)' : undefined, whiteSpace: 'normal', fontSize: 11 }}>{r.staffAfter || '—'}</td>
              <td style={{ ...tdStyle, color: 'var(--muted)', fontSize: 11 }}>{r.matchedOn ? (MATCHED_ON_LABELS[r.matchedOn] ?? r.matchedOn) : '—'}</td>
              <td style={tdStyle}>
                {r.status === 'success'
                  ? <span style={{ color: 'var(--good)' }}><CheckCircle2 size={12} style={{ verticalAlign: -2 }} /> Saved</span>
                  : r.status === 'skipped'
                    ? <span className="muted">Skipped</span>
                    : <span style={{ color: 'var(--bad)' }}><XCircle size={12} style={{ verticalAlign: -2 }} /> Failed</span>}
              </td>
              <td style={{ ...tdStyle, maxWidth: 320, whiteSpace: 'normal', fontSize: 11, color: r.error ? 'var(--bad)' : 'var(--muted)' }}>
                {r.error || r.reason || (r.warnings?.length ? r.warnings.join('; ') : '—')}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Job Budget result views ──────────────────────────────────────────────────

function JobBudgetTestResultView({ result }: { result: any }) {
  const applied: any = result.taskResults?.[0] || {};
  const verify = result.verification;

  // Nothing was written — blank hours cell, so no SaveBudgets call was made.
  if (result.status === 'skipped') {
    return (
      <div style={{
        background: 'rgba(255,255,255,.03)', border: '1px solid var(--border)',
        borderRadius: 6, padding: 12, fontSize: 13,
      }}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>Row skipped — nothing saved</div>
        <div className="muted" style={{ fontSize: 12 }}>
          {applied.reason || result.reason || 'No budget hours supplied'}
        </div>
      </div>
    );
  }

  // The read-back should show exactly one budget row per task. More means PE
  // appended rather than updated, which would double-count the job's budget.
  const rowCountMismatch =
    verify && verify.budgetRowCount != null && verify.expectedRowCount != null &&
    verify.budgetRowCount !== verify.expectedRowCount;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{
        background: 'rgba(87,211,140,.08)', border: '1px solid rgba(87,211,140,.2)',
        borderRadius: 6, padding: 12,
      }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--good)', marginBottom: 4 }}>
          <CheckCircle2 size={13} style={{ verticalAlign: -2 }} /> Budget saved to PE
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: '4px 12px', fontSize: 12, marginTop: 6 }}>
          <span className="muted">Job_Idx:</span>
          <span><JobLink jobIdx={result.jobIdx} /></span>
          <span className="muted">Task:</span>
          <span>{applied.taskSubject || '—'} {applied.taskCode ? <span className="muted" style={{ fontFamily: 'monospace', fontSize: 11 }}>({applied.taskCode})</span> : null}</span>
          <span className="muted">Task hours:</span>
          <span>
            <span style={{ color: 'var(--muted)' }}>{applied.hoursBefore ?? 0}</span>
            {' → '}
            <span style={{ color: 'var(--accent)', fontWeight: 600 }}>{applied.hoursAfter ?? 0}</span>
          </span>
          <span className="muted">Job budget total:</span>
          <span>
            <span style={{ color: 'var(--muted)' }}>{result.jobBudgetHoursBefore ?? 0}</span>
            {' → '}
            <span style={{ color: 'var(--accent)', fontWeight: 600 }}>{result.jobBudgetHoursAfter ?? 0}</span>
          </span>
          <span className="muted">Tasks preserved:</span>
          <span>{Math.max(0, (result.taskCount ?? 0) - (result.tasksApplied ?? 0))} of {result.taskCount ?? 0} left untouched</span>
        </div>
        {result.peResponse !== undefined && (
          <div className="muted" style={{ fontSize: 11, marginTop: 6, fontFamily: 'monospace' }}>
            PE response: {result.peResponse || '(empty)'}
          </div>
        )}
      </div>

      {rowCountMismatch && (
        <div style={{
          background: 'rgba(255,107,107,.08)', border: '1px solid rgba(255,107,107,.2)',
          borderRadius: 6, padding: 12, fontSize: 12, color: 'var(--bad)',
        }}>
          <strong><AlertTriangle size={13} style={{ verticalAlign: -2 }} /> Budget row count doesn't match.</strong>{' '}
          PE now has {verify.budgetRowCount} budget rows for {verify.expectedRowCount} tasks — it may have
          appended rows instead of updating them. Check the job in PE before running the batch.
        </div>
      )}

      {verify?.error && (
        <div className="muted" style={{ fontSize: 12 }}>
          Couldn't read the budget back to verify: {verify.error}
        </div>
      )}

      {verify?.tasks && verify.tasks.length > 0 && (
        <div style={{ fontSize: 12 }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>Budget in PE after save (read back)</div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
            <thead>
              <tr>
                <th style={thStyle}>Task</th>
                <th style={thStyle}>TaskIndex</th>
                <th style={thStyle}>Hours</th>
                <th style={thStyle}>ETC</th>
              </tr>
            </thead>
            <tbody>
              {verify.tasks.map((t: any, i: number) => {
                const isTarget = t.taskIndex === applied.taskIndex;
                return (
                  <tr key={t.taskIndex} style={{
                    background: isTarget ? 'rgba(122,162,255,.1)' : (i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,.02)'),
                  }}>
                    <td style={{ ...tdStyle, fontWeight: isTarget ? 600 : 400 }}>{t.taskSubject}</td>
                    <td style={{ ...tdStyle, fontFamily: 'monospace', color: 'var(--muted)' }}>{t.taskIndex}</td>
                    <td style={{ ...tdStyle, color: isTarget ? 'var(--accent)' : undefined }}>{t.hours}</td>
                    <td style={{ ...tdStyle, color: 'var(--muted)' }}>{t.etc}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <details style={{ fontSize: 12 }}>
        <summary style={{ cursor: 'pointer', color: 'var(--muted)' }}>Raw SaveBudgets payload</summary>
        <pre style={{
          background: 'rgba(0,0,0,.3)', borderRadius: 6, padding: 12,
          overflowX: 'auto', fontSize: 11, marginTop: 6, maxHeight: 300,
        }}>
          {JSON.stringify(result.savePayload ?? {}, null, 2)}
        </pre>
      </details>
    </div>
  );
}

function JobBudgetExecuteTable({ results }: { results: ExecuteRowResult[] }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr>
            <th style={thStyle}>Row</th>
            <th style={thStyle}>Job_Idx</th>
            <th style={thStyle}>Task</th>
            <th style={thStyle}>Hours Before</th>
            <th style={thStyle}>Hours After</th>
            <th style={thStyle}>Job Total</th>
            <th style={thStyle}>Status</th>
            <th style={thStyle}>Detail</th>
          </tr>
        </thead>
        <tbody>
          {results.map((r) => (
            <tr key={r.row} style={{ background: r.row % 2 === 0 ? 'transparent' : 'rgba(255,255,255,.02)' }}>
              <td style={tdStyle}>{r.row}</td>
              <td style={{ ...tdStyle, fontFamily: 'monospace' }}>{r.jobIdx ? <JobLink jobIdx={r.jobIdx} /> : '—'}</td>
              <td style={tdStyle}>{r.taskSubject || '—'}</td>
              <td style={{ ...tdStyle, color: 'var(--muted)' }}>{r.hoursBefore ?? '—'}</td>
              <td style={{ ...tdStyle, color: r.status === 'success' ? 'var(--accent)' : undefined }}>{r.hoursAfter ?? '—'}</td>
              <td style={{ ...tdStyle, color: 'var(--muted)' }}>{r.jobBudgetHours ?? '—'}</td>
              <td style={tdStyle}>
                {r.status === 'success'
                  ? <span style={{ color: 'var(--good)' }}><CheckCircle2 size={12} style={{ verticalAlign: -2 }} /> Saved</span>
                  : r.status === 'skipped'
                    ? <span className="muted">Skipped</span>
                    : <span style={{ color: 'var(--bad)' }}><XCircle size={12} style={{ verticalAlign: -2 }} /> Failed</span>}
              </td>
              <td style={{ ...tdStyle, maxWidth: 380, whiteSpace: 'normal', fontSize: 11, color: r.error ? 'var(--bad)' : 'var(--muted)' }}>
                {r.error || r.reason || '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Job Complexity result views ──────────────────────────────────────────────

function JobComplexityTestResultView({ result }: { result: any }) {
  if (result.status === 'skipped') {
    return (
      <div style={{
        background: 'rgba(255,255,255,.03)', border: '1px solid var(--border)',
        borderRadius: 6, padding: 12, fontSize: 13,
      }}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>Row skipped</div>
        <div className="muted" style={{ fontSize: 12 }}>{result.reason || 'No complexity value supplied'}</div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{
        background: 'rgba(87,211,140,.08)', border: '1px solid rgba(87,211,140,.2)',
        borderRadius: 6, padding: 12,
      }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--good)', marginBottom: 4 }}>
          <CheckCircle2 size={13} style={{ verticalAlign: -2 }} /> Complexity saved to PE
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '4px 12px', fontSize: 12, marginTop: 6 }}>
          <span className="muted">Job_Idx:</span>
          <span><JobLink jobIdx={result.jobIdx} /></span>
          <span className="muted">Complexity:</span>
          <span>
            <span style={{ color: 'var(--muted)' }}>
              {result.complexityBefore || '—'}{result.complexityBeforeName ? ` (${result.complexityBeforeName})` : ''}
            </span>
            {' → '}
            <span style={{ color: 'var(--accent)', fontWeight: 600 }}>
              {result.complexityAfter}{result.complexityName ? ` (${result.complexityName})` : ''}
            </span>
          </span>
          <span className="muted">Value from:</span>
          <span>{result.source === 'row' ? 'CSV column' : 'form'}</span>
        </div>
        {result.peResponse !== undefined && (
          <div className="muted" style={{ fontSize: 11, marginTop: 6, fontFamily: 'monospace' }}>
            PE response: {result.peResponse || '(empty)'}
          </div>
        )}
      </div>

      <details style={{ fontSize: 12 }}>
        <summary style={{ cursor: 'pointer', color: 'var(--muted)' }}>Raw SaveManagement payload</summary>
        <pre style={{
          background: 'rgba(0,0,0,.3)', borderRadius: 6, padding: 12,
          overflowX: 'auto', fontSize: 11, marginTop: 6, maxHeight: 300,
        }}>
          {JSON.stringify(result.savePayload ?? {}, null, 2)}
        </pre>
      </details>
    </div>
  );
}

function JobComplexityExecuteTable({ results }: { results: ExecuteRowResult[] }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr>
            <th style={thStyle}>Row</th>
            <th style={thStyle}>Job_Idx</th>
            <th style={thStyle}>Before</th>
            <th style={thStyle}>After</th>
            <th style={thStyle}>Level</th>
            <th style={thStyle}>Status</th>
            <th style={thStyle}>Detail</th>
          </tr>
        </thead>
        <tbody>
          {results.map((r) => (
            <tr key={r.row} style={{ background: r.row % 2 === 0 ? 'transparent' : 'rgba(255,255,255,.02)' }}>
              <td style={tdStyle}>{r.row}</td>
              <td style={{ ...tdStyle, fontFamily: 'monospace' }}>{r.jobIdx ? <JobLink jobIdx={r.jobIdx} /> : '—'}</td>
              <td style={{ ...tdStyle, color: 'var(--muted)' }}>{r.complexityBefore || '—'}</td>
              <td style={{ ...tdStyle, color: r.status === 'success' ? 'var(--accent)' : undefined }}>{r.complexityAfter || '—'}</td>
              <td style={tdStyle}>{r.complexityName || '—'}</td>
              <td style={tdStyle}>
                {r.status === 'success'
                  ? <span style={{ color: 'var(--good)' }}><CheckCircle2 size={12} style={{ verticalAlign: -2 }} /> Saved</span>
                  : r.status === 'skipped'
                    ? <span className="muted">Skipped</span>
                    : <span style={{ color: 'var(--bad)' }}><XCircle size={12} style={{ verticalAlign: -2 }} /> Failed</span>}
              </td>
              <td style={{ ...tdStyle, maxWidth: 380, whiteSpace: 'normal', fontSize: 11, color: r.error ? 'var(--bad)' : 'var(--muted)' }}>
                {r.error || r.reason || '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Create Client result views ───────────────────────────────────────────────

function CreateClientTestResultView({ result }: { result: any }) {
  const isSkipped = result.status === 'skipped';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{
        background: isSkipped ? 'rgba(255,200,0,.08)' : 'rgba(87,211,140,.08)',
        border: `1px solid ${isSkipped ? 'rgba(255,200,0,.2)' : 'rgba(87,211,140,.2)'}`,
        borderRadius: 6, padding: 12,
      }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: isSkipped ? 'var(--warn, #ffc800)' : 'var(--good)', marginBottom: 6 }}>
          {isSkipped
            ? <><AlertCircle size={13} style={{ verticalAlign: -2 }} /> Skipped — already exists</>
            : <><CheckCircle2 size={13} style={{ verticalAlign: -2 }} /> Client created in PE</>}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: '4px 12px', fontSize: 12 }}>
          <span className="muted">Client Code:</span>
          <span><ClientLink clientCode={result.clientCode}>{result.clientCode}</ClientLink></span>
          <span className="muted">Client Name:</span>
          <span>{result.clientName || '—'}</span>
          {!isSkipped && (
            <>
              <span className="muted">ContIndex:</span>
              <span style={{ fontFamily: 'monospace' }}>{result.contIndex ?? '—'}</span>
              <span className="muted">Entity:</span>
              <span>{result.entity || '—'} {result.isIndividual !== undefined && <span className="muted">({result.isIndividual ? 'Individual' : 'Organization'})</span>}</span>
              <span className="muted">Type:</span>
              <span>{result.clientType || '—'}</span>
              <span className="muted">Partner:</span>
              <span>{result.partner || '—'}</span>
              <span className="muted">Manager:</span>
              <span>{result.manager || '—'}</span>
              <span className="muted">Office:</span>
              <span>{result.office || '—'}</span>
              <span className="muted">Request ID:</span>
              <span style={{ fontFamily: 'monospace' }}>{result.requestId ?? '—'}</span>
            </>
          )}
          {isSkipped && (
            <>
              <span className="muted">Reason:</span>
              <span style={{ color: 'var(--muted)' }}>{result.reason}</span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function CreateClientExecuteTable({ results }: { results: ExecuteRowResult[] }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr>
            <th style={thStyle}>Row</th>
            <th style={thStyle}>Client Code</th>
            <th style={thStyle}>Client Name</th>
            <th style={thStyle}>ContIndex</th>
            <th style={thStyle}>Type</th>
            <th style={thStyle}>Entity</th>
            <th style={thStyle}>Partner</th>
            <th style={thStyle}>Office</th>
            <th style={thStyle}>Status</th>
            <th style={thStyle}>Notes / Error</th>
          </tr>
        </thead>
        <tbody>
          {results.map((r) => {
            const ra = r as any;
            return (
              <tr key={r.row} style={{ background: r.row % 2 === 0 ? 'transparent' : 'rgba(255,255,255,.02)' }}>
                <td style={tdStyle}>{r.row}</td>
                <td style={tdStyle}>{ra.clientCode ? <ClientLink clientCode={ra.clientCode}>{ra.clientCode}</ClientLink> : '—'}</td>
                <td style={tdStyle}>{ra.clientName ?? '—'}</td>
                <td style={{ ...tdStyle, fontFamily: 'monospace' }}>{ra.contIndex ?? '—'}</td>
                <td style={tdStyle}>{ra.clientType ?? '—'}</td>
                <td style={tdStyle}>{ra.entity ?? '—'}</td>
                <td style={tdStyle}>{ra.partner ?? '—'}</td>
                <td style={tdStyle}>{ra.office ?? '—'}</td>
                <td style={tdStyle}>
                  {r.status === 'success'
                    ? <span style={{ color: 'var(--good)' }}><CheckCircle2 size={12} style={{ verticalAlign: -2 }} /> Created</span>
                    : r.status === 'skipped'
                    ? <span style={{ color: 'var(--muted)' }}>Skipped</span>
                    : <span style={{ color: 'var(--bad)' }}><XCircle size={12} style={{ verticalAlign: -2 }} /> Failed</span>}
                </td>
                <td style={{ ...tdStyle, maxWidth: 300, whiteSpace: 'normal', fontSize: 11, color: r.error ? 'var(--bad)' : 'var(--muted)' }}>
                  {r.error || ra.reason || '—'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── Helpers ─────────────────────────────────────────────────────────────────

const PE_JOB_URL      = 'https://bmss.pehosted.com/PE/Job/Job';
const PE_CLIENT_URL   = 'https://bmss.pehosted.com/PE/Client/Edit';
const PE_DRAFT_URL    = 'https://bmss.pehosted.com/PE/Billing/FeeWizard';

function JobLink({ jobIdx }: { jobIdx: string | number }) {
  const val = String(jobIdx);
  if (!val || val === '—') return <>{val}</>;
  return (
    <a
      href={`${PE_JOB_URL}/${val}?panel=0`}
      target="_blank"
      rel="noopener noreferrer"
      style={{ color: 'var(--accent)', textDecoration: 'none' }}
      onMouseEnter={(e) => (e.currentTarget.style.textDecoration = 'underline')}
      onMouseLeave={(e) => (e.currentTarget.style.textDecoration = 'none')}
      onClick={(e) => e.stopPropagation()}
    >
      {val}
    </a>
  );
}

function ClientLink({ clientCode, children }: { clientCode: string | undefined; children: React.ReactNode }) {
  if (!clientCode) return <>{children}</>;
  return (
    <a
      href={`${PE_CLIENT_URL}/${encodeURIComponent(clientCode)}`}
      target="_blank"
      rel="noopener noreferrer"
      style={{ color: 'var(--accent)', textDecoration: 'none' }}
      onMouseEnter={(e) => (e.currentTarget.style.textDecoration = 'underline')}
      onMouseLeave={(e) => (e.currentTarget.style.textDecoration = 'none')}
      onClick={(e) => e.stopPropagation()}
    >
      {children}
    </a>
  );
}

function DraftFeeLink({ draftIdx, children }: { draftIdx: number | null | undefined; children: React.ReactNode }) {
  if (!draftIdx) return <>{children}</>;
  return (
    <a
      href={`${PE_DRAFT_URL}/${draftIdx}`}
      target="_blank"
      rel="noopener noreferrer"
      style={{ color: 'var(--accent)', textDecoration: 'none' }}
      onMouseEnter={(e) => (e.currentTarget.style.textDecoration = 'underline')}
      onMouseLeave={(e) => (e.currentTarget.style.textDecoration = 'none')}
      onClick={(e) => e.stopPropagation()}
    >
      {children}
    </a>
  );
}

/** Render a table cell value — make Job_Idx a PE link */
function CellValue({ header, value }: { header: string; value: string }) {
  if (header === 'Job_Idx' && value) return <JobLink jobIdx={value} />;
  return <>{value}</>;
}

// ── Shared styles ───────────────────────────────────────────────────────────

const thStyle: React.CSSProperties = {
  textAlign: 'left', padding: '6px 10px', borderBottom: '1px solid var(--border)',
  fontSize: 11, fontWeight: 600, color: 'var(--muted)', whiteSpace: 'nowrap',
};

const tdStyle: React.CSSProperties = {
  padding: '5px 10px', borderBottom: '1px solid rgba(255,255,255,.04)', whiteSpace: 'nowrap',
};

const selStyle: React.CSSProperties = {
  background: 'var(--bg)', color: 'var(--text)', border: '1px solid var(--border)',
  borderRadius: 6, padding: '7px 10px', fontSize: 13, width: '100%',
};

const inStyle: React.CSSProperties = {
  background: 'var(--bg)', color: 'var(--text)', border: '1px solid var(--border)',
  borderRadius: 6, padding: '7px 10px', fontSize: 13, width: '100%',
};

// ── Excel/CSV download of execute results ──────────────────────────────────

interface ColumnSpec {
  header: string;
  /** Returns either a plain cell value or a `{ formula: '=HYPERLINK(...)' }` for Excel formulas. */
  get: (r: ExecuteRowResult) => string | number | { formula: string } | null | undefined;
}

const COLUMN_SPECS: Record<string, ColumnSpec[]> = {
  'roll-jurisdictions': [
    { header: 'Row',           get: (r) => r.row },
    { header: 'Job_Idx',       get: (r) => r.jobIdx ?? '' },
    { header: 'Jurisdictions', get: (r) => r.jurisdictionCount ?? '' },
    { header: 'Status',        get: (r) => r.status },
    { header: 'Error',         get: (r) => r.error ?? '' },
    { header: 'PE Response',   get: (r) => r.peResponse ?? '' },
  ],
  'set-client-attributes': [
    { header: 'Row',           get: (r) => r.row },
    { header: 'ClientCode',    get: (r) => r.clientCode ?? '' },
    { header: 'ClientName',    get: (r) => r.clientName ?? '' },
    { header: 'EntityId',      get: (r) => r.entityId ?? '' },
    { header: 'FieldId',       get: (r) => r.fieldId ?? '' },
    { header: 'Value',         get: (r) => (r.submittedValue == null ? '' : String(r.submittedValue)) },
    { header: 'Status',        get: (r) => r.status },
    { header: 'Error',         get: (r) => r.error ?? '' },
  ],
  'writeoff-disbursements': [
    { header: 'Row',         get: (r) => r.row },
    { header: 'ClientCode',  get: (r) => r.clientCode ?? '' },
    { header: 'ClientName',  get: (r) => r.clientName ?? '' },
    { header: 'ContIndex',   get: (r) => r.contIndex ?? '' },
    { header: 'WIPIndex',    get: (r) => r.wipIndex ?? '' },
    { header: 'Job_Idx',     get: (r) => r.servPeriod ?? '' },
    { header: 'Amount',      get: (r) => r.wipOutstanding ?? '' },
    { header: 'DraftIdx',    get: (r) => r.draftIdx ?? '' },
    {
      header: 'DraftLink',
      get: (r) => {
        if (!r.draftIdx) return '';
        const url = `https://bmss.pehosted.com/PE/Billing/FeeWizard/${r.draftIdx}`;
        return { formula: `=HYPERLINK("${url}","Open Draft #${r.draftIdx}")` };
      },
    },
    { header: 'Stage',       get: (r) => r.stage ?? '' },
    { header: 'Status',      get: (r) => r.status },
    { header: 'Error',       get: (r) => r.error ?? '' },
  ],
  'update-staff-budget': [
    { header: 'Row',            get: (r) => r.row },
    { header: 'EmployeeName',   get: (r) => r.employeeName ?? '' },
    { header: 'BudgetYear',     get: (r) => r.budgetYear ?? '' },
    { header: 'PeriodsUpdated', get: (r) => r.periodsUpdated ?? '' },
    { header: 'Status',         get: (r) => r.status },
    { header: 'Error',          get: (r) => r.error ?? '' },
    { header: 'PE Response',    get: (r) => r.peResponse ?? '' },
  ],
  'create-jobs': [
    { header: 'Row',         get: (r) => r.row },
    { header: 'ClientCode',  get: (r) => r.clientCode ?? '' },
    { header: 'ClientName',  get: (r) => r.clientName ?? '' },
    { header: 'ContIndex',   get: (r) => r.contIndex ?? '' },
    { header: 'JobName',     get: (r) => r.jobName ?? '' },
    { header: 'JobCode',     get: (r) => r.jobCode ?? '' },
    { header: 'Partner',     get: (r) => r.partnerName ?? '' },
    { header: 'Manager',     get: (r) => r.managerName ?? '' },
    { header: 'AddService',  get: (r) => (r.addService ? 'yes' : 'no') },
    { header: 'Status',      get: (r) => r.status },
    { header: 'Detail',      get: (r) => r.error ?? r.reason ?? r.peResponse ?? '' },
  ],
  'update-client-record': [
    { header: 'Row',           get: (r) => r.row },
    { header: 'ClientCode',    get: (r) => r.clientCode ?? '' },
    { header: 'ClientName',    get: (r) => r.clientName ?? '' },
    { header: 'ContIndex',     get: (r) => r.contIndex ?? '' },
    { header: 'Fields Updated', get: (r) => r.updatedFields ?? '' },
    { header: 'Status',        get: (r) => r.status },
    { header: 'Error',         get: (r) => r.error ?? '' },
  ],
  'modify-jobs': [
    { header: 'Row',           get: (r) => r.row },
    { header: 'Job_Idx',       get: (r) => r.jobIdx ?? '' },
    { header: 'ClientCode',    get: (r) => r.clientCode ?? '' },
    { header: 'Job Name',      get: (r) => r.jobName ?? '' },
    { header: 'Fields Updated', get: (r) => r.updatedFields ?? '' },
    { header: 'Status',        get: (r) => r.status },
    { header: 'Error',         get: (r) => r.error ?? '' },
  ],
  'update-client-status': [
    { header: 'Row',         get: (r) => r.row },
    { header: 'ContIndex',   get: (r) => r.contIndex ?? '' },
    { header: 'ClientCode',  get: (r) => r.clientCode ?? '' },
    { header: 'ClientName',  get: (r) => r.clientName ?? '' },
    { header: 'Before',      get: (r) => r.statusBefore ?? '' },
    { header: 'After',       get: (r) => r.statusAfter ?? '' },
    { header: 'Status',      get: (r) => r.status },
    { header: 'Detail',      get: (r) => r.error ?? r.reason ?? '' },
  ],
  'update-contact-status': [
    { header: 'Row',        get: (r) => r.row },
    { header: 'ContIndex',  get: (r) => r.contIndex ?? '' },
    { header: 'Contact',    get: (r) => r.contName ?? '' },
    { header: 'Before',     get: (r) => r.statusBeforeName ?? r.statusBefore ?? '' },
    { header: 'After',      get: (r) => r.statusAfterName ?? r.statusAfter ?? '' },
    { header: 'Loaded Via', get: (r) => r.loadedVia ?? '' },
    { header: 'Status',     get: (r) => r.status },
    { header: 'Detail',     get: (r) => r.error ?? r.reason ?? '' },
  ],
  'update-contact-record': [
    { header: 'Row',            get: (r) => r.row },
    { header: 'ContIndex',      get: (r) => r.contIndex ?? '' },
    { header: 'Contact',        get: (r) => r.contName ?? '' },
    { header: 'Fields Updated', get: (r) => r.updatedFields ?? '' },
    { header: 'Source',         get: (r) => (r.valueSource === 'json' ? 'JSON' : r.valueSource ? 'columns/form' : '') },
    { header: 'Loaded Via',     get: (r) => r.loadedVia ?? '' },
    { header: 'Ignored Keys',   get: (r) => (r.ignoredKeys ?? []).join('; ') },
    { header: 'Status',         get: (r) => r.status },
    { header: 'Detail',         get: (r) => r.error ?? r.reason ?? '' },
  ],
  'update-job-management': [
    { header: 'Row',             get: (r) => r.row },
    { header: 'Job_Idx',         get: (r) => r.jobIdx ?? '' },
    { header: 'Mode',            get: (r) => r.mode ?? '' },
    { header: 'Partner Before',  get: (r) => r.partnerBefore ?? '' },
    { header: 'Partner After',   get: (r) => r.partnerAfter ?? '' },
    { header: 'Partner Name',    get: (r) => r.partnerAfterName ?? '' },
    { header: 'Manager Before',  get: (r) => r.managerBefore ?? '' },
    { header: 'Manager After',   get: (r) => r.managerAfter ?? '' },
    { header: 'Manager Name',    get: (r) => r.managerAfterName ?? '' },
    { header: 'Roles Updated',   get: (r) => r.rolesUpdated ?? '' },
    { header: 'Matched On',      get: (r) => r.matchedOn ?? '' },
    { header: 'Warnings',        get: (r) => (r.warnings ?? []).join('; ') },
    { header: 'Status',          get: (r) => r.status },
    { header: 'Detail',          get: (r) => r.error ?? r.reason ?? '' },
  ],
  'update-service-roles': [
    { header: 'Row',            get: (r) => r.row },
    { header: 'ContIndex',      get: (r) => r.contIndex ?? '' },
    { header: 'ServIndex',      get: (r) => r.servIndex ?? '' },
    { header: 'Manager Before', get: (r) => r.managerBefore ?? '' },
    { header: 'Manager After',  get: (r) => r.managerAfter ?? '' },
    { header: 'Manager Name',   get: (r) => r.managerAfterName ?? '' },
    { header: 'Partner Before', get: (r) => r.partnerBefore ?? '' },
    { header: 'Partner After',  get: (r) => r.partnerAfter ?? '' },
    { header: 'Partner Name',   get: (r) => r.partnerAfterName ?? '' },
    { header: 'Staff Before',   get: (r) => r.serviceStaffBefore ?? '' },
    { header: 'Staff After',    get: (r) => r.serviceStaffAfter ?? '' },
    { header: 'Roles Updated',  get: (r) => r.rolesUpdated ?? '' },
    { header: 'Matched On',     get: (r) => r.matchedOn ?? '' },
    { header: 'Warnings',       get: (r) => (r.warnings ?? []).join('; ') },
    { header: 'Status',         get: (r) => r.status },
    { header: 'Detail',         get: (r) => r.error ?? r.reason ?? '' },
  ],
  'update-job-roles': [
    { header: 'Row',           get: (r) => r.row },
    { header: 'Job_Idx',       get: (r) => r.jobIdx ?? '' },
    { header: 'Role',          get: (r) => r.roleName ?? '' },
    { header: 'Action',        get: (r) => (r.action ? (ROLE_ACTION_LABELS[r.action] ?? r.action) : '') },
    { header: 'Staff Before',  get: (r) => r.staffBefore ?? '' },
    { header: 'Staff After',   get: (r) => r.staffAfter ?? '' },
    { header: 'Matched On',    get: (r) => r.matchedOn ?? '' },
    { header: 'Warnings',      get: (r) => (r.warnings ?? []).join('; ') },
    { header: 'Status',        get: (r) => r.status },
    { header: 'Detail',        get: (r) => r.error ?? r.reason ?? '' },
  ],
  'update-job-budget': [
    { header: 'Row',          get: (r) => r.row },
    { header: 'Job_Idx',      get: (r) => r.jobIdx ?? '' },
    { header: 'Task',         get: (r) => r.taskSubject ?? '' },
    { header: 'Task Code',    get: (r) => r.taskCode ?? '' },
    { header: 'TaskIndex',    get: (r) => r.taskIndex ?? '' },
    { header: 'Hours Before', get: (r) => r.hoursBefore ?? '' },
    { header: 'Hours After',  get: (r) => r.hoursAfter ?? '' },
    { header: 'Job Total',    get: (r) => r.jobBudgetHours ?? '' },
    { header: 'Status',       get: (r) => r.status },
    { header: 'Detail',       get: (r) => r.error ?? r.reason ?? '' },
  ],
  'update-job-complexity': [
    { header: 'Row',        get: (r) => r.row },
    { header: 'Job_Idx',    get: (r) => r.jobIdx ?? '' },
    { header: 'Before',     get: (r) => r.complexityBefore ?? '' },
    { header: 'After',      get: (r) => r.complexityAfter ?? '' },
    { header: 'Level Name', get: (r) => r.complexityName ?? '' },
    { header: 'Status',     get: (r) => r.status },
    { header: 'Detail',     get: (r) => r.error ?? r.reason ?? '' },
  ],
  'create-client': [
    { header: 'Row',         get: (r) => r.row },
    { header: 'FullCode',    get: (r) => (r as any).clientCode ?? '' },
    { header: 'Client Name', get: (r) => (r as any).clientName ?? '' },
    { header: 'ContIndex',   get: (r) => (r as any).contIndex ?? '' },
    { header: 'Type',        get: (r) => (r as any).clientType ?? '' },
    { header: 'Entity',      get: (r) => (r as any).entity ?? '' },
    { header: 'Partner',     get: (r) => (r as any).partner ?? '' },
    { header: 'Office',      get: (r) => (r as any).office ?? '' },
    { header: 'Status',      get: (r) => r.status },
    { header: 'Reason',      get: (r) => (r as any).reason ?? '' },
    { header: 'Error',       get: (r) => r.error ?? '' },
  ],
};

function csvEscape(value: string | number | { formula: string } | null | undefined): string {
  if (value == null || value === '') return '';
  if (typeof value === 'object' && 'formula' in value) {
    // Excel reads ="..." or starts-with-= as a formula; quote the entire cell so embedded
    // quotes inside the formula are preserved.
    return `"${value.formula.replace(/"/g, '""')}"`;
  }
  const s = String(value);
  if (s.includes(',') || s.includes('"') || s.includes('\n') || s.includes('\r')) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function downloadExecuteResultsCsv(useCaseId: string, results: ExecuteRowResult[]) {
  const cols = COLUMN_SPECS[useCaseId] || COLUMN_SPECS['roll-jurisdictions'];
  const lines: string[] = [];
  lines.push(cols.map((c) => csvEscape(c.header)).join(','));
  for (const r of results) {
    lines.push(cols.map((c) => csvEscape(c.get(r))).join(','));
  }
  // Prepend BOM so Excel detects UTF-8 properly
  const csv = '﻿' + lines.join('\r\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);

  const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
  const filename = `import-${useCaseId}-${ts}.csv`;

  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function fmtDateVal(val: unknown): string {
  if (!val) return '—';
  const s = String(val);
  try {
    const d = new Date(s);
    if (isNaN(d.getTime())) return s;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return s;
  }
}
